import type { Document } from "../document/Document";
import type { AdjustmentLayer, BlendMode, GroupLayer, Layer, RasterLayer, SmartObjectLayer, BlendIfConfig, SolidFillLayer, GradientFillLayer, PatternFillLayer, BackgroundLayer, ArtboardLayer, ShapeLayer } from "../document/Layer";
import type { TypeLayer, TextRun, AntiAliasMode } from "../document/TypeLayer";
import { applyWarpToPoint } from "../document/TypeLayer";
import { compositeLayer, nativeCompositeOp } from "../pixel/blend";
import { renderLayerStyles, hasAnyStyle } from "./LayerStylesRenderer";
import { Selection } from "../selection/Selection";
import type { AlphaChannel } from "../selection/Selection";
import { extractBoundary, type Point } from "../selection/SelectionMarchingAnts";
import type { Renderer } from "./Renderer";
import type { Viewport } from "./Viewport";
import { applyAdjustment } from "../adjustments/applyAdjustment";
import { PixelBuffer } from "../pixel/PixelBuffer";
import { getFilterById } from "../filters/FilterRegistry";
import type { Filter } from "../filters/Filter";
import { gaussianBlurMaskAlpha } from "./MaskFeather";

// A parallel agent is adding `selection: Selection | null` to Document. Until
// the interface picks it up officially, treat the field as optional so this
// file compiles standalone and continues to compile once it lands.
import type { AnchorPoint } from "../document/PathModel";

export interface ToolOverlay {
  /**
   * Crop rect in document-space pixels, or null if no active crop.
   * `rotation` (radians, about the rect's own center) is set while dragging
   * the Crop tool's rotation handle or using Straighten (#i21); omitted or 0
   * means an axis-aligned rect.
   */
  cropRect?: { x: number; y: number; w: number; h: number; rotation?: number } | null;
  /** Crop overlay style: "rule-thirds" | "grid" | "diagonal" | "triangle" | "golden-ratio" | "golden-spiral" | null */
  cropOverlay?: string | null;
  /**
   * Crop Shield opacity (0..1) — Photoshop's dimmed-outside-crop-rect
   * overlay strength (#i108). Populated by main.ts from
   * `CropTool.shieldOpacity`; undefined/null falls back to the pre-fix
   * hardcoded 0.5 default.
   */
  cropShieldOpacity?: number | null;
  /**
   * Whether the Crop Shield (the dimmed area outside the crop rect) is
   * shown at all (#i108) — Photoshop's shield enable checkbox, from
   * `CropTool.shieldOutside`. Undefined/omitted defaults to visible
   * (`true`), matching legacy (pre-checkbox) behavior.
   */
  cropShieldVisible?: boolean;
  /**
   * Free-transform bounding box in document-space pixels, or null.
   * `rotation` (radians) is set while the active layer/transform is rotated
   * (#i106); omitted or 0 means an axis-aligned box, matching pre-fix behavior
   * byte-for-byte. `pivotX`/`pivotY` (document-space) are the rotation
   * reference point — FreeTransform rotates about its user-selected reference
   * point (default center, but changeable), and the box/handles MUST rotate
   * about the same pivot or they diverge from the real layer. When absent, the
   * box's own geometric centre is used (correct for the default center pivot).
   */
  transformBBox?: { x: number; y: number; w: number; h: number; rotation?: number; pivotX?: number; pivotY?: number } | null;
  /** Grid visibility and settings. */
  grid?: { visible: boolean; spacing: number; color?: string; majorEvery?: number; majorColor?: string } | null;
  /** Guides in document-space pixels. */
  guides?: Guide[] | null;
  /** Pen tool session for path preview. */
  penSession?: { points: AnchorPoint[]; closed: boolean } | null;
  /** Cursor position in doc-space for rubber-band line. */
  penCursorPos?: { x: number; y: number } | null;
  /** Clone stamp source point crosshair in document-space pixels. */
  cloneStampSource?: { x: number; y: number } | null;
  /** Smart guides visibility and alignment data. */
  smartGuides?: { visible: boolean; layers: Array<{ id: string; x: number; y: number; w: number; h: number }> } | null;
  /** Pixel grid visibility (shown at zoom > 500%). */
  pixelGrid?: { visible: boolean } | null;
  /** Proof colors mode (CMYK soft proof). */
  proofColors?: { enabled: boolean } | null;
  /** Gamut warning overlay. */
  gamutWarning?: { enabled: boolean } | null;
  /** Layer edges outline visibility. */
  layerEdges?: { visible: boolean } | null;
  /**
   * Perspective Crop tool overlay (#i104 follow-up) — the 4 corners the user
   * has placed (in document-space pixels; `null` entries are not-yet-placed
   * corners) plus the tool's current phase. The corner handles + connecting
   * quad outline are drawn once `phase === "adjusting"` (all 4 corners
   * placed). Populated by main.ts each render from
   * `PerspectiveCropTool.getCorners()` / `getPhase()` — see i104 blockers.
   */
  perspectiveCrop?: {
    corners: readonly ({ x: number; y: number } | null)[];
    phase: "placing" | "adjusting";
  } | null;
  /**
   * Object Selection tool's live drag-rectangle (marquee) in document-space
   * pixels while the user drags out a selection box, or null when not
   * dragging (#i64). Populated by main.ts each render from
   * `ObjectSelectionTool.getDragRect()`.
   */
  objectSelectionDragRect?: { x: number; y: number; w: number; h: number } | null;
  /**
   * Patch tool's in-progress selection rect (document-space corner pair —
   * `x0`/`y0`/`x1`/`y1` are NOT guaranteed min/max ordered, mirroring
   * `PatchTool.getSelectionRect()`'s own return shape), or null when no
   * selection is armed (#i79). Populated by main.ts from
   * `PatchTool.getSelectionRect()`.
   */
  patchSelectionRect?: { x0: number; y0: number; x1: number; y1: number } | null;
  /**
   * Patch tool's live drag offset while the armed selection is being
   * dragged toward its source/destination (used to draw a ghost preview of
   * where the patch content will land), or null when not dragging (#i79).
   * Populated by main.ts from `PatchTool.getDragOffset()`.
   */
  patchDragOffset?: { dx: number; dy: number } | null;
  /**
   * Color Sampler tool's placed sample points — persistent, numbered
   * crosshairs (#i137). Populated by main.ts from
   * `ColorSamplerTool.samplers`. Empty/null when no samplers are placed.
   */
  colorSamplers?: ReadonlyArray<{ id: string; x: number; y: number; enabled: boolean }> | null;
  /**
   * Type tool's live paragraph-text-box drag rectangle, in document-space
   * pixels (#i102) — drawn as a dashed marquee so dragging out a paragraph
   * text box gets the same live feedback as a marquee/Object-Selection drag,
   * instead of no visual at all until pointerup. Populated by main.ts each
   * render from `TypeTool.getDragPreview()` (converted from its native
   * screen-space to document-space) while the Type tool is active and
   * dragging; null otherwise.
   */
  typeDragRect?: { x: number; y: number; w: number; h: number } | null;
}

export interface Guide {
  readonly kind: "horizontal" | "vertical";
  readonly position: number; // doc-space pixels from top/left
}

/** Parallel agent type: selection may be optional on Document. */
type DocumentWithSelection = Document & { readonly selection?: Selection | null };

// Canvas2D implementation. Maintains a per-layer offscreen canvas cache
// keyed by layer.id; cache is dropped when a layer leaves the document.
//
// Blend-mode dispatch (M2 / M7 coverage of all 27 Photoshop modes):
//
//   Native (set ctx.globalCompositeOperation directly):
//     normal, multiply, screen, overlay, darken, lighten,
//     color-dodge, color-burn, hard-light, soft-light,
//     difference, exclusion, hue, saturation, color, luminosity,
//     linear-dodge (mapped to Canvas2D's "lighter").
//
//   Offscreen pixel composite (`compositeLayer` in core/pixel/blend.ts):
//     dissolve, linear-burn, darker-color, lighter-color, vivid-light,
//     linear-light, pin-light, hard-mix, subtract, divide.
//
// Groups: non-"normal" blend modes use an isolated offscreen buffer so
// children composite among themselves first, then the group result blends
// with the destination using the group's blendMode + opacity. "normal"
// falls through to pass-through (children blend directly against parent).

export class Canvas2DRenderer implements Renderer {
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private dpr = 1;
  private widthCss = 0;
  private heightCss = 0;
  private _quickMask = false;
  private _quickMaskOptions = { color: { r: 255, g: 0, b: 0 }, opacity: 50, indicate: "masked" as "selected" | "masked" };
  private layerCache = new Map<string, { canvas: HTMLCanvasElement; version: number }>();
  // Separate cache for layer-mask byproducts. Two entry kinds, distinguished
  // by the suffix in the key:
  //   `${id}:mask`     — the masked layer composite (layer × mask alpha)
  //   `${id}:maskbuf`  — the materialised mask PixelBuffer canvas
  // Tracked by a packed `version` matching the inputs' version fields. Keyed
  // by layer id so eviction can drop both alongside the main layer cache
  // when a layer leaves the document.
  private maskCache = new Map<string, { canvas: HTMLCanvasElement; version: number }>();

  // Marching-ants boundary cache. Keyed by Selection identity (!==), so any
  // new Selection instance invalidates automatically. WeakMap lets the GC
  // reclaim entries once the Selection is no longer referenced elsewhere.
  private antsCache = new WeakMap<Selection, Point[][]>();
  private antsCallback: (() => void) | null = null;
  private antsRafPending = false;
  /** Overlay state for tool visuals (crop rect, transform handles). */
  private overlay: ToolOverlay = {};
  /** Channel viewing state: which channel(s) are currently being viewed. */
  private _channelMode: { r: boolean; g: boolean; b: boolean; alpha: AlphaChannel | null } = {
    r: true, g: true, b: true, alpha: null,
  };
  /**
   * Anti-Alias mode of the type layer currently being painted by
   * paintTypeLayerContent (#i97) — set at the top of that method and read
   * by fillTextAA() at every glyph-drawing call site it (and the methods it
   * calls: paintSegments, paintVerticalTypeLayerContent) reaches. A transient
   * instance field (same pattern as `_currentDoc`/`_channelMode`) avoids
   * threading an extra parameter through the whole text-painting call graph.
   */
  private _typeAAMode: AntiAliasMode = "smooth";
  /** Whether extras (selection edges, guides, grid) are visible. */
  private _showExtras = true;
  /** Whether selection edges (marching ants) are visible independently. */
  private _showSelectionEdges = true;

  // Performance: rAF-based render coalescing
  private _renderPending = false;
  private _pendingDoc: Document | null = null;
  private _pendingViewport: Viewport | undefined;
  private _lastRenderTime = 0;
  private _renderDebounceMs = 16; // ~60fps minimum

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas2D context unavailable");
    this.ctx = ctx;
  }

  resize(width: number, height: number, dpr = 1): void {
    this.widthCss = width;
    this.heightCss = height;
    this.dpr = dpr;
    this.canvas.width = Math.floor(width * dpr);
    this.canvas.height = Math.floor(height * dpr);
    this.canvas.style.width = `${width}px`;
    this.canvas.style.height = `${height}px`;
  }

  /** Enable or disable Quick Mask overlay rendering. */
  setQuickMask(enabled: boolean): void {
    this._quickMask = enabled;
  }

  setQuickMaskOptions(options: { color: { r: number; g: number; b: number }; opacity: number; indicate: "selected" | "masked" }): void {
    this._quickMaskOptions = options;
  }

  /** Set which channel(s) to display. RGB composite = {r,g,b all true, alpha: null}. */
  setChannelMode(mode: { r: boolean; g: boolean; b: boolean; alpha: AlphaChannel | null }): void {
    this._channelMode = { ...mode };
  }

  /** Get current channel mode. */
  getChannelMode(): { r: boolean; g: boolean; b: boolean; alpha: AlphaChannel | null } {
    return { ...this._channelMode };
  }

  /** Set extras (selection edges, guides, grid) visibility. */
  setShowExtras(visible: boolean): void {
    this._showExtras = visible;
  }

  /** Set selection edges (marching ants) visibility. */
  setShowSelectionEdges(visible: boolean): void {
    this._showSelectionEdges = visible;
  }

  /**
   * Schedule a render via requestAnimationFrame for coalescing. Multiple
   * calls within the same frame are batched into a single render.
   */
  scheduleRender(doc: Document | null, viewport?: Viewport): void {
    this._pendingDoc = doc;
    this._pendingViewport = viewport;

    if (this._renderPending) return;

    const now = performance.now();
    const elapsed = now - this._lastRenderTime;
    const delay = Math.max(0, this._renderDebounceMs - elapsed);

    this._renderPending = true;
    if (delay > 0) {
      setTimeout(() => {
        this._renderPending = false;
        this.render(this._pendingDoc, this._pendingViewport);
      }, delay);
    } else {
      requestAnimationFrame(() => {
        this._renderPending = false;
        this.render(this._pendingDoc, this._pendingViewport);
      });
    }
  }

  render(doc: Document | null, viewport?: Viewport): void {
    this._currentDoc = doc;
    this._lastRenderTime = performance.now();
    const ctx = this.ctx;
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    ctx.clearRect(0, 0, this.widthCss, this.heightCss);
    if (!doc || !viewport) return;

    // Pan + zoom transform on top of DPR.
    ctx.translate(viewport.panX, viewport.panY);
    ctx.scale(viewport.zoom, viewport.zoom);

    // Document background (the artboard).
    //
    // Intentionally NOT filled with opaque white (bug i40). The canvas was
    // cleared to transparent above, and the CSS `.canvas-viewport` paints a
    // transparency checkerboard behind it. Filling white here would hide the
    // checkerboard, so transparent document regions (transparent-background new
    // docs, alpha PNGs, erased-to-transparent areas) would show as solid white
    // instead of the checkerboard Photoshop shows. Leaving the artboard
    // transparent lets the checkerboard read through for in-document
    // transparency; opaque background/raster layers paint over it as before.

    // Layers, bottom-first, with clipping mask support.
    // Skip invisible layers early in the tree walk.
    ctx.save();
    ctx.beginPath();
    ctx.rect(0, 0, doc.width, doc.height);
    ctx.clip();
    this.renderLayersWithClipping(ctx, this.filterVisibleLayers(doc.layers));
    ctx.restore();

    // Channel mode: if viewing a single channel, convert to grayscale.
    const cm = this._channelMode;
    const isSingleChannel =
      (cm.r && !cm.g && !cm.b && !cm.alpha) ||
      (!cm.r && cm.g && !cm.b && !cm.alpha) ||
      (!cm.r && !cm.g && cm.b && !cm.alpha) ||
      (cm.alpha !== null);
    if (isSingleChannel) {
      this.applyChannelModeFilter(ctx, doc.width, doc.height);
    }

    // Doc border drawn in CSS pixels (post-DPR-scale, pre-zoom). One pixel
    // wide regardless of zoom; subtle bevel — pure black vanishes on dark.
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    ctx.translate(viewport.panX, viewport.panY);
    const w = doc.width * viewport.zoom;
    const h = doc.height * viewport.zoom;
    ctx.strokeStyle = "rgba(0,0,0,0.6)";
    ctx.lineWidth = 1;
    ctx.strokeRect(-0.5, -0.5, w + 1, h + 1);
    ctx.strokeStyle = "rgba(255,255,255,0.06)";
    ctx.strokeRect(0.5, 0.5, w - 1, h - 1);

    const sel = (doc as DocumentWithSelection).selection;

    if (this._quickMask) {
      // Quick Mask overlay — red tint over non-selected areas. Replaces
      // marching ants with a solid color overlay (Photoshop behavior).
      this.drawQuickMaskOverlay(ctx, sel, viewport, doc);
    } else if (sel && this._showExtras && this._showSelectionEdges) {
      // Marching-ants selection overlay. Drawn in CSS-pixel space (same
      // transform we just set up for the border) so the dash pattern stays at
      // a constant on-screen size regardless of zoom.
      this.drawMarchingAnts(ctx, sel, viewport);
    }

    // Tool overlays (crop rect, transform handles) drawn in CSS-pixel space
    // so handles stay a constant on-screen size regardless of zoom.
    if (this._showExtras) {
      this.drawToolOverlays(ctx, viewport, doc);
    }

    // GC: drop cached canvases for layers no longer in the document.
    this.evictUnused(doc);
  }

  /**
   * Filter out invisible layers and groups with no visible children.
   * This avoids unnecessary tree walks during rendering.
   */
  private filterVisibleLayers(layers: readonly Layer[]): readonly Layer[] {
    const result: Layer[] = [];
    for (const layer of layers) {
      if (!layer.visible || layer.opacity <= 0) continue;
      if (layer.kind === "group") {
        const visibleChildren = this.filterVisibleLayers(layer.children);
        if (visibleChildren.length > 0) {
          result.push({ ...layer, children: visibleChildren } as Layer);
        }
      } else {
        result.push(layer);
      }
    }
    return result;
  }

  /**
   * The renderer doesn't run a frame loop, but marching ants need one to
   * animate. The shell/orchestrator wires this to `state.requestRender()` so
   * a render with an active selection schedules the next frame via rAF.
   */
  setAntsAnimationCallback(fn: (() => void) | null): void {
    this.antsCallback = fn;
  }

  /** Set the tool overlay state (crop rect, transform bounding box). */
  setOverlay(overlay: ToolOverlay): void {
    this.overlay = overlay;
  }

  /**
   * Read the document rectangle [0,0,w,h] from `ctx` at DOCUMENT resolution,
   * honoring the context's current transform (DPR · pan · zoom).
   *
   * `getImageData` ignores the CTM: it always reads raw DEVICE pixels starting
   * at the canvas top-left. A naive `getImageData(0,0,w,h)` on the pan/zoom/
   * dpr-transformed main context therefore samples the wrong pixels (bugs
   * i39/i41) — it grabs a w×h device box at (0,0) rather than the region the
   * document actually occupies on screen. We map the doc rect through the CTM
   * to the device box it occupies (dx=e, dy=f, dw=a·w, dh=d·h — no rotation or
   * skew at our call sites) and blit that box back down to a w×h temp buffer,
   * so downstream per-pixel effects see the correct document pixels at document
   * resolution. Nearest-neighbor sampling mirrors the (imageSmoothing-disabled)
   * way layers are drawn, so at zoom·dpr ≥ 1 it round-trips exactly.
   *
   * Degrades to a direct 1:1 getImageData for identity contexts (e.g. the
   * offscreen buffers used during isolated group compositing), so callers work
   * unchanged whether they run against the main context or an offscreen one.
   */
  private readDocPixels(ctx: CanvasRenderingContext2D, w: number, h: number): ImageData | null {
    if (w <= 0 || h <= 0) return null;
    const m = ctx.getTransform();
    const isIdentity =
      m.a === 1 && m.b === 0 && m.c === 0 && m.d === 1 && m.e === 0 && m.f === 0;
    if (isIdentity) return ctx.getImageData(0, 0, w, h);
    const dx = m.e;
    const dy = m.f;
    const dw = m.a * w;
    const dh = m.d * h;
    if (dw <= 0 || dh <= 0) return null;
    let temp = this.readbackTemp;
    if (!temp) { temp = document.createElement("canvas"); this.readbackTemp = temp; }
    temp.width = w;
    temp.height = h;
    const tctx = temp.getContext("2d");
    if (!tctx) return null;
    tctx.setTransform(1, 0, 0, 1, 0, 0);
    tctx.clearRect(0, 0, w, h);
    tctx.imageSmoothingEnabled = false;
    // Sample the on-screen device box for the doc rect back down to doc res.
    tctx.drawImage(ctx.canvas, dx, dy, dw, dh, 0, 0, w, h);
    return tctx.getImageData(0, 0, w, h);
  }

  /** Apply channel mode filter: convert canvas pixels to grayscale based on active channel. */
  private applyChannelModeFilter(ctx: CanvasRenderingContext2D, w: number, h: number): void {
    const cm = this._channelMode;
    if (w <= 0 || h <= 0) return;
    // CTM-correct read: sample the on-screen document region (bug i41 — a plain
    // getImageData(0,0,w,h) reads the wrong device box when zoom≠1/pan≠0/dpr≠1).
    const imageData = this.readDocPixels(ctx, w, h);
    if (!imageData) return;
    const data = imageData.data;
    if (cm.alpha) {
      // Alpha channel: display alpha as grayscale
      const ap = cm.alpha.pixels;
      const aw = cm.alpha.width;
      const ah = cm.alpha.height;
      for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
          const i = (y * w + x) * 4;
          // Map alpha pixel to grayscale
          const ai = (y % ah) * aw + (x % aw);
          const v = ap[ai] ?? 0;
          data[i] = v;
          data[i + 1] = v;
          data[i + 2] = v;
          data[i + 3] = 255;
        }
      }
    } else if (cm.r && !cm.g && !cm.b) {
      // Red channel only
      for (let i = 0; i < data.length; i += 4) {
        const v = data[i] ?? 0;
        data[i] = v;
        data[i + 1] = v;
        data[i + 2] = v;
      }
    } else if (!cm.r && cm.g && !cm.b) {
      // Green channel only
      for (let i = 0; i < data.length; i += 4) {
        const v = data[i + 1] ?? 0;
        data[i] = v;
        data[i + 1] = v;
        data[i + 2] = v;
      }
    } else if (!cm.r && !cm.g && cm.b) {
      // Blue channel only
      for (let i = 0; i < data.length; i += 4) {
        const v = data[i + 2] ?? 0;
        data[i] = v;
        data[i + 1] = v;
        data[i + 2] = v;
      }
    }
    // CTM-correct write-back (bug i41): putImageData ignores the CTM and would
    // splat the result to the device top-left. Route the doc-resolution result
    // through a temp canvas and drawImage it under the current transform so it
    // lands on the on-screen document region. Clear first to replicate
    // putImageData's replace semantics; imageSmoothing off keeps pixels crisp.
    let out = this.offscreenTemp;
    if (!out) { out = document.createElement("canvas"); this.offscreenTemp = out; }
    out.width = w;
    out.height = h;
    const octx = out.getContext("2d");
    if (!octx) return;
    octx.setTransform(1, 0, 0, 1, 0, 0);
    octx.clearRect(0, 0, w, h);
    octx.putImageData(imageData, 0, 0);
    ctx.save();
    ctx.imageSmoothingEnabled = false;
    ctx.clearRect(0, 0, w, h);
    ctx.globalCompositeOperation = "source-over";
    ctx.drawImage(out, 0, 0);
    ctx.restore();
  }

  private drawMarchingAnts(
    ctx: CanvasRenderingContext2D,
    sel: Selection,
    viewport: Viewport,
  ): void {
    // Skip empty selections
    if (sel.isEmpty()) return;

    let polylines = this.antsCache.get(sel);
    if (!polylines) {
      polylines = extractBoundary(sel);
      this.antsCache.set(sel, polylines);
    }
    if (polylines.length === 0) return;

    // We're currently in CSS-pixel space with translate(panX, panY) applied
    // (the doc-border block set this up). Doc-space points map to screen
    // via (x * zoom, y * zoom) on top of that translate.
    const zoom = viewport.zoom;

    // Animate by advancing the dash offset based on wall-clock time.
    // ~1px per 150 ms matches PS's ~8fps march (8px cycle / 150ms = ~53px/s).
    const offset = (Date.now() / 150) % 8;

    ctx.save();
    ctx.lineWidth = 1;
    ctx.lineCap = "butt";
    ctx.lineJoin = "miter";

    // Build the path once; we'll stroke it twice (white under, black over).
    const trace = (): void => {
      ctx.beginPath();
      for (const poly of polylines!) {
        if (poly.length === 0) continue;
        const p0 = poly[0]!;
        ctx.moveTo(p0.x * zoom, p0.y * zoom);
        for (let i = 1; i < poly.length; i++) {
          const p = poly[i]!;
          ctx.lineTo(p.x * zoom, p.y * zoom);
        }
      }
    };

    // Bottom pass: white dashes.
    ctx.setLineDash([4, 4]);
    ctx.lineDashOffset = -offset;
    ctx.strokeStyle = "#ffffff";
    trace();
    ctx.stroke();

    // Top pass: black dashes offset by 4 px so they fill the white gaps.
    ctx.lineDashOffset = -offset + 4;
    ctx.strokeStyle = "#000000";
    trace();
    ctx.stroke();

    ctx.setLineDash([]);
    ctx.lineDashOffset = 0;
    ctx.restore();

    // Schedule the next animation frame. We coalesce so back-to-back render()
    // calls only ever have a single rAF in flight.
    if (this.antsCallback && !this.antsRafPending) {
      this.antsRafPending = true;
      requestAnimationFrame(() => {
        this.antsRafPending = false;
        if (this.antsCallback) this.antsCallback();
      });
    }
  }

  /**
   * Draw the Quick Mask red overlay. Semi-transparent red covers unselected
   * areas (mask === 0). Selected areas (mask > 0) show through normally.
   * When there is no active selection, the entire document gets the red tint
   * (nothing is selected).
   */
  private drawQuickMaskOverlay(
    ctx: CanvasRenderingContext2D,
    sel: Selection | null | undefined,
    viewport: Viewport,
    doc: Document,
  ): void {
    const zoom = viewport.zoom;
    const w = doc.width;
    const h = doc.height;
    if (w <= 0 || h <= 0) return;

    // Create an offscreen canvas at doc resolution, fill with red at 50%.
    const off = document.createElement("canvas");
    off.width = w;
    off.height = h;
    const octx = off.getContext("2d");
    if (!octx) return;

    // Fill the entire offscreen with red.
    const { color, opacity, indicate } = this._quickMaskOptions;
    const alpha = Math.round(opacity * 255 / 100);
    octx.fillStyle = `rgba(${color.r}, ${color.g}, ${color.b}, ${opacity / 100})`;
    octx.fillRect(0, 0, w, h);

    if (sel && !sel.isEmpty()) {
      // Punch holes in the red overlay where the selection mask is > 0.
      const imageData = octx.createImageData(w, h);
      const pixels = imageData.data;
      const mask = sel.mask;
      for (let i = 0; i < mask.length; i++) {
        const m = mask[i] ?? 0;
        const selected = m / 255;
        const a = indicate === "masked"
          ? Math.round((1 - selected) * alpha)
          : Math.round(selected * alpha);
        const o = i * 4;
        pixels[o] = color.r;
        pixels[o + 1] = color.g;
        pixels[o + 2] = color.b;
        pixels[o + 3] = a;
      }
      octx.putImageData(imageData, 0, 0);
    }

    // Draw the overlay at the document position with current zoom.
    ctx.save();
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    ctx.translate(viewport.panX, viewport.panY);
    ctx.globalCompositeOperation = "source-over";
    ctx.drawImage(off, 0, 0, w, h, 0, 0, w * zoom, h * zoom);
    ctx.restore();
  }

  /**
   * Draw tool overlays: crop rect with dimmed-outside area, rule-of-thirds
   * grid, Free Transform bounding box with 8 handles, document grid, and guides.
   * All drawn in CSS-pixel space so UI elements stay a constant on-screen size.
   */
  private drawToolOverlays(
    ctx: CanvasRenderingContext2D,
    viewport: Viewport,
    doc: Document,
  ): void {
    const zoom = viewport.zoom;
    const { cropRect, transformBBox, grid, guides, penSession } = this.overlay;

    // Grid overlay
    if (grid?.visible && grid.spacing > 0) {
      ctx.save();
      ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
      ctx.translate(viewport.panX, viewport.panY);
      ctx.scale(zoom, zoom);

      const spacing = grid.spacing;
      const color = grid.color ?? "rgba(200, 200, 200, 0.3)";
      ctx.strokeStyle = color;
      ctx.lineWidth = 1 / zoom;

      // Vertical lines
      ctx.beginPath();
      for (let x = spacing; x < doc.width; x += spacing) {
        ctx.moveTo(x, 0);
        ctx.lineTo(x, doc.height);
      }
      // Horizontal lines
      for (let y = spacing; y < doc.height; y += spacing) {
        ctx.moveTo(0, y);
        ctx.lineTo(doc.width, y);
      }
      ctx.stroke();

      // Major gridlines (#i256) — Photoshop's "Gridline every: N, Subdivisions:
      // M" draws a heavier/differently-colored line every Mth subdivision.
      // Drawn as a second pass over the positions that are multiples of
      // `majorEvery` minor-line steps, on top of (after) the minor lines above.
      if (grid.majorEvery && grid.majorEvery > 0) {
        const majorSpacing = spacing * grid.majorEvery;
        ctx.strokeStyle = grid.majorColor ?? color;
        ctx.beginPath();
        for (let x = majorSpacing; x < doc.width; x += majorSpacing) {
          ctx.moveTo(x, 0);
          ctx.lineTo(x, doc.height);
        }
        for (let y = majorSpacing; y < doc.height; y += majorSpacing) {
          ctx.moveTo(0, y);
          ctx.lineTo(doc.width, y);
        }
        ctx.stroke();
      }
      ctx.restore();
    }

    // Guides overlay
    if (guides && guides.length > 0) {
      ctx.save();
      ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
      ctx.translate(viewport.panX, viewport.panY);
      ctx.scale(zoom, zoom);

      // PS default guide color is cyan #00c8ff (user-configurable, but cyan is the default).
      ctx.strokeStyle = "#00c8ff";
      ctx.lineWidth = 1 / zoom;
      ctx.setLineDash([]); // guides are always solid, never dashed

      for (const guide of guides) {
        ctx.beginPath();
        if (guide.kind === "horizontal") {
          ctx.moveTo(0, guide.position);
          ctx.lineTo(doc.width, guide.position);
        } else {
          ctx.moveTo(guide.position, 0);
          ctx.lineTo(guide.position, doc.height);
        }
        ctx.stroke();
      }
      ctx.restore();
    }

    if (cropRect && cropRect.w > 0 && cropRect.h > 0) {
      // Crop overlay: dim the area outside the crop rect, draw rule-of-thirds
      // (or another style), rotated about the rect's own center when the
      // rotation handle / Straighten has tilted it (#i21).
      const rotation = cropRect.rotation ?? 0;
      const cw = cropRect.w;
      const ch = cropRect.h;
      const centerX = cropRect.x + cw / 2;
      const centerY = cropRect.y + ch / 2;
      // Local (rect-centered, unrotated) bounds — used for every shape drawn
      // inside the rotate+translate transform below.
      const lx0 = -cw / 2;
      const ly0 = -ch / 2;
      const lx1 = cw / 2;
      const ly1 = ch / 2;
      const dw = doc.width;
      const dh = doc.height;

      // Reset to base transform for overlay drawing.
      ctx.save();
      ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
      ctx.translate(viewport.panX, viewport.panY);
      ctx.scale(zoom, zoom);

      // PS crop shield: dims the area outside the crop rect. An
      // axis-aligned rect punches 4 strips; a rotated rect shields the whole
      // canvas and cuts a rotated rect-shaped hole out of it.
      // #i108: opacity now comes from CropTool.shieldOpacity (via
      // this.overlay.cropShieldOpacity, wired by main.ts) instead of a
      // hardcoded 0.5; the shield can also be turned off entirely
      // (CropTool.shieldOutside -> this.overlay.cropShieldVisible).
      if (this.overlay.cropShieldVisible !== false) {
        const shieldOpacity = this.overlay.cropShieldOpacity ?? 0.5;
        ctx.fillStyle = `rgba(0, 0, 0, ${shieldOpacity})`;
        if (rotation === 0) {
          const cx = cropRect.x;
          const cy = cropRect.y;
          // Top strip
          if (cy > 0) ctx.fillRect(0, 0, dw, cy);
          // Bottom strip
          if (cy + ch < dh) ctx.fillRect(0, cy + ch, dw, dh - cy - ch);
          // Left strip
          if (cx > 0) ctx.fillRect(0, cy, cx, ch);
          // Right strip
          if (cx + cw < dw) ctx.fillRect(cx + cw, cy, dw - cx - cw, ch);
        } else {
          ctx.fillRect(0, 0, dw, dh);
          ctx.save();
          ctx.globalCompositeOperation = "destination-out";
          ctx.translate(centerX, centerY);
          ctx.rotate(rotation);
          ctx.fillRect(lx0, ly0, cw, ch);
          ctx.restore();
        }
      }

      // Crop overlay grid + border, drawn in the rect's local (rotated)
      // frame so they rotate together with the rect (style is set by
      // CropTool.overlay via ToolOverlay.cropOverlay).
      ctx.save();
      ctx.translate(centerX, centerY);
      ctx.rotate(rotation);

      const cropOverlayStyle = this.overlay.cropOverlay ?? "rule-thirds";
      ctx.strokeStyle = "rgba(255, 255, 255, 0.4)";
      ctx.lineWidth = 1 / zoom; // 1 CSS-pixel wide regardless of zoom
      ctx.beginPath();
      if (cropOverlayStyle === "grid") {
        // Dense 6×6 grid
        for (let c = 1; c < 6; c++) {
          const x6 = lx0 + (cw * c) / 6;
          ctx.moveTo(x6, ly0); ctx.lineTo(x6, ly1);
        }
        for (let r = 1; r < 6; r++) {
          const y6 = ly0 + (ch * r) / 6;
          ctx.moveTo(lx0, y6); ctx.lineTo(lx1, y6);
        }
      } else if (cropOverlayStyle === "diagonal") {
        // Two diagonals
        ctx.moveTo(lx0, ly0); ctx.lineTo(lx1, ly1);
        ctx.moveTo(lx1, ly0); ctx.lineTo(lx0, ly1);
      } else if (cropOverlayStyle === "golden-ratio") {
        // Vertical and horizontal lines at golden-ratio split (~38.2% / 61.8%)
        const phi = 1 - 1 / 1.618;
        ctx.moveTo(lx0 + cw * phi, ly0); ctx.lineTo(lx0 + cw * phi, ly1);
        ctx.moveTo(lx0 + cw * (1 - phi), ly0); ctx.lineTo(lx0 + cw * (1 - phi), ly1);
        ctx.moveTo(lx0, ly0 + ch * phi); ctx.lineTo(lx1, ly0 + ch * phi);
        ctx.moveTo(lx0, ly0 + ch * (1 - phi)); ctx.lineTo(lx1, ly0 + ch * (1 - phi));
      } else if (cropOverlayStyle === "triangle") {
        // Golden triangle: main diagonal + perpendiculars from other corners
        ctx.moveTo(lx0, ly1); ctx.lineTo(lx1, ly0);
        // Perpendicular from top-left to main diagonal
        const tNum = (ly0 - ly1) * (-ch) - (lx0 - lx0) * (-cw);
        const tDen = (cw * cw + ch * ch);
        const t = tNum / tDen;
        const footX = lx0 + t * cw;
        const footY = ly1 + t * (-ch);
        ctx.moveTo(lx0, ly0); ctx.lineTo(footX, footY);
        // From bottom-right corner
        const tNum2 = (ly1 - ly1) * (-ch) - (lx1 - lx0) * (-cw);
        const t2 = tNum2 / tDen;
        const footX2 = lx0 + t2 * cw;
        const footY2 = ly1 + t2 * (-ch);
        ctx.moveTo(lx1, ly1); ctx.lineTo(footX2, footY2);
      } else if (cropOverlayStyle === "golden-spiral") {
        // Golden (Fibonacci) spiral (#i108): repeatedly cut the largest
        // possible square off the long side of the remaining rectangle and
        // trace a quarter-circle arc inscribed in that square. Consecutive
        // squares always share an edge, so the arcs join into one
        // continuous curling spiral. Whenever the remainder becomes taller
        // than it is wide, the LOCAL drawing frame itself is rotated -90°
        // (about the corner the next square will be cut from) so the same
        // "cut the left square, arc from its bottom-left to its top-right
        // corner" step keeps applying — this is what keeps the spiral
        // curling consistently in one direction across iterations instead
        // of alternating.  Drawn (and stroked) here directly since it needs
        // its own nested save/translate/rotate, unlike the straight-line
        // styles above which share the single ctx.stroke() below.
        ctx.save();
        ctx.translate(lx0, ly0);
        let sw = cw, sh = ch;
        for (let iter = 0; iter < 12 && sw > 0.5 && sh > 0.5; iter++) {
          if (sh > sw) {
            ctx.translate(0, sh);
            ctx.rotate(-Math.PI / 2);
            const tmp = sw; sw = sh; sh = tmp;
          }
          const side = sh;
          ctx.beginPath();
          ctx.arc(side, side, side, Math.PI, 1.5 * Math.PI);
          ctx.stroke();
          ctx.translate(side, 0);
          sw -= side;
        }
        ctx.restore();
        // i108: the arcs above are already stroked per-iteration; clear the
        // leftover current path (not part of save/restore state) so the shared
        // ctx.stroke() below doesn't re-stroke the final arc at double opacity.
        ctx.beginPath();
      } else {
        // Default: Rule of Thirds (3×3 grid)
        // Vertical thirds
        ctx.moveTo(lx0 + cw / 3, ly0); ctx.lineTo(lx0 + cw / 3, ly1);
        ctx.moveTo(lx0 + 2 * cw / 3, ly0); ctx.lineTo(lx0 + 2 * cw / 3, ly1);
        // Horizontal thirds
        ctx.moveTo(lx0, ly0 + ch / 3); ctx.lineTo(lx1, ly0 + ch / 3);
        ctx.moveTo(lx0, ly0 + 2 * ch / 3); ctx.lineTo(lx1, ly0 + 2 * ch / 3);
      }
      ctx.stroke();

      // Crop border.
      ctx.strokeStyle = "#ffffff";
      ctx.lineWidth = 1 / zoom;
      ctx.strokeRect(lx0, ly0, cw, ch);

      ctx.restore(); // undo rotate+translate
      ctx.restore(); // undo base transform

      // Handles drawn in CSS-pixel space (constant on-screen size).
      this.drawCropHandles(ctx, viewport, cropRect);
    }

    if (transformBBox && transformBBox.w > 0 && transformBBox.h > 0) {
      // Free Transform bounding box + handles. When rotated (#i106), the
      // box is drawn in a translate(center)→rotate(angle) local frame
      // instead of a plain axis-aligned strokeRect, mirroring the Crop
      // tool's rotated-rect handling (#i21) — so the box actually traces
      // the layer's rotated edges instead of its AABB envelope. Absent/0
      // rotation takes the exact original axis-aligned call sequence.
      const bboxRotation = transformBBox.rotation ?? 0;
      ctx.save();
      ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
      ctx.translate(viewport.panX, viewport.panY);
      ctx.scale(zoom, zoom);

      // Bounding box outline.
      ctx.strokeStyle = "#ffffff";
      ctx.lineWidth = 1 / zoom;
      ctx.setLineDash([]);
      if (bboxRotation === 0) {
        ctx.strokeRect(transformBBox.x, transformBBox.y, transformBBox.w, transformBBox.h);
      } else {
        // Rotate about the PIVOT (reference point), not the box centre, so the
        // outline matches the actual rotated layer for any reference point (#i106).
        const pivotX = transformBBox.pivotX ?? transformBBox.x + transformBBox.w / 2;
        const pivotY = transformBBox.pivotY ?? transformBBox.y + transformBBox.h / 2;
        ctx.translate(pivotX, pivotY);
        ctx.rotate(bboxRotation);
        ctx.strokeRect(transformBBox.x - pivotX, transformBBox.y - pivotY, transformBBox.w, transformBBox.h);
      }

      ctx.restore();

      // Handles in CSS-pixel space.
      this.drawTransformHandles(ctx, viewport, transformBBox);
    }

    // Object Selection tool — live drag-rectangle overlay (#i64). Same
    // dashed-marquee treatment as a standard marquee selection preview.
    const osDragRect = this.overlay.objectSelectionDragRect;
    if (osDragRect && osDragRect.w > 0 && osDragRect.h > 0) {
      ctx.save();
      ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
      ctx.translate(viewport.panX, viewport.panY);
      ctx.scale(zoom, zoom);

      ctx.strokeStyle = "#ffffff";
      ctx.lineWidth = 1 / zoom;
      ctx.setLineDash([4 / zoom, 4 / zoom]);
      ctx.strokeRect(osDragRect.x, osDragRect.y, osDragRect.w, osDragRect.h);
      ctx.strokeStyle = "#000000";
      ctx.lineDashOffset = 4 / zoom;
      ctx.strokeRect(osDragRect.x, osDragRect.y, osDragRect.w, osDragRect.h);
      ctx.setLineDash([]);
      ctx.restore();
    }

    // Type tool — live paragraph-text-box drag rectangle (#i102). Same
    // dashed-marquee treatment as the Object Selection drag preview above,
    // so dragging out a paragraph text box gets live visual feedback instead
    // of nothing until pointerup.
    const typeDragRect = this.overlay.typeDragRect;
    if (typeDragRect && typeDragRect.w > 0 && typeDragRect.h > 0) {
      ctx.save();
      ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
      ctx.translate(viewport.panX, viewport.panY);
      ctx.scale(zoom, zoom);

      ctx.strokeStyle = "#ffffff";
      ctx.lineWidth = 1 / zoom;
      ctx.setLineDash([4 / zoom, 4 / zoom]);
      ctx.strokeRect(typeDragRect.x, typeDragRect.y, typeDragRect.w, typeDragRect.h);
      ctx.strokeStyle = "#000000";
      ctx.lineDashOffset = 4 / zoom;
      ctx.strokeRect(typeDragRect.x, typeDragRect.y, typeDragRect.w, typeDragRect.h);
      ctx.setLineDash([]);
      ctx.restore();
    }

    // Patch tool — selection marquee + drag ghost preview (#i79).
    const patchRect = this.overlay.patchSelectionRect;
    if (patchRect) {
      const px = Math.min(patchRect.x0, patchRect.x1);
      const py = Math.min(patchRect.y0, patchRect.y1);
      const pw = Math.abs(patchRect.x1 - patchRect.x0);
      const ph = Math.abs(patchRect.y1 - patchRect.y0);
      if (pw > 0 && ph > 0) {
        ctx.save();
        ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
        ctx.translate(viewport.panX, viewport.panY);
        ctx.scale(zoom, zoom);

        // The armed selection's own marquee, always drawn at its original
        // (source) location.
        ctx.strokeStyle = "#ffffff";
        ctx.lineWidth = 1 / zoom;
        ctx.setLineDash([4 / zoom, 4 / zoom]);
        ctx.strokeRect(px, py, pw, ph);
        ctx.strokeStyle = "#000000";
        ctx.lineDashOffset = 4 / zoom;
        ctx.strokeRect(px, py, pw, ph);

        // While dragging, also draw a dimmer "ghost" outline at the
        // destination the patch content would be sampled/pasted from —
        // this is what actually gives the user live feedback of where the
        // patch destination is before they release the drag.
        const dragOffset = this.overlay.patchDragOffset;
        if (dragOffset && (dragOffset.dx !== 0 || dragOffset.dy !== 0)) {
          ctx.setLineDash([2 / zoom, 3 / zoom]);
          ctx.strokeStyle = "rgba(255, 255, 255, 0.6)";
          ctx.strokeRect(px + dragOffset.dx, py + dragOffset.dy, pw, ph);
        }
        ctx.setLineDash([]);
        ctx.restore();
      }
    }

    // Perspective Crop corner overlay (#i104 follow-up) — draw the 4 placed
    // corner handles + connecting quad outline once the tool has entered its
    // adjustment phase (all 4 corners placed).
    const perspCrop = this.overlay.perspectiveCrop;
    if (perspCrop && perspCrop.phase === "adjusting") {
      const corners = perspCrop.corners.filter((c): c is { x: number; y: number } => c !== null);
      if (corners.length === 4) {
        ctx.save();
        ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
        ctx.translate(viewport.panX, viewport.panY);
        ctx.scale(zoom, zoom);

        // Quad outline connecting the 4 corners in placement order.
        ctx.strokeStyle = "#ffffff";
        ctx.lineWidth = 1 / zoom;
        ctx.setLineDash([]);
        ctx.beginPath();
        ctx.moveTo(corners[0]!.x, corners[0]!.y);
        for (let i = 1; i < corners.length; i++) ctx.lineTo(corners[i]!.x, corners[i]!.y);
        ctx.closePath();
        ctx.stroke();

        ctx.restore();

        // Corner handles in CSS-pixel space (constant on-screen size),
        // same pattern as drawCropHandles / drawTransformHandles.
        this.drawPerspectiveCropHandles(ctx, viewport, corners);
      }
    }

    // Pen tool path preview
    if (penSession && penSession.points.length > 0) {
      this.drawPenPathOverlay(ctx, viewport, penSession, this.overlay.penCursorPos ?? null);
    }

    // Clone stamp source crosshair
    const cs = this.overlay.cloneStampSource;
    if (cs) {
      ctx.save();
      ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
      ctx.translate(viewport.panX, viewport.panY);
      ctx.scale(zoom, zoom);

      const size = 10; // crosshair arm length in doc pixels
      ctx.strokeStyle = "rgba(255, 255, 255, 0.9)";
      ctx.lineWidth = 1 / zoom;
      ctx.beginPath();
      ctx.moveTo(cs.x - size, cs.y);
      ctx.lineTo(cs.x + size, cs.y);
      ctx.moveTo(cs.x, cs.y - size);
      ctx.lineTo(cs.x, cs.y + size);
      ctx.stroke();

      // Black outline for visibility on light backgrounds
      ctx.strokeStyle = "rgba(0, 0, 0, 0.5)";
      ctx.beginPath();
      ctx.moveTo(cs.x - size, cs.y);
      ctx.lineTo(cs.x + size, cs.y);
      ctx.moveTo(cs.x, cs.y - size);
      ctx.lineTo(cs.x, cs.y + size);
      ctx.stroke();

      ctx.restore();
    }

    // Color Sampler tool — persistent numbered crosshairs at each placed
    // sample point (#i137). Disabled samplers are drawn dimmer (they're
    // still placed, just excluded from the Info panel readout).
    const samplers = this.overlay.colorSamplers;
    if (samplers && samplers.length > 0) {
      ctx.save();
      ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
      ctx.translate(viewport.panX, viewport.panY);
      ctx.scale(zoom, zoom);

      const armLen = 6; // crosshair arm length in doc pixels
      const ringR = 4; // center ring radius in doc pixels
      for (let i = 0; i < samplers.length; i++) {
        const sp = samplers[i]!;
        const alpha = sp.enabled === false ? 0.4 : 1;
        ctx.lineWidth = 1 / zoom;

        // Black outline first (visibility on light backgrounds), white on top.
        for (const style of [`rgba(0, 0, 0, ${0.6 * alpha})`, `rgba(255, 255, 255, ${alpha})`]) {
          ctx.strokeStyle = style;
          ctx.beginPath();
          ctx.moveTo(sp.x - armLen, sp.y);
          ctx.lineTo(sp.x + armLen, sp.y);
          ctx.moveTo(sp.x, sp.y - armLen);
          ctx.lineTo(sp.x, sp.y + armLen);
          ctx.stroke();
          // Center ring — separate path so arc() doesn't draw a connecting
          // line back from the crosshair's last point.
          ctx.beginPath();
          ctx.arc(sp.x, sp.y, ringR, 0, Math.PI * 2);
          ctx.stroke();
        }

        // Number badge (1-based, matching placement/Info-panel order).
        // Still inside the outer dpr/pan/zoom transform established above —
        // just move to the sampler's doc-space point, then undo the zoom
        // scale for the label itself so it stays a constant on-screen size
        // regardless of document zoom (same idea as the CSS-pixel-space
        // handle overlays elsewhere in this method).
        ctx.save();
        ctx.translate(sp.x, sp.y);
        ctx.scale(1 / zoom, 1 / zoom);
        ctx.font = "10px sans-serif";
        ctx.textAlign = "left";
        ctx.textBaseline = "bottom";
        ctx.globalAlpha = alpha;
        ctx.fillStyle = "#000000";
        ctx.fillText(String(i + 1), armLen + 3 + 1, -armLen - 1 + 1);
        ctx.fillStyle = "#ffffff";
        ctx.fillText(String(i + 1), armLen + 3, -armLen - 1);
        ctx.restore();
      }
      ctx.restore();
    }

    // Smart guides — magenta alignment guides when moving layers
    const sg = this.overlay.smartGuides;
    if (sg?.visible && sg.layers.length > 1) {
      this.drawSmartGuides(ctx, viewport, doc, sg.layers);
    }

    // Pixel grid — thin grid lines at pixel boundaries when zoomed in > 500%
    const pg = this.overlay.pixelGrid;
    if (pg?.visible && zoom > 5) {
      this.drawPixelGrid(ctx, viewport, doc, zoom);
    }

    // Proof Colors — CMYK soft proof approximation.
    // These read/write document pixels via readDocPixels()/writeDocPixelsBack(),
    // which derive the on-screen device box from ctx.getTransform(). By the time
    // drawToolOverlays() runs, the ambient transform has been reset to the
    // no-zoom (dpr·pan) doc-border transform, so — like every other overlay
    // block here — we must re-establish the full dpr·pan·zoom transform first,
    // otherwise the sampled/painted rectangle is the wrong size at any zoom≠100%.
    const pc = this.overlay.proofColors;
    if (pc?.enabled) {
      ctx.save();
      ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
      ctx.translate(viewport.panX, viewport.panY);
      ctx.scale(zoom, zoom);
      this.applyProofColors(ctx, doc.width, doc.height);
      ctx.restore();
    }

    // Gamut Warning — highlight out-of-gamut pixels
    const gw = this.overlay.gamutWarning;
    if (gw?.enabled) {
      ctx.save();
      ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
      ctx.translate(viewport.panX, viewport.panY);
      ctx.scale(zoom, zoom);
      this.applyGamutWarning(ctx, doc.width, doc.height);
      ctx.restore();
    }

    // Layer Edges — outline each layer's bounds
    const le = this.overlay.layerEdges;
    if (le?.visible) {
      this.drawLayerEdges(ctx, viewport, doc);
    }
  }

  /** Draw smart guides — magenta alignment guides when layers align. */
  private drawSmartGuides(
    ctx: CanvasRenderingContext2D,
    viewport: Viewport,
    doc: Document,
    layers: Array<{ id: string; x: number; y: number; w: number; h: number }>,
  ): void {
    if (layers.length < 2) return;
    const zoom = viewport.zoom;
    const threshold = 3 / zoom; // 3 CSS pixels threshold

    ctx.save();
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    ctx.translate(viewport.panX, viewport.panY);
    ctx.scale(zoom, zoom);

    ctx.strokeStyle = "#ff00ff"; // magenta
    ctx.lineWidth = 1 / zoom;
    ctx.setLineDash([4 / zoom, 4 / zoom]);

    const activeLayer = layers[layers.length - 1]!;
    const ax1 = activeLayer.x;
    const ay1 = activeLayer.y;
    const ax2 = activeLayer.x + activeLayer.w;
    const ay2 = activeLayer.y + activeLayer.h;
    const acx = activeLayer.x + activeLayer.w / 2;
    const acy = activeLayer.y + activeLayer.h / 2;

    for (let i = 0; i < layers.length - 1; i++) {
      const l = layers[i]!;
      const x1 = l.x, y1 = l.y, x2 = l.x + l.w, y2 = l.y + l.h;
      const cx = l.x + l.w / 2, cy = l.y + l.h / 2;

      // Horizontal alignment guides
      if (Math.abs(ay1 - y1) < threshold) { this.drawGuideLine(ctx, 0, ay1, doc.width, ay1); }
      if (Math.abs(ay1 - y2) < threshold) { this.drawGuideLine(ctx, 0, ay1, doc.width, ay1); }
      if (Math.abs(ay2 - y1) < threshold) { this.drawGuideLine(ctx, 0, ay2, doc.width, ay2); }
      if (Math.abs(ay2 - y2) < threshold) { this.drawGuideLine(ctx, 0, ay2, doc.width, ay2); }
      if (Math.abs(acy - cy) < threshold) { this.drawGuideLine(ctx, 0, acy, doc.width, acy); }

      // Vertical alignment guides
      if (Math.abs(ax1 - x1) < threshold) { this.drawGuideLine(ctx, ax1, 0, ax1, doc.height); }
      if (Math.abs(ax1 - x2) < threshold) { this.drawGuideLine(ctx, ax1, 0, ax1, doc.height); }
      if (Math.abs(ax2 - x1) < threshold) { this.drawGuideLine(ctx, ax2, 0, ax2, doc.height); }
      if (Math.abs(ax2 - x2) < threshold) { this.drawGuideLine(ctx, ax2, 0, ax2, doc.height); }
      if (Math.abs(acx - cx) < threshold) { this.drawGuideLine(ctx, acx, 0, acx, doc.height); }

      // Canvas edge guides
      if (Math.abs(ax1) < threshold) { this.drawGuideLine(ctx, 0, 0, 0, doc.height); }
      if (Math.abs(ax2 - doc.width) < threshold) { this.drawGuideLine(ctx, doc.width, 0, doc.width, doc.height); }
      if (Math.abs(ay1) < threshold) { this.drawGuideLine(ctx, 0, 0, doc.width, 0); }
      if (Math.abs(ay2 - doc.height) < threshold) { this.drawGuideLine(ctx, 0, doc.height, doc.width, doc.height); }
    }

    ctx.setLineDash([]);
    ctx.restore();
  }

  private drawGuideLine(ctx: CanvasRenderingContext2D, x1: number, y1: number, x2: number, y2: number): void {
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.stroke();
  }

  /** Draw pixel grid at zoom > 500% — thin lines at pixel boundaries. */
  private drawPixelGrid(
    ctx: CanvasRenderingContext2D,
    viewport: Viewport,
    doc: Document,
    zoom: number,
  ): void {
    const visibleLeft = -viewport.panX / zoom;
    const visibleTop = -viewport.panY / zoom;
    const visibleRight = (this.widthCss - viewport.panX) / zoom;
    const visibleBottom = (this.heightCss - viewport.panY) / zoom;

    const x0 = Math.max(0, Math.floor(visibleLeft));
    const y0 = Math.max(0, Math.floor(visibleTop));
    const x1 = Math.min(doc.width, Math.ceil(visibleRight));
    const y1 = Math.min(doc.height, Math.ceil(visibleBottom));

    if (x1 <= x0 || y1 <= y0) return;

    ctx.save();
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    ctx.translate(viewport.panX, viewport.panY);
    ctx.scale(zoom, zoom);

    ctx.strokeStyle = "rgba(128, 128, 128, 0.3)";
    ctx.lineWidth = 0.5 / zoom;

    ctx.beginPath();
    for (let x = x0; x <= x1; x++) {
      ctx.moveTo(x, y0);
      ctx.lineTo(x, y1);
    }
    for (let y = y0; y <= y1; y++) {
      ctx.moveTo(x0, y);
      ctx.lineTo(x1, y);
    }
    ctx.stroke();
    ctx.restore();
  }

  /** Draw Layer Edges — outline each visible layer's bounds (#i146). */
  private drawLayerEdges(
    ctx: CanvasRenderingContext2D,
    viewport: Viewport,
    doc: Document,
  ): void {
    const zoom = viewport.zoom;
    ctx.save();
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    ctx.translate(viewport.panX, viewport.panY);
    ctx.scale(zoom, zoom);

    ctx.strokeStyle = "rgba(0, 170, 255, 0.7)";
    ctx.lineWidth = 1 / zoom;
    ctx.setLineDash([3 / zoom, 3 / zoom]);

    // #i146: RasterLayer/TypeLayer/etc. have no w/h fields (only
    // pixels.width/height, or nothing at all for vector/adjustment kinds),
    // so reading `layer.w`/`layer.h` directly was always undefined and drew
    // nothing for any real layer. `layerBBox()` already computes correct
    // per-kind bounds (including a real union box for groups, via
    // groupBoundingBox()) — reuse it here, and recurse into group.children
    // so nested layers get their own individual outline too, not just the
    // group's outer box.
    const drawLayerBounds = (layers: readonly Layer[]) => {
      for (const layer of layers) {
        if (!layer.visible || layer.opacity <= 0) continue;
        const bbox = this.layerBBox(layer);
        if (bbox && bbox.w > 0 && bbox.h > 0) {
          ctx.strokeRect(bbox.x, bbox.y, bbox.w, bbox.h);
        }
        if (layer.kind === "group") {
          drawLayerBounds((layer as GroupLayer).children);
        }
      }
    };

    drawLayerBounds(doc.layers);
    ctx.restore();
  }

  /**
   * Proof Colors — apply CMYK color profile approximation (#i145).
   * CTM-correct read (this.readDocPixels) + write-back (temp-canvas +
   * ctx.drawImage), same pattern as applyChannelModeFilter below: a raw
   * `ctx.getImageData(0,0,w,h)`/`putImageData(0,0)` pair ignores the
   * pan/zoom/dpr transform and mis-registers whenever zoom≠1, pan≠0, or
   * dpr≠1 (i.e. always on HiDPI) — see readDocPixels' docstring for detail.
   */
  private applyProofColors(ctx: CanvasRenderingContext2D, w: number, h: number): void {
    if (w <= 0 || h <= 0) return;
    const imageData = this.readDocPixels(ctx, w, h);
    if (!imageData) return;
    const data = imageData.data;
    // Simple CMYK approximation: reduce saturation and shift colors slightly
    // to simulate the narrower CMYK gamut vs RGB
    for (let i = 0; i < data.length; i += 4) {
      const r = data[i]!, g = data[i + 1]!, b = data[i + 2]!;
      // Convert RGB to CMYK and back (lossy conversion simulates proof)
      const c = 1 - r / 255;
      const m = 1 - g / 255;
      const y = 1 - b / 255;
      const k = Math.min(c, m, y);
      // Convert back with slight gamut compression
      const cmykR = Math.round(255 * (1 - c) * (1 - k * 0.95));
      const cmykG = Math.round(255 * (1 - m) * (1 - k * 0.95));
      const cmykB = Math.round(255 * (1 - y) * (1 - k * 0.95));
      data[i] = cmykR;
      data[i + 1] = cmykG;
      data[i + 2] = cmykB;
    }
    this.writeDocPixelsBack(ctx, imageData, w, h);
  }

  /**
   * Gamut Warning — overlay gray on pixels that would be out of CMYK gamut
   * (#i145). CTM-correct read/write-back, same rationale as
   * applyProofColors above.
   */
  private applyGamutWarning(ctx: CanvasRenderingContext2D, w: number, h: number): void {
    if (w <= 0 || h <= 0) return;
    const imageData = this.readDocPixels(ctx, w, h);
    if (!imageData) return;
    const data = imageData.data;
    for (let i = 0; i < data.length; i += 4) {
      const r = data[i]!, g = data[i + 1]!, b = data[i + 2]!;
      // Check if pixel is out of CMYK gamut (simplified check)
      // Highly saturated colors, very bright colors are typically out of gamut
      const max = Math.max(r, g, b);
      const min = Math.min(r, g, b);
      const saturation = max === 0 ? 0 : (max - min) / max;
      // If saturation is high and brightness is high, likely out of gamut
      if (saturation > 0.8 && max > 200) {
        // Overlay gray
        data[i] = 128;
        data[i + 1] = 128;
        data[i + 2] = 128;
      }
    }
    this.writeDocPixelsBack(ctx, imageData, w, h);
  }

  /**
   * CTM-correct write-back for a doc-resolution ImageData (#i145): calling
   * putImageData directly on the main context ignores the CTM and would
   * splat the result to the device top-left. Routes it through a temp canvas and
   * `ctx.drawImage` under the current transform instead, so it lands back
   * on the on-screen document region — the exact write-back pattern
   * applyChannelModeFilter already uses (see that method, a few methods up
   * in this file), reused here for Proof Colors / Gamut Warning.
   */
  private writeDocPixelsBack(ctx: CanvasRenderingContext2D, imageData: ImageData, w: number, h: number): void {
    let out = this.offscreenTemp;
    if (!out) { out = document.createElement("canvas"); this.offscreenTemp = out; }
    out.width = w;
    out.height = h;
    const octx = out.getContext("2d");
    if (!octx) return;
    octx.setTransform(1, 0, 0, 1, 0, 0);
    octx.clearRect(0, 0, w, h);
    octx.putImageData(imageData, 0, 0);
    ctx.save();
    ctx.imageSmoothingEnabled = false;
    ctx.clearRect(0, 0, w, h);
    ctx.globalCompositeOperation = "source-over";
    ctx.drawImage(out, 0, 0);
    ctx.restore();
  }

  /**
   * Draw the 8 crop handles in CSS-pixel space (constant on-screen size).
   * When `rect.rotation` is set, handle positions are rotated about the
   * rect's own center to match the tilted overlay (#i21).
   */
  private drawCropHandles(
    ctx: CanvasRenderingContext2D,
    viewport: Viewport,
    rect: { x: number; y: number; w: number; h: number; rotation?: number },
  ): void {
    const zoom = viewport.zoom;
    const handles = [
      { x: rect.x, y: rect.y },                        // TL
      { x: rect.x + rect.w / 2, y: rect.y },           // T
      { x: rect.x + rect.w, y: rect.y },               // TR
      { x: rect.x + rect.w, y: rect.y + rect.h / 2 },  // R
      { x: rect.x + rect.w, y: rect.y + rect.h },      // BR
      { x: rect.x + rect.w / 2, y: rect.y + rect.h },  // B
      { x: rect.x, y: rect.y + rect.h },               // BL
      { x: rect.x, y: rect.y + rect.h / 2 },           // L
    ];
    const size = 6; // CSS pixels
    const rotation = rect.rotation ?? 0;
    const centerX = rect.x + rect.w / 2;
    const centerY = rect.y + rect.h / 2;
    const cos = Math.cos(rotation);
    const sin = Math.sin(rotation);

    ctx.save();
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    ctx.translate(viewport.panX, viewport.panY);

    ctx.fillStyle = "#ffffff";
    ctx.strokeStyle = "#000000";
    ctx.lineWidth = 1;

    for (const h of handles) {
      // Rotate the handle's doc-space position about the rect's center
      // before projecting to screen space.
      const dx = h.x - centerX;
      const dy = h.y - centerY;
      const rx = rotation === 0 ? h.x : centerX + dx * cos - dy * sin;
      const ry = rotation === 0 ? h.y : centerY + dx * sin + dy * cos;
      const sx = rx * zoom;
      const sy = ry * zoom;
      ctx.fillRect(sx - size / 2, sy - size / 2, size, size);
      ctx.strokeRect(sx - size / 2, sy - size / 2, size, size);
    }
    ctx.restore();
  }

  /**
   * Draw the 8 transform handles + rotation handle in CSS-pixel space.
   * When `bbox.rotation` is set (#i106), each handle's doc-space anchor
   * point is rotated about the box's own center before being projected to
   * screen space — same technique as `drawCropHandles` (#i21) — so the
   * squares land on the box's true rotated corners/edges instead of its
   * axis-aligned envelope. The squares themselves are still drawn upright
   * (not rotated), matching Photoshop and the crop-handle precedent.
   * Absent/0 rotation reduces to the exact original (unrotated) math.
   */
  private drawTransformHandles(
    ctx: CanvasRenderingContext2D,
    viewport: Viewport,
    bbox: { x: number; y: number; w: number; h: number; rotation?: number; pivotX?: number; pivotY?: number },
  ): void {
    const zoom = viewport.zoom;
    const rotation = bbox.rotation ?? 0;
    // Rotate handle anchors about the PIVOT (reference point), matching the
    // outline + the tool's own hit-testing, not the box centre (#i106).
    const pivotX = bbox.pivotX ?? bbox.x + bbox.w / 2;
    const pivotY = bbox.pivotY ?? bbox.y + bbox.h / 2;
    const cos = Math.cos(rotation);
    const sin = Math.sin(rotation);
    const rotatePoint = (x: number, y: number): { x: number; y: number } => {
      if (rotation === 0) return { x, y };
      const dx = x - pivotX;
      const dy = y - pivotY;
      return { x: pivotX + dx * cos - dy * sin, y: pivotY + dx * sin + dy * cos };
    };

    const handles = [
      { x: bbox.x, y: bbox.y },                        // TL
      { x: bbox.x + bbox.w / 2, y: bbox.y },           // T
      { x: bbox.x + bbox.w, y: bbox.y },               // TR
      { x: bbox.x + bbox.w, y: bbox.y + bbox.h / 2 },  // R
      { x: bbox.x + bbox.w, y: bbox.y + bbox.h },      // BR
      { x: bbox.x + bbox.w / 2, y: bbox.y + bbox.h },  // B
      { x: bbox.x, y: bbox.y + bbox.h },               // BL
      { x: bbox.x, y: bbox.y + bbox.h / 2 },           // L
    ].map((h) => rotatePoint(h.x, h.y));
    // PS free-transform handles: 7×7px hollow square, 1px black outer border,
    // white fill. Use imageSmoothingEnabled=false for crisp pixel-aligned squares.
    const hSize = 7; // CSS pixels

    ctx.save();
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    ctx.translate(viewport.panX, viewport.panY);
    ctx.imageSmoothingEnabled = false;
    ctx.lineWidth = 1;

    // Rotation handle (above top-center), rotated about the box's center so
    // it stays attached to the (possibly rotated) top edge (#i106).
    const rotHandleAnchor = rotatePoint(bbox.x + bbox.w / 2, bbox.y - 20);
    const rotX = rotHandleAnchor.x * zoom;
    const rotY = rotHandleAnchor.y * zoom;
    ctx.fillStyle = "#ffffff";
    ctx.strokeStyle = "#000000";
    ctx.beginPath();
    ctx.arc(rotX, rotY, hSize / 2, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    // Line from top-center to rotation handle.
    const topCenterAnchor = rotatePoint(bbox.x + bbox.w / 2, bbox.y);
    ctx.beginPath();
    ctx.moveTo(topCenterAnchor.x * zoom, topCenterAnchor.y * zoom);
    ctx.lineTo(rotX, rotY);
    ctx.strokeStyle = "rgba(255,255,255,0.7)";
    ctx.stroke();

    // Corner + edge handles: PS-style 7×7 hollow square (white fill, black 1px border).
    // fillRect(cx-3, cy-3, 7, 7) places the square pixel-perfectly.
    ctx.fillStyle = "#ffffff";
    ctx.strokeStyle = "#000000";
    for (const h of handles) {
      const sx = Math.round(h.x * zoom);
      const sy = Math.round(h.y * zoom);
      ctx.fillRect(sx - 3, sy - 3, hSize, hSize);
      ctx.strokeRect(sx - 3 + 0.5, sy - 3 + 0.5, hSize - 1, hSize - 1);
    }
    ctx.restore();
  }

  /**
   * Draw the 4 Perspective Crop corner handles (#i104 follow-up) in
   * CSS-pixel space, same pattern as drawCropHandles / drawTransformHandles
   * (constant on-screen size regardless of zoom).
   */
  private drawPerspectiveCropHandles(
    ctx: CanvasRenderingContext2D,
    viewport: Viewport,
    corners: Array<{ x: number; y: number }>,
  ): void {
    const zoom = viewport.zoom;
    const size = 8; // CSS pixels — a hair larger than the crop/transform handles.

    ctx.save();
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    ctx.translate(viewport.panX, viewport.panY);

    ctx.fillStyle = "#ffffff";
    ctx.strokeStyle = "#000000";
    ctx.lineWidth = 1;

    for (const c of corners) {
      const sx = c.x * zoom;
      const sy = c.y * zoom;
      ctx.beginPath();
      ctx.arc(sx, sy, size / 2, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
    }
    ctx.restore();
  }

  /** Draw pen tool path overlay: path segments, anchor points, handles, rubber-band line. */
  private drawPenPathOverlay(
    ctx: CanvasRenderingContext2D,
    viewport: Viewport,
    session: { points: AnchorPoint[]; closed: boolean },
    cursorPos: { x: number; y: number } | null,
  ): void {
    const zoom = viewport.zoom;
    const pts = session.points;
    if (pts.length === 0) return;

    ctx.save();
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    ctx.translate(viewport.panX, viewport.panY);

    // Draw path segments
    ctx.strokeStyle = "#3b82f6";
    ctx.lineWidth = 1.5;
    ctx.setLineDash([]);
    ctx.beginPath();
    const firstScreen = { x: pts[0]!.x * zoom, y: pts[0]!.y * zoom };
    ctx.moveTo(firstScreen.x, firstScreen.y);

    for (let i = 1; i < pts.length; i++) {
      const prev = pts[i - 1]!;
      const curr = pts[i]!;
      if (prev.rightDirection && curr.leftDirection) {
        ctx.bezierCurveTo(
          prev.rightDirection.x * zoom, prev.rightDirection.y * zoom,
          curr.leftDirection.x * zoom, curr.leftDirection.y * zoom,
          curr.x * zoom, curr.y * zoom,
        );
      } else {
        ctx.lineTo(curr.x * zoom, curr.y * zoom);
      }
    }

    // Close path if needed
    if (session.closed && pts.length > 2) {
      const last = pts[pts.length - 1]!;
      const first = pts[0]!;
      if (last.rightDirection && first.leftDirection) {
        ctx.bezierCurveTo(
          last.rightDirection.x * zoom, last.rightDirection.y * zoom,
          first.leftDirection.x * zoom, first.leftDirection.y * zoom,
          first.x * zoom, first.y * zoom,
        );
      } else {
        ctx.lineTo(firstScreen.x, firstScreen.y);
      }
      ctx.closePath();
    }
    ctx.stroke();

    // Rubber-band preview line from last point to cursor
    if (cursorPos && !session.closed) {
      const last = pts[pts.length - 1]!;
      ctx.strokeStyle = "rgba(59, 130, 246, 0.5)";
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 4]);
      ctx.beginPath();
      ctx.moveTo(last.x * zoom, last.y * zoom);
      ctx.lineTo(cursorPos.x * zoom, cursorPos.y * zoom);
      ctx.stroke();
      ctx.setLineDash([]);
    }

    // Draw control handles and anchor points
    const anchorSize = 4; // CSS pixels
    const handleSize = 3;

    for (let i = 0; i < pts.length; i++) {
      const pt = pts[i]!;
      const sx = pt.x * zoom;
      const sy = pt.y * zoom;

      // Draw control handles
      if (pt.rightDirection) {
        const hx = pt.rightDirection.x * zoom;
        const hy = pt.rightDirection.y * zoom;
        ctx.strokeStyle = "#666";
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(sx, sy);
        ctx.lineTo(hx, hy);
        ctx.stroke();
        // Handle endpoint circle
        ctx.fillStyle = "#ffffff";
        ctx.strokeStyle = "#333";
        ctx.beginPath();
        ctx.arc(hx, hy, handleSize, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
      }
      if (pt.leftDirection) {
        const hx = pt.leftDirection.x * zoom;
        const hy = pt.leftDirection.y * zoom;
        ctx.strokeStyle = "#666";
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(sx, sy);
        ctx.lineTo(hx, hy);
        ctx.stroke();
        // Handle endpoint circle
        ctx.fillStyle = "#ffffff";
        ctx.strokeStyle = "#333";
        ctx.beginPath();
        ctx.arc(hx, hy, handleSize, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
      }
    }

    // Draw anchor points on top
    for (const pt of pts) {
      const sx = pt.x * zoom;
      const sy = pt.y * zoom;
      ctx.fillStyle = "#ffffff";
      ctx.strokeStyle = "#000000";
      ctx.lineWidth = 1;
      ctx.fillRect(sx - anchorSize / 2, sy - anchorSize / 2, anchorSize, anchorSize);
      ctx.strokeRect(sx - anchorSize / 2, sy - anchorSize / 2, anchorSize, anchorSize);
    }

    // Highlight the first point if path is not closed and has >2 points (close target)
    if (!session.closed && pts.length > 2) {
      const first = pts[0]!;
      const sx = first.x * zoom;
      const sy = first.y * zoom;
      ctx.strokeStyle = "#3b82f6";
      ctx.lineWidth = 2;
      const highlightSize = anchorSize + 2;
      ctx.strokeRect(sx - highlightSize / 2, sy - highlightSize / 2, highlightSize, highlightSize);
    }

    ctx.restore();
  }

  /**
     * Render a TypeLayer to the canvas using Canvas2D text API.
     */
  /**
   * Paint the text content of `layer` into `ctx`.  Coordinates are in the
   * document coordinate system (layer.x / layer.y already included).
   * Shared by the direct render path and the offscreen-for-styles path.
   */
  private paintTypeLayerContent(ctx: CanvasRenderingContext2D, layer: TypeLayer): void {
    const color = layer.color;
    ctx.fillStyle = `rgba(${color.r},${color.g},${color.b},${color.a})`;

    const aaMode = layer.antiAlias ?? "smooth";
    // #i97: ctx.imageSmoothingEnabled/Quality is still set for consistency
    // with any drawImage-based compositing of this layer's offscreen
    // canvases (styles/warp/vector-mask path), but it has NO effect on
    // fillText() itself — Canvas2D never consults it for text rasterization.
    // The actual mode-dependent glyph rendering happens in fillTextAA(),
    // which every glyph-drawing call site below routes through via
    // `this._typeAAMode`.
    this._typeAAMode = aaMode;
    if (aaMode === "none") {
      ctx.imageSmoothingEnabled = false;
    } else {
      ctx.imageSmoothingEnabled = true;
      switch (aaMode) {
        case "sharp": ctx.imageSmoothingQuality = "low"; break;
        case "crisp": ctx.imageSmoothingQuality = "medium"; break;
        case "strong":
        case "smooth": ctx.imageSmoothingQuality = "high"; break;
      }
    }

    let weight = layer.fontWeight;
    if (layer.fauxBold) weight = Math.min(900, weight + 300);
    let fontStyle = layer.fontStyle;
    if (layer.fauxItalic && fontStyle === "normal") fontStyle = "italic";
    const fontSize = layer.fontSize * (layer.superscript ? 0.6 : layer.subscript ? 0.6 : 1);
    ctx.font = `${fontStyle} ${weight} ${fontSize}px "${layer.fontFamily}"`;
    const canvasAlign: CanvasTextAlign = layer.textAlign === "justify" ? "left" : layer.textAlign;
    ctx.textAlign = canvasAlign;
    ctx.textBaseline = layer.textBaseline;

    const baselineShift = layer.baselineShift ?? 0;
    const verticalShift = layer.superscript ? -layer.fontSize * 0.35 : layer.subscript ? layer.fontSize * 0.25 : baselineShift;

    let displayText = layer.text;
    if (layer.allCaps) displayText = displayText.toUpperCase();
    else if (layer.smallCaps) {
      displayText = displayText.replace(/[a-z]/g, (c) => c.toUpperCase());
    }

    const lineHeight = layer.fontSize * layer.leading;

    // Vertical (CJK) orientation (#i15) — glyphs flow top-to-bottom within a
    // column and columns advance right-to-left. This is handled by a
    // dedicated method (both point- and box-anchored text) so the horizontal
    // path below stays byte-for-byte unchanged when `layer.vertical` is
    // falsy.
    if (layer.vertical) {
      this.paintVerticalTypeLayerContent(ctx, layer, displayText, fontSize, verticalShift, lineHeight);
      return;
    }

    // Per-run text styling (#i17 follow-up): when the layer carries
    // meaningful per-character run overrides (currently fontSize, wired from
    // the Character panel; fontFamily/fontWeight/fontStyle/color are
    // supported too if a run carries them), paint each run segment with its
    // own font/color, advancing the pen by that segment's measured width.
    // Only point text is run-aware: paragraph (`textBounds`) wrapping already
    // normalises whitespace when it rewraps lines, so wrapped-line text no
    // longer has a reliable 1:1 offset mapping back into `layer.text` for
    // locating which run covers which glyph — see i17 follow-up blockers.
    const runs = layer.textRuns;
    const hasMeaningfulRuns = !!runs && runs.length > 0 && runs.some((r) =>
      r.fontSize !== undefined || r.fontFamily !== undefined || r.fontWeight !== undefined ||
      r.fontStyle !== undefined || r.color !== undefined,
    );

    if (layer.textBounds) {
      const bounds = layer.textBounds;
      const boxLeft = layer.x + bounds.x;
      const boxTop = layer.y + bounds.y;
      const boxWidth = bounds.w;
      const boxHeight = bounds.h;

      ctx.save();
      ctx.beginPath();
      ctx.rect(boxLeft, boxTop, boxWidth, boxHeight);
      ctx.clip();

      // Paragraph panel fields (#i100): Left/Right/First-Line Indent narrow
      // the wrap width (and first line's start), Space Before/After add
      // extra vertical gaps at paragraph boundaries (a "paragraph" here is a
      // `\n`-separated block, matching wrapParagraphText's/Photoshop's own
      // notion of a hard-return paragraph — soft-wrapped lines within it get
      // no extra gap). All default to 0 via createTypeLayer, so a layer that
      // never touched the Paragraph panel renders byte-for-byte as before.
      const leftIndent = layer.leftIndent ?? 0;
      const rightIndent = layer.rightIndent ?? 0;
      const firstLineIndent = layer.firstLineIndent ?? 0;
      const spaceBefore = layer.spaceBefore ?? 0;
      const spaceAfter = layer.spaceAfter ?? 0;
      const innerLeft = boxLeft + leftIndent;
      const innerWidth = Math.max(0, boxWidth - leftIndent - rightIndent);

      const wrappedLines = this.wrapParagraphText(ctx, displayText, innerWidth, firstLineIndent);

      // Each line's vertical offset from the top of the wrapped block —
      // Space Before/After makes paragraph-start gaps non-uniform, so this
      // is no longer a plain `i * lineHeight`.
      const lineOffsets: number[] = [];
      let cursor = 0;
      for (let i = 0; i < wrappedLines.length; i++) {
        if (wrappedLines[i]!.firstOfParagraph) {
          cursor += i === 0 ? spaceBefore : spaceBefore + spaceAfter;
        }
        lineOffsets.push(cursor);
        cursor += lineHeight;
      }
      const totalBlockHeight = wrappedLines.length > 0 ? lineOffsets[lineOffsets.length - 1]! + lineHeight : 0;

      let startY = boxTop + verticalShift;
      if (layer.textBaseline === "middle") startY += (boxHeight - totalBlockHeight) / 2;
      else if (layer.textBaseline === "bottom") startY += boxHeight - totalBlockHeight;

      for (let i = 0; i < wrappedLines.length; i++) {
        const { text: lineText, firstOfParagraph } = wrappedLines[i]!;
        const y = startY + lineOffsets[i]!;
        if (y > boxTop + boxHeight) break;

        let lineX = innerLeft + (firstOfParagraph ? firstLineIndent : 0);
        if (layer.textAlign === "center") lineX = innerLeft + innerWidth / 2;
        else if (layer.textAlign === "right") lineX = innerLeft + innerWidth;

        this.fillTextAA(ctx, lineText, lineX, y, this._typeAAMode);
        this.drawTextDecorations(ctx, lineText, lineX, y, fontSize, layer);
      }

      ctx.restore();
      return;
    }

    // Point text
    const lines = displayText.split("\n");
    const startX = layer.x;
    let startY = layer.y + verticalShift;
    if (layer.textBaseline === "middle") startY -= (lines.length * lineHeight) / 2;
    else if (layer.textBaseline === "bottom") startY -= lines.length * lineHeight;

    // Small Caps (#i98): only reachable when there are no meaningful runs
    // (those take priority, same as before) and the layer isn't already
    // All Caps. `displayText`/`lines` were already case-folded to uppercase
    // above (line ~1388) so wrapping/measurement elsewhere stays unaffected;
    // `originalLines` (split from the untouched `layer.text`, which shares
    // the same length/newline positions) lets each line's small-caps runs
    // be told apart from runs that were already uppercase in the source.
    // Point text only — see the i17-follow-up comment above for why
    // paragraph/wrapped text can't reliably map back to per-character source
    // casing once it's been rewrapped.
    const originalLines = !hasMeaningfulRuns && layer.smallCaps && !layer.allCaps
      ? layer.text.split("\n")
      : null;

    let lineGlobalStart = 0;
    for (let i = 0; i < lines.length; i++) {
      const y = startY + i * lineHeight;
      const lineText = lines[i]!;
      const originalLineText = originalLines?.[i];
      if (hasMeaningfulRuns) {
        this.paintTextLineWithRuns(ctx, lineText, lineGlobalStart, layer, startX, y, fontSize);
      } else if (originalLineText !== undefined && /[a-z]/.test(originalLineText)) {
        this.paintTextLineWithSmallCaps(ctx, originalLineText, layer, startX, y, fontSize);
      } else {
        this.fillTextAA(ctx, lineText, startX, y, this._typeAAMode);
        this.drawTextDecorations(ctx, lineText, startX, y, fontSize, layer);
      }
      lineGlobalStart += lineText.length + 1; // +1 for the consumed "\n"
    }
  }

  /**
   * Draw one run of glyphs honoring the type layer's Anti-Alias mode
   * (#i97). Canvas2D's fillText() never consults
   * ctx.imageSmoothingEnabled/Quality — that flag only affects drawImage
   * scaling — so every AA mode used to be pixel-identical. This renders the
   * glyphs into a small supersampled offscreen buffer, applies a
   * mode-dependent alpha post-process (applyAntiAliasCurve), then
   * composites the result back onto `ctx` at the glyph's real position:
   *   - "none"          hard 0/255 alpha cutoff + nearest-neighbor
   *                      downsample -> genuinely jagged/aliased edges.
   *   - "sharp"/"crisp"  increasing alpha-contrast curves around the 0.5
   *                      midpoint (sharp pushes harder than crisp).
   *   - "strong"         a real edge-darkening pass (3x3 alpha dilation) —
   *                      visibly bolder/distinct from "smooth".
   *   - "smooth"         (the default) skips all of this and calls
   *                      fillText() directly, so the common case stays
   *                      byte-for-byte identical to the pre-fix path.
   *
   * Falls back to a direct fillText() whenever the environment can't
   * support the offscreen buffer (no working <canvas> 2D context — e.g.
   * jsdom test doubles) or `ctx.measureText()` doesn't report
   * actualBoundingBox* metrics (needed to size/anchor the buffer); both are
   * safe degradations since a direct fillText() is exactly what ran before
   * this fix.
   */
  private fillTextAA(
    ctx: CanvasRenderingContext2D,
    text: string,
    x: number,
    y: number,
    aaMode: AntiAliasMode,
  ): void {
    if (!text || aaMode === "smooth") {
      ctx.fillText(text, x, y);
      return;
    }

    const metrics: TextMetrics = ctx.measureText(text);
    const left = (metrics as unknown as { actualBoundingBoxLeft?: number }).actualBoundingBoxLeft;
    const right = (metrics as unknown as { actualBoundingBoxRight?: number }).actualBoundingBoxRight;
    const ascent = (metrics as unknown as { actualBoundingBoxAscent?: number }).actualBoundingBoxAscent;
    const descent = (metrics as unknown as { actualBoundingBoxDescent?: number }).actualBoundingBoxDescent;
    if (
      typeof left !== "number" || typeof right !== "number" ||
      typeof ascent !== "number" || typeof descent !== "number" ||
      right - left <= 0 || ascent + descent <= 0
    ) {
      ctx.fillText(text, x, y);
      return;
    }

    const PAD = 2; // local-space padding so edge post-processing has room
    const boxW = Math.ceil(right - left) + PAD * 2;
    const boxH = Math.ceil(ascent + descent) + PAD * 2;
    const SS = 3; // supersample factor

    let off: HTMLCanvasElement;
    try {
      off = document.createElement("canvas");
    } catch {
      ctx.fillText(text, x, y);
      return;
    }
    off.width = Math.max(1, Math.round(boxW * SS));
    off.height = Math.max(1, Math.round(boxH * SS));
    const octx = off.getContext("2d");
    if (!octx) {
      ctx.fillText(text, x, y);
      return;
    }

    octx.setTransform(SS, 0, 0, SS, 0, 0);
    octx.font = ctx.font;
    octx.fillStyle = ctx.fillStyle;
    octx.textAlign = "left";
    octx.textBaseline = "alphabetic";
    octx.imageSmoothingEnabled = true;
    // Re-measure under octx's OWN (fixed) alignment/baseline — the box's
    // overall size (boxW/boxH, from the source ctx's metrics) is alignment-
    // invariant, but the anchor offset within it is not, so placing the
    // fillText call correctly regardless of what the SOURCE ctx's
    // textAlign/textBaseline happened to be requires local metrics.
    const localMetrics = octx.measureText(text);
    const localLeft = (localMetrics as unknown as { actualBoundingBoxLeft?: number }).actualBoundingBoxLeft ?? 0;
    const localAscent = (localMetrics as unknown as { actualBoundingBoxAscent?: number }).actualBoundingBoxAscent ?? ascent;
    octx.fillText(text, PAD + localLeft, PAD + localAscent);

    const img = octx.getImageData(0, 0, off.width, off.height);
    this.applyAntiAliasCurve(img.data, off.width, off.height, aaMode);
    octx.putImageData(img, 0, 0);

    ctx.save();
    // "none" keeps the downsample nearest-neighbor so the binary cutoff
    // above actually reads as hard/jagged pixels rather than being
    // re-smoothed away on the way back down.
    ctx.imageSmoothingEnabled = aaMode !== "none";
    ctx.drawImage(off, 0, 0, off.width, off.height, x - left - PAD, y - ascent - PAD, boxW, boxH);
    ctx.restore();
  }

  /**
   * Mode-dependent alpha post-process applied to the supersampled glyph
   * buffer built by fillTextAA (#i97). Mutates `data` (an RGBA byte buffer)
   * in place.
   */
  private applyAntiAliasCurve(
    data: Uint8ClampedArray,
    w: number,
    h: number,
    aaMode: AntiAliasMode,
  ): void {
    if (aaMode === "none") {
      // Hard binary cutoff — no antialiasing at all.
      for (let i = 3; i < data.length; i += 4) {
        data[i] = data[i]! >= 128 ? 255 : 0;
      }
      return;
    }
    if (aaMode === "sharp" || aaMode === "crisp") {
      // Contrast-boost the alpha channel around the 0.5 midpoint —
      // "sharp" pushes harder toward binary than "crisp".
      const k = aaMode === "sharp" ? 2.2 : 1.5;
      for (let i = 3; i < data.length; i += 4) {
        const a = data[i]! / 255;
        const boosted = 0.5 + (a - 0.5) * k;
        data[i] = Math.max(0, Math.min(255, Math.round(boosted * 255)));
      }
      return;
    }
    if (aaMode === "strong") {
      // Edge-darkening pass: dilate alpha to the max of each pixel's 3x3
      // neighborhood, thickening/boldening glyph edges — a path visibly
      // distinct from "smooth" (which never reaches this method at all;
      // see fillTextAA's early-return).
      const src = new Uint8ClampedArray(data.length);
      src.set(data);
      for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
          const i = (y * w + x) * 4 + 3;
          let maxA = src[i]!;
          for (let dy = -1; dy <= 1; dy++) {
            for (let dx = -1; dx <= 1; dx++) {
              if (dx === 0 && dy === 0) continue;
              const nx = x + dx, ny = y + dy;
              if (nx < 0 || ny < 0 || nx >= w || ny >= h) continue;
              const ni = (ny * w + nx) * 4 + 3;
              if (src[ni]! > maxA) maxA = src[ni]!;
            }
          }
          data[i] = maxA;
        }
      }
    }
  }

  /**
   * Paint one line of point text using per-run font/color overrides
   * (#i17 follow-up), advancing the pen by each run segment's own measured
   * width. `lineGlobalStart` is this line's starting offset into the
   * layer's raw `text` (used to look up which run covers which glyph).
   */
  private paintTextLineWithRuns(
    ctx: CanvasRenderingContext2D,
    lineText: string,
    lineGlobalStart: number,
    layer: TypeLayer,
    anchorX: number,
    y: number,
    baseFontSize: number,
  ): void {
    const segments = this.buildRunSegments(lineText, lineGlobalStart, layer.textRuns!, layer);
    this.paintSegments(ctx, segments, layer, anchorX, y, baseFontSize);
  }

  /**
   * Paint one line of point text as Small Caps (#i98): originally-lowercase
   * runs are uppercased and painted at a reduced font size (~75% of
   * layer.fontSize); originally-uppercase (or non-letter) runs stay full
   * size — unlike All Caps, which renders every character pixel-identical
   * regardless of source casing. Reuses the same segment-painting machinery
   * as per-run text styling (`paintSegments`/`fontCssForRun`).
   */
  private paintTextLineWithSmallCaps(
    ctx: CanvasRenderingContext2D,
    originalLineText: string,
    layer: TypeLayer,
    anchorX: number,
    y: number,
    baseFontSize: number,
  ): void {
    const segments = this.buildSmallCapsSegments(originalLineText, layer);
    this.paintSegments(ctx, segments, layer, anchorX, y, baseFontSize);
  }

  /**
   * Shared segment-painting core for both per-run text styling (#i17
   * follow-up) and Small Caps (#i98): measures each segment with its own
   * font first (so center/right alignment and pen-advancement both use real
   * per-segment widths), then paints each segment left-to-right, honoring
   * the layer's Anti-Alias mode (#i97) via fillTextAA.
   */
  private paintSegments(
    ctx: CanvasRenderingContext2D,
    segments: Array<{
      text: string;
      fontSize: number;
      fontFamily: string;
      fontWeight: number;
      fontStyle: "normal" | "italic";
      color: { r: number; g: number; b: number; a: number };
    }>,
    layer: TypeLayer,
    anchorX: number,
    y: number,
    baseFontSize: number,
  ): void {
    if (segments.length === 0) return;

    const savedFont = ctx.font;
    const savedAlign = ctx.textAlign;
    const savedFill = ctx.fillStyle;

    // Measure every segment with its own font first so alignment (center /
    // right) and per-segment pen advancement both use real per-run widths.
    ctx.textAlign = "left";
    const widths: number[] = [];
    for (const seg of segments) {
      ctx.font = this.fontCssForRun(seg, layer);
      widths.push(ctx.measureText(seg.text).width);
    }
    const totalWidth = widths.reduce((a, b) => a + b, 0);

    let x = anchorX;
    if (layer.textAlign === "center") x -= totalWidth / 2;
    else if (layer.textAlign === "right") x -= totalWidth;

    for (let i = 0; i < segments.length; i++) {
      const seg = segments[i]!;
      ctx.font = this.fontCssForRun(seg, layer);
      const c = seg.color;
      ctx.fillStyle = `rgba(${c.r},${c.g},${c.b},${c.a})`;
      this.fillTextAA(ctx, seg.text, x, y, this._typeAAMode);
      x += widths[i]!;
    }

    ctx.fillStyle = savedFill; // restore the layer's base color for decorations
    this.drawTextDecorationsAt(ctx, anchorX, y, totalWidth, baseFontSize, layer);

    ctx.font = savedFont;
    ctx.textAlign = savedAlign;
    ctx.fillStyle = savedFill;
  }

  /**
   * Split `lineText` into contiguous segments that share the same run (or no
   * run — the layer's baseline attributes). `lineGlobalStart` maps position
   * 0 of `lineText` to its offset in `layer.text`, so runs (which are
   * indexed against the raw layer text) can be located per glyph.
   */
  private buildRunSegments(
    lineText: string,
    lineGlobalStart: number,
    runs: readonly TextRun[],
    layer: TypeLayer,
  ): Array<{
    text: string;
    fontSize: number;
    fontFamily: string;
    fontWeight: number;
    fontStyle: "normal" | "italic";
    color: { r: number; g: number; b: number; a: number };
  }> {
    const n = lineText.length;
    if (n === 0) return [];

    const findRun = (globalIdx: number): TextRun | null => {
      for (const r of runs) {
        if (globalIdx >= r.start && globalIdx < r.end) return r;
      }
      return null;
    };

    const segments: Array<{
      text: string;
      fontSize: number;
      fontFamily: string;
      fontWeight: number;
      fontStyle: "normal" | "italic";
      color: { r: number; g: number; b: number; a: number };
    }> = [];
    let i = 0;
    while (i < n) {
      const run = findRun(lineGlobalStart + i);
      let j = i + 1;
      while (j < n && findRun(lineGlobalStart + j) === run) j++;
      segments.push({
        text: lineText.slice(i, j),
        fontSize: run?.fontSize ?? layer.fontSize,
        fontFamily: run?.fontFamily ?? layer.fontFamily,
        fontWeight: run?.fontWeight ?? layer.fontWeight,
        fontStyle: run?.fontStyle ?? layer.fontStyle,
        color: run?.color ?? layer.color,
      });
      i = j;
    }
    return segments;
  }

  /**
   * Build Small Caps segments (#i98) from `lineText` (the ORIGINAL, un-
   * case-folded source line — see `paintTypeLayerContent`'s `originalLines`).
   * Contiguous runs of originally-lowercase [a-z] characters are uppercased
   * and painted at a reduced font size; every other run (already-uppercase
   * letters, digits, punctuation, whitespace, non-Latin scripts) stays at
   * the layer's full font size and original text. Mirrors `buildRunSegments`
   * so both feed the same `paintSegments` painter.
   */
  private buildSmallCapsSegments(
    lineText: string,
    layer: TypeLayer,
  ): Array<{
    text: string;
    fontSize: number;
    fontFamily: string;
    fontWeight: number;
    fontStyle: "normal" | "italic";
    color: { r: number; g: number; b: number; a: number };
  }> {
    const n = lineText.length;
    if (n === 0) return [];

    const isLower = (ch: string): boolean => ch >= "a" && ch <= "z";

    const segments: Array<{
      text: string;
      fontSize: number;
      fontFamily: string;
      fontWeight: number;
      fontStyle: "normal" | "italic";
      color: { r: number; g: number; b: number; a: number };
    }> = [];
    let i = 0;
    while (i < n) {
      const lower = isLower(lineText[i]!);
      let j = i + 1;
      while (j < n && isLower(lineText[j]!) === lower) j++;
      const raw = lineText.slice(i, j);
      segments.push({
        text: lower ? raw.toUpperCase() : raw,
        // Photoshop's Small Caps draws lowercase-derived caps at roughly
        // 70-80% of the surrounding font size.
        fontSize: lower ? layer.fontSize * 0.75 : layer.fontSize,
        fontFamily: layer.fontFamily,
        fontWeight: layer.fontWeight,
        fontStyle: layer.fontStyle,
        color: layer.color,
      });
      i = j;
    }
    return segments;
  }

  /** Build the CSS font string for a run segment, applying the layer's faux-bold/italic/superscript/subscript the same way the base path does. */
  private fontCssForRun(
    seg: { fontSize: number; fontFamily: string; fontWeight: number; fontStyle: "normal" | "italic" },
    layer: TypeLayer,
  ): string {
    let weight = seg.fontWeight;
    if (layer.fauxBold) weight = Math.min(900, weight + 300);
    let style = seg.fontStyle;
    if (layer.fauxItalic && style === "normal") style = "italic";
    const size = seg.fontSize * (layer.superscript ? 0.6 : layer.subscript ? 0.6 : 1);
    return `${style} ${weight} ${size}px "${seg.fontFamily}"`;
  }

  /**
   * Paint the text content of a *vertical* (CJK) type layer (#i15). Each
   * `\n`-delimited line of source text becomes one column of glyphs that
   * flows top-to-bottom; the FIRST line is the RIGHTMOST column, and each
   * subsequent line steps one column further left (columns advance
   * right-to-left).
   *
   * This mirrors the horizontal point-text layout in `paintTypeLayerContent`
   * with the two axes' *roles* swapped:
   *   - `lineHeight` (fontSize * leading) was the vertical gap between
   *     horizontal lines; here it's the horizontal gap between columns.
   *   - `fontSize` is the per-glyph vertical advance within a column — the
   *     manual analogue of the browser's automatic per-glyph horizontal
   *     advance inside a single horizontal fillText() call (Canvas2D has no
   *     built-in vertical text-flow mode, so glyphs are drawn one at a time).
   *   - `layer.textBaseline` (top/middle/bottom), which anchored the whole
   *     *block* of horizontal lines along y, now anchors every column's
   *     glyph run along y (using the longest column's glyph count, so every
   *     column starts flush — matching Photoshop's paragraph-wide anchor).
   *   - `layer.textAlign` (left/center/right), which let Canvas2D center each
   *     individual horizontal line along x, now positions the whole column
   *     *block* along x (glyphs are always centered within their own column
   *     cell, since per-glyph canvas alignment has no vertical-flow
   *     equivalent).
   */
  private paintVerticalTypeLayerContent(
    ctx: CanvasRenderingContext2D,
    layer: TypeLayer,
    displayText: string,
    fontSize: number,
    verticalShift: number,
    lineHeight: number,
  ): void {
    // Paragraph (bounded) vertical text isn't reflowed to fit the box (that
    // would need full CJK line-breaking rules); we simply anchor the first
    // (rightmost) column to the box's right edge instead of `layer.x`.
    const anchorX = layer.textBounds ? layer.x + layer.textBounds.x + layer.textBounds.w : layer.x;
    const anchorY = layer.textBounds ? layer.y + layer.textBounds.y : layer.y;

    const lines = displayText.split("\n");
    const numColumns = lines.length;
    const columnAdvance = lineHeight;
    const charAdvance = fontSize;
    const charsOf = (s: string): string[] => Array.from(s);
    const maxChars = Math.max(1, ...lines.map((l) => charsOf(l).length));

    let startY = anchorY + verticalShift;
    if (layer.textBaseline === "middle") startY -= (maxChars * charAdvance) / 2;
    else if (layer.textBaseline === "bottom") startY -= maxChars * charAdvance;

    const totalColumnsWidth = (numColumns - 1) * columnAdvance;
    let startX = anchorX;
    if (layer.textAlign === "center") startX += totalColumnsWidth / 2;
    else if (layer.textAlign !== "right") startX += totalColumnsWidth; // "left" / "justify"

    const savedAlign = ctx.textAlign;
    const savedBaseline = ctx.textBaseline;
    ctx.textAlign = "center";
    ctx.textBaseline = "top";

    for (let col = 0; col < numColumns; col++) {
      const x = startX - col * columnAdvance;
      const chars = charsOf(lines[col]!);
      for (let row = 0; row < chars.length; row++) {
        const glyphY = startY + row * charAdvance;
        this.fillTextAA(ctx, chars[row]!, x, glyphY, this._typeAAMode);
      }
      if (chars.length > 0) {
        this.drawVerticalTextDecorations(ctx, x, startY, chars.length, charAdvance, fontSize, layer);
      }
    }

    ctx.textAlign = savedAlign;
    ctx.textBaseline = savedBaseline;
  }

  /**
   * Compute the axis-aligned bounding rect of the text content for a type
   * layer in document-space.  Used as the warp reference frame.
   *
   * For paragraph text (textBounds != null) the bounds are taken directly from
   * the layer.  For point text we measure via a temporary Canvas2D context,
   * falling back to a reasonable approximation if measure returns 0.
   */
  private getTypeLayerTextRect(layer: TypeLayer): { x: number; y: number; w: number; h: number } {
    if (layer.textBounds) {
      return {
        x: layer.x + layer.textBounds.x,
        y: layer.y + layer.textBounds.y,
        w: layer.textBounds.w,
        h: layer.textBounds.h,
      };
    }
    // Point text: measure each line and use the widest + total height.
    const lineHeight = layer.fontSize * layer.leading;
    let displayText = layer.text;
    if (layer.allCaps) displayText = displayText.toUpperCase();
    else if (layer.smallCaps) displayText = displayText.replace(/[a-z]/g, (c) => c.toUpperCase());
    const lines = displayText.split("\n");

    if (layer.vertical) {
      // Vertical (CJK) content bounds (#i15) — mirrors the column layout in
      // paintVerticalTypeLayerContent: each line is a column cell of width
      // `lineHeight`, glyphs advance top-to-bottom in steps of `fontSize`,
      // and the block anchors to layer.x / layer.y the same way (see that
      // method's comment for the full axis-swap rationale). Kept in sync
      // with that method so the warp reference frame matches what's drawn.
      const columnAdvance = lineHeight;
      const charAdvance = layer.fontSize;
      const numColumns = lines.length;
      const maxChars = Math.max(1, ...lines.map((l) => Array.from(l).length));

      let startY = layer.y;
      if (layer.textBaseline === "middle") startY -= (maxChars * charAdvance) / 2;
      else if (layer.textBaseline === "bottom") startY -= maxChars * charAdvance;

      const totalColumnsWidth = (numColumns - 1) * columnAdvance;
      let anchorX = layer.x;
      if (layer.textAlign === "center") anchorX += totalColumnsWidth / 2;
      else if (layer.textAlign !== "right") anchorX += totalColumnsWidth; // "left" / "justify"

      const blockWidth = numColumns * columnAdvance;
      const blockHeight = maxChars * charAdvance;
      const startX = anchorX - totalColumnsWidth - columnAdvance / 2;

      return { x: startX, y: startY, w: blockWidth, h: blockHeight };
    }

    // Try to measure text width using a throwaway canvas.
    let maxWidth = layer.fontSize * 0.6 * Math.max(...lines.map((l) => l.length)); // fallback
    try {
      const tmpC = document.createElement("canvas");
      const tmpCtx = tmpC.getContext("2d");
      if (tmpCtx) {
        const weight = layer.fauxBold ? Math.min(900, layer.fontWeight + 300) : layer.fontWeight;
        const style = layer.fauxItalic && layer.fontStyle === "normal" ? "italic" : layer.fontStyle;
        tmpCtx.font = `${style} ${weight} ${layer.fontSize}px "${layer.fontFamily}"`;
        let measured = 0;
        for (const ln of lines) {
          const m = tmpCtx.measureText(ln).width;
          if (m > measured) measured = m;
        }
        if (measured > 0) maxWidth = measured;
      }
    } catch (_) {
      // Ignore: fallback already set.
    }

    const totalHeight = lines.length * lineHeight;
    let startX = layer.x;
    if (layer.textAlign === "center") startX -= maxWidth / 2;
    else if (layer.textAlign === "right") startX -= maxWidth;

    let startY = layer.y;
    if (layer.textBaseline === "middle") startY -= totalHeight / 2;
    else if (layer.textBaseline === "bottom") startY -= totalHeight;

    return { x: startX, y: startY, w: maxWidth, h: totalHeight };
  }

  /**
   * Mesh-warp `srcCanvas` (full doc-size) onto `dstCtx` using the warp params
   * from `layer`.  The `textRect` defines the region of `srcCanvas` that is
   * treated as the [0,1] × [0,1] normalised warp space.
   *
   * Strategy: subdivide textRect into COLS×ROWS cells.  For each cell we map
   * all four corners through applyWarpToPoint to obtain destination positions,
   * then draw the source cell with a 3-point affine setTransform so straight
   * edges approximating the curve are rendered.  With enough columns (32) this
   * gives visually smooth results for all 15 PS styles.
   */
  private meshWarpCanvas(
    dstCtx: CanvasRenderingContext2D,
    srcCanvas: HTMLCanvasElement,
    layer: TypeLayer,
    textRect: { x: number; y: number; w: number; h: number },
  ): void {
    const { x: rx, y: ry, w: rw, h: rh } = textRect;
    if (rw <= 0 || rh <= 0) return;
    const warp = layer.warp!;

    // Grid resolution: more columns for horizontal curvature, fewer rows.
    const COLS = 32;
    const ROWS = 8;

    const cellW = rw / COLS;
    const cellH = rh / ROWS;

    // Map a normalised [0,1] point through applyWarpToPoint then back to
    // document-space coordinates.
    const warpPt = (u: number, v: number): { x: number; y: number } => {
      const out = applyWarpToPoint(warp, u, v);
      return {
        x: rx + out.x * rw,
        y: ry + out.y * rh,
      };
    };

    dstCtx.save();
    dstCtx.imageSmoothingEnabled = true;
    dstCtx.imageSmoothingQuality = "high";

    for (let row = 0; row < ROWS; row++) {
      for (let col = 0; col < COLS; col++) {
        // Normalised [0,1] corners of this cell.
        const u0 = col / COLS;
        const u1 = (col + 1) / COLS;
        const v0 = row / ROWS;
        const v1 = (row + 1) / ROWS;

        // Source rectangle in srcCanvas pixels.
        const sx = rx + u0 * rw;
        const sy = ry + v0 * rh;
        const sw = cellW;
        const sh = cellH;

        // Warped destination corners (TL, TR, BL, BR).
        const tl = warpPt(u0, v0);
        const tr = warpPt(u1, v0);
        const bl = warpPt(u0, v1);
        // BR is implicitly consistent; we use a parallelogram approximation.

        // Affine transform that maps the unit-cell source rect to the
        // parallelogram defined by tl/tr/bl.  This is the standard 3-point
        // affine derivation:
        //   src (0,0)→tl, (sw,0)→tr, (0,sh)→bl
        //   [a,b,c,d,e,f]:
        //     a = (tr.x - tl.x) / sw
        //     b = (tr.y - tl.y) / sw
        //     c = (bl.x - tl.x) / sh
        //     d = (bl.y - tl.y) / sh
        //     e = tl.x - sx*a - sy*c
        //     f = tl.y - sx*b - sy*d
        const a = (tr.x - tl.x) / sw;
        const b = (tr.y - tl.y) / sw;
        const c = (bl.x - tl.x) / sh;
        const d = (bl.y - tl.y) / sh;
        const e = tl.x - sx * a - sy * c;
        const f = tl.y - sx * b - sy * d;

        dstCtx.setTransform(a, b, c, d, e, f);
        dstCtx.drawImage(srcCanvas, sx, sy, sw, sh, sx, sy, sw, sh);
      }
    }

    dstCtx.restore();
  }

  private drawTypeLayer(ctx: CanvasRenderingContext2D, layer: TypeLayer): void {
    if (layer.text.length === 0) return;

    const doc = this._currentDoc;
    const hasStyles = !!(layer.styles && hasAnyStyle(layer.styles));
    const hasVectorMask = !!(layer as { vectorMask?: unknown }).vectorMask;
    const hasWarp = !!(layer.warp && layer.warp.style !== "none");

    // When layer styles or a vector mask are present, render to an offscreen
    // first so that renderLayerStyles can work with pixel-alpha data and
    // so the vector mask clip stays isolated from the main canvas state.
    // This fixes #192 (styles on type layers) and #194 (vector masks on
    // non-shape layers).
    if (hasStyles || hasVectorMask || hasWarp) {
      const w = doc?.width ?? 4096;
      const h = doc?.height ?? 4096;
      if (w <= 0 || h <= 0) return;

      // Step 1: paint flat text to an offscreen canvas.
      const flatCanvas = document.createElement("canvas");
      flatCanvas.width = w;
      flatCanvas.height = h;
      const flatCtx = flatCanvas.getContext("2d")!;
      this.paintTypeLayerContent(flatCtx, layer);

      // Step 2: apply warp if active.  The warp result replaces flatCanvas so
      // that layer styles are applied to the already-warped pixels.
      let warpedCanvas: HTMLCanvasElement = flatCanvas;
      if (hasWarp) {
        const warpOut = document.createElement("canvas");
        warpOut.width = w;
        warpOut.height = h;
        const warpCtx = warpOut.getContext("2d")!;
        const textRect = this.getTypeLayerTextRect(layer);
        this.meshWarpCanvas(warpCtx, flatCanvas, layer, textRect);
        warpedCanvas = warpOut;
      }

      // Step 3: apply layer styles (to the potentially-warped canvas).
      let resultCanvas: HTMLCanvasElement = warpedCanvas;
      if (hasStyles) {
        resultCanvas = renderLayerStyles(warpedCanvas, layer.styles!, w, h);
      }

      const native = nativeCompositeOp(layer.blendMode);
      ctx.save();
      ctx.globalAlpha = layer.opacity;
      if (native !== null) ctx.globalCompositeOperation = native;
      if (hasVectorMask) {
        // applyVectorMask calls ctx.save() + clip internally — we call
        // ctx.restore() afterwards to pop the clip.
        this.applyVectorMask(ctx, layer as unknown as Layer);
      }
      ctx.imageSmoothingEnabled = false;
      ctx.drawImage(resultCanvas, 0, 0);
      if (hasVectorMask) ctx.restore(); // pop the vector-mask clip
      ctx.restore(); // pop the opacity / blend-mode save
      return;
    }

    // Fast path: no styles, no vector mask, no warp — render directly.
    ctx.save();
    ctx.globalAlpha = layer.opacity;
    const nativeDirect = nativeCompositeOp(layer.blendMode);
    if (nativeDirect !== null) ctx.globalCompositeOperation = nativeDirect;
    this.paintTypeLayerContent(ctx, layer);
    ctx.restore();
  }

  /**
   * Word-wrap paragraph text within `innerWidth` (already reduced by
   * Left/Right Indent — see the `layer.textBounds` branch of
   * `paintTypeLayerContent`), returning each output line's text plus whether
   * it's the first wrapped line of its source paragraph. Photoshop applies
   * First Line Indent and Space Before/After only at paragraph starts, not
   * every wrapped line, so callers need that distinction (#i100). A
   * "paragraph" is a `\n`-separated block of `text`, same as the pre-#i100
   * `wrapText` this replaces.
   */
  private wrapParagraphText(
    ctx: CanvasRenderingContext2D,
    text: string,
    innerWidth: number,
    firstLineIndent: number,
  ): Array<{ text: string; firstOfParagraph: boolean }> {
    const paragraphs = text.split("\n");
    const lines: Array<{ text: string; firstOfParagraph: boolean }> = [];

    for (const paragraph of paragraphs) {
      if (paragraph.length === 0) {
        lines.push({ text: "", firstOfParagraph: true });
        continue;
      }

      const words = paragraph.split(/\s+/);
      let currentLine = "";
      let isFirstLineOfParagraph = true;

      for (const word of words) {
        if (word.length === 0) continue;

        const maxWidth = Math.max(0, innerWidth - (isFirstLineOfParagraph ? firstLineIndent : 0));
        const testLine = currentLine ? currentLine + " " + word : word;
        const metrics = ctx.measureText(testLine);

        if (metrics.width > maxWidth && currentLine) {
          lines.push({ text: currentLine, firstOfParagraph: isFirstLineOfParagraph });
          isFirstLineOfParagraph = false;
          currentLine = word;
        } else {
          currentLine = testLine;
        }
      }

      if (currentLine) {
        lines.push({ text: currentLine, firstOfParagraph: isFirstLineOfParagraph });
      }
    }

    return lines;
  }

  /**
   * Draw underline/strikethrough decorations for a line of text.
   */
  private drawTextDecorations(
    ctx: CanvasRenderingContext2D,
    lineText: string,
    startX: number,
    y: number,
    fontSize: number,
    layer: TypeLayer,
  ): void {
    if (!layer.underline && !layer.strikethrough) return;
    const metrics = ctx.measureText(lineText);
    this.drawTextDecorationsAt(ctx, startX, y, metrics.width, fontSize, layer);
  }

  /**
   * Underline/strikethrough for a line whose rendered width is already
   * known (e.g. summed from per-run segment widths in
   * `paintTextLineWithRuns`, #i17 follow-up) — avoids re-measuring with
   * whatever single font happens to be set on `ctx` at call time.
   */
  private drawTextDecorationsAt(
    ctx: CanvasRenderingContext2D,
    startX: number,
    y: number,
    lineWidth: number,
    fontSize: number,
    layer: TypeLayer,
  ): void {
    if (!layer.underline && !layer.strikethrough) return;

    let lineStartX = startX;
    if (layer.textAlign === "center") lineStartX -= lineWidth / 2;
    else if (layer.textAlign === "right") lineStartX -= lineWidth;

    ctx.strokeStyle = ctx.fillStyle;
    ctx.lineWidth = Math.max(1, fontSize * 0.08);

    if (layer.underline) {
      const underlineY = y + fontSize * 0.15;
      ctx.beginPath();
      ctx.moveTo(lineStartX, underlineY);
      ctx.lineTo(lineStartX + lineWidth, underlineY);
      ctx.stroke();
    }

    if (layer.strikethrough) {
      const strikeY = y - fontSize * 0.3;
      ctx.beginPath();
      ctx.moveTo(lineStartX, strikeY);
      ctx.lineTo(lineStartX + lineWidth, strikeY);
      ctx.stroke();
    }
  }

  /**
   * Underline/strikethrough for one column of vertical (CJK) text (#i15) —
   * drawn as vertical rules alongside the glyph run instead of horizontal
   * rules under/through a line, since the column itself already reads
   * top-to-bottom.
   */
  private drawVerticalTextDecorations(
    ctx: CanvasRenderingContext2D,
    x: number,
    startY: number,
    charCount: number,
    charAdvance: number,
    fontSize: number,
    layer: TypeLayer,
  ): void {
    if (!layer.underline && !layer.strikethrough) return;

    const topY = startY;
    const bottomY = startY + charCount * charAdvance;

    ctx.strokeStyle = ctx.fillStyle;
    ctx.lineWidth = Math.max(1, fontSize * 0.08);

    if (layer.strikethrough) {
      // Through the middle of the column's glyphs.
      ctx.beginPath();
      ctx.moveTo(x, topY);
      ctx.lineTo(x, bottomY);
      ctx.stroke();
    }

    if (layer.underline) {
      // Beside the column (the vertical-text analogue of "below the line").
      const underlineX = x + fontSize * 0.35;
      ctx.beginPath();
      ctx.moveTo(underlineX, topY);
      ctx.lineTo(underlineX, bottomY);
      ctx.stroke();
    }
  }

  private drawLayer(ctx: CanvasRenderingContext2D, layer: Layer): void {
    // Early exit for invisible or fully transparent layers
    if (!layer.visible || layer.opacity <= 0) return;

    if (layer.kind === "type") {
      const t = layer as TypeLayer;
      if (t.text.length === 0) return; // Skip empty type layers
      this.drawTypeLayer(ctx, layer);
      return;
    }
    if (layer.kind === "group") {
      this.drawGroupLayer(ctx, layer);
      return;
    }
    if (layer.kind === "adjustment") {
      this.drawAdjustmentLayer(ctx, layer);
      return;
    }
    if (layer.kind === "smart-object") {
      this.drawSmartObjectLayer(ctx, layer);
      return;
    }
    if (layer.kind === "shape") {
      this.drawShapeLayer(ctx, layer);
      return;
    }
    if (layer.kind === "solid-fill") {
      this.drawSolidFillLayer(ctx, layer);
      return;
    }
    if (layer.kind === "gradient-fill") {
      this.drawGradientFillLayer(ctx, layer);
      return;
    }
    if (layer.kind === "pattern-fill") {
      this.drawPatternFillLayer(ctx, layer);
      return;
    }
    if (layer.kind === "background") {
      this.drawBackgroundLayer(ctx, layer);
      return;
    }
    if (layer.kind === "artboard") {
      this.drawArtboardLayer(ctx, layer);
      return;
    }
    if (layer.kind === "frame") {
      this.drawFrameLayer(ctx, layer);
      return;
    }
    if (layer.kind === "linked-smart-object") {
      this.drawLinkedSmartObjectLayer(ctx, layer);
      return;
    }
    // Stub rendering for Video Frame and 3D layers — full pipelines deferred to M6/M7.
    // Draw composite if available; otherwise skip silently.
    if (layer.kind === "video-frame" || layer.kind === "3d") {
      if (layer.composite) {
        const buf = layer.composite;
        const off = document.createElement("canvas");
        off.width = buf.width;
        off.height = buf.height;
        const offCtx = off.getContext("2d");
        if (offCtx) {
          const imageData = offCtx.createImageData(buf.width, buf.height);
          imageData.data.set(buf.data);
          offCtx.putImageData(imageData, 0, 0);
          ctx.save();
          ctx.globalAlpha = layer.opacity;
          ctx.imageSmoothingEnabled = false;
          ctx.drawImage(off, layer.x, layer.y);
          ctx.restore();
        }
      }
      return;
    }
    const rawBitmap = this.getLayerBitmap(layer as unknown as import("../document/Layer").RasterLayer);
    if (!rawBitmap) return;

    // If a raster mask is attached and enabled, pre-bake the layer pixels
    // multiplied by mask alpha into a temporary canvas, and composite *that*
    // instead. Everything below — both the native and offscreen paths — runs
    // unchanged against this masked bitmap. Layers without a mask take the
    // exact same code path as before (the `mask` branch is the only
    // divergence, and it copies the same canvas reference forward).
    let bitmap =
      layer.mask && layer.mask.enabled
        ? this.getMaskedBitmap(layer, rawBitmap)
        : rawBitmap;
    if (!bitmap) return;

    // Bake fillOpacity into pixel alpha. In Photoshop, fillOpacity scales the
    // layer pixel content but NOT layer styles (drop shadow, stroke, etc.).
    // This must happen after mask compositing and before blend compositing.
    if (layer.fillOpacity != null && layer.fillOpacity < 1) {
      bitmap = this.getFillOpacityBitmap(layer, bitmap);
    }
    if (!bitmap) return;

    // When layer styles are present, build the styled canvas first (which
    // includes the layer pixels + all effects), then composite the whole
    // thing with the correct blend mode in a single pass.  This fixes two
    // bugs (#193):
    //   1. The layer content was drawn twice (once as `bitmap`, once inside
    //      renderLayerStyles which always draws sourceCanvas at step 3).
    //   2. The styled drawImage defaulted to source-over instead of the
    //      layer's blend mode.
    // `bitmap` (with fill-opacity / mask baked in) is forwarded as the
    // `fillSource` override so effects still use the full-alpha `rawBitmap`
    // for mask extraction (Photoshop behaviour).
    if (layer.styles && hasAnyStyle(layer.styles)) {
      const styled = renderLayerStyles(
        rawBitmap,
        layer.styles,
        layer.pixels.width,
        layer.pixels.height,
        null,
        bitmap, // fillSource — fill-opacity / mask baked in for pixel content
      );
      const styledNative = nativeCompositeOp(layer.blendMode);
      // #i147: a natively-supported blend mode still needs Blend If's
      // per-pixel channel-range filtering, which a plain
      // globalCompositeOperation can't express — force the manual
      // pixel-composite branch (below) whenever the layer has an active
      // Blend If config, regardless of blend-mode native-compatibility.
      if (styledNative !== null && !layer.blendIf) {
        ctx.save();
        ctx.globalAlpha = layer.opacity;
        ctx.globalCompositeOperation = styledNative;
        ctx.imageSmoothingEnabled = false;
        ctx.drawImage(styled, layer.x, layer.y);
        ctx.restore();
      } else {
        // Non-native blend mode (or a native one forced here by an active
        // Blend If): pixel-blend the styled canvas manually.
        const sw = styled.width;
        const sh = styled.height;
        if (sw > 0 && sh > 0) {
          const dstData = ctx.getImageData(layer.x, layer.y, sw, sh);
          const sctx = styled.getContext("2d");
          if (sctx) {
            const srcData = sctx.getImageData(0, 0, sw, sh);
            if (layer.blendIf) {
              this.applyBlendIf(srcData.data, dstData.data, layer.blendIf, sw, sh);
            }
            compositeLayer(dstData.data, srcData.data, layer.blendMode, layer.opacity);
            ctx.save();
            ctx.globalCompositeOperation = "source-over";
            ctx.putImageData(dstData, layer.x, layer.y);
            ctx.restore();
          }
        }
      }
      return;
    }

    const native = nativeCompositeOp(layer.blendMode);
    // #i147: same reasoning as the styled branch above — an active Blend If
    // forces the offscreen path (compositeOffscreen already applies it)
    // even when the blend mode itself is natively supported.
    if (native !== null && !layer.blendIf) {
      // Native path: a single globalCompositeOperation handles everything.
      ctx.save();
      ctx.globalAlpha = layer.opacity;
      ctx.globalCompositeOperation = native;
      ctx.imageSmoothingEnabled = false;
      ctx.drawImage(bitmap, layer.x, layer.y);
      ctx.restore();
    } else {
      // Offscreen pixel composite for modes Canvas2D can't do itself (or a
      // native mode forced here by an active Blend If).
      // Strategy:
      //   1. Read back the rect of the destination beneath the layer.
      //   2. Apply Blend If (if any), then blend src into that rect with
      //      compositeLayer().
      //   3. Write it back with source-over (the per-pixel alpha is already
      //      baked in).
      this.compositeOffscreen(ctx, layer, bitmap);
    }
  }

  /**
   * Render a list of layers with clipping mask support. Walks bottom-first,
   * grouping consecutive clipped layers with their base layer.
   */
  private renderLayersWithClipping(ctx: CanvasRenderingContext2D, layers: readonly Layer[]): void {
    let i = 0;
    while (i < layers.length) {
      const base = layers[i]!;
      // Collect all consecutive clipped layers above this base
      const clipped: Layer[] = [];
      let j = i + 1;
      while (j < layers.length && layers[j]!.clippedToBelow) {
        clipped.push(layers[j]!);
        j++;
      }

      if (clipped.length === 0) {
        // No clipped layers — render normally
        this.drawLayer(ctx, base);
      } else {
        // Render the base layer
        this.drawLayer(ctx, base);

        // Render each clipped layer masked to the base
        for (const clipLayer of clipped) {
          this.drawClippedLayer(ctx, clipLayer, base);
        }
      }

      i = j;
    }
  }

  /**
   * Render a clipped layer: its content is visible only where the base layer
   * has non-transparent pixels. Uses an offscreen canvas + destination-in
   * compositing to mask the clipped layer against the base layer's alpha.
   */
  private drawClippedLayer(ctx: CanvasRenderingContext2D, layer: Layer, base: Layer): void {
    if (!layer.visible || layer.opacity <= 0) return;

    // Compute the combined bounding box of base + clipped layer
    const baseBbox = this.layerBBox(base);
    const clipBbox = this.layerBBox(layer);
    if (!baseBbox || !clipBbox) return;

    const minX = Math.min(baseBbox.x, clipBbox.x);
    const minY = Math.min(baseBbox.y, clipBbox.y);
    const maxX = Math.max(baseBbox.x + baseBbox.w, clipBbox.x + clipBbox.w);
    const maxY = Math.max(baseBbox.y + baseBbox.h, clipBbox.y + clipBbox.h);
    const w = maxX - minX;
    const h = maxY - minY;
    if (w <= 0 || h <= 0) return;

    // Use the shared temp canvas
    let temp = this.offscreenTemp;
    if (!temp) { temp = document.createElement("canvas"); this.offscreenTemp = temp; }
    temp.width = w;
    temp.height = h;
    const tctx = temp.getContext("2d");
    if (!tctx) return;

    tctx.setTransform(1, 0, 0, 1, 0, 0);
    tctx.clearRect(0, 0, w, h);

    // 1. Render the clipped layer into temp, offset so (minX, minY) → (0, 0)
    tctx.save();
    tctx.translate(-minX, -minY);
    this.drawLayerInternalForClipping(tctx, layer);
    tctx.restore();

    // 2. Mask with the base layer's alpha using destination-in
    const baseAlpha = this.getLayerAlphaBitmap(base);
    if (baseAlpha) {
      tctx.save();
      tctx.globalCompositeOperation = "destination-in";
      tctx.imageSmoothingEnabled = false;
      tctx.drawImage(baseAlpha, baseBbox.x - minX, baseBbox.y - minY);
      tctx.restore();
    }

    // 3. Composite the masked result onto the main canvas
    ctx.save();
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(temp, minX, minY);
    ctx.restore();
  }

  /**
   * Draw a layer into a provided context without blend-mode dispatch — used
   * for rendering clipped layers into offscreen buffers. Handles raster, type,
   * group, and adjustment layers.
   */
  private drawLayerInternalForClipping(ctx: CanvasRenderingContext2D, layer: Layer): void {
    if (!layer.visible || layer.opacity <= 0) return;

    if (layer.kind === "type") {
      // Simplified type draw for clipping — use globalAlpha
      ctx.save();
      ctx.globalAlpha = layer.opacity;
      const t = layer as TypeLayer;
      if (t.text.length > 0) {
        const color = t.color;
        ctx.fillStyle = `rgba(${color.r},${color.g},${color.b},${color.a})`;
        ctx.font = `${t.fontStyle} ${t.fontWeight} ${t.fontSize}px "${t.fontFamily}"`;
        ctx.textAlign = t.textAlign === "justify" ? "left" : t.textAlign;
        ctx.textBaseline = t.textBaseline;
        const lines = t.text.split("\n");
        const lineHeight = t.fontSize * t.leading;
        let startY = t.y;
        if (t.textBaseline === "middle") startY -= (lines.length * lineHeight) / 2;
        else if (t.textBaseline === "bottom") startY -= lines.length * lineHeight;
        for (let i = 0; i < lines.length; i++) {
          ctx.fillText(lines[i]!, t.x, startY + i * lineHeight);
        }
      }
      ctx.restore();
      return;
    }

    if (layer.kind === "group") {
      ctx.save();
      ctx.globalAlpha *= layer.opacity;
      for (const child of (layer as GroupLayer).children) {
        this.drawLayerInternalForClipping(ctx, child);
      }
      ctx.restore();
      return;
    }

    if (layer.kind === "adjustment") {
      // Adjustment layers in clipping: apply the adjustment to the current
      // offscreen buffer content (layers below within the clip group).
      this.drawAdjustmentLayer(ctx, layer as AdjustmentLayer);
      return;
    }

    if (layer.kind === "smart-object") {
      const so = layer as SmartObjectLayer;
      const bitmap = this.getSmartObjectBitmap(so);
      if (!bitmap) return;
      ctx.save();
      ctx.globalAlpha = layer.opacity;
      ctx.imageSmoothingEnabled = false;
      ctx.drawImage(bitmap, so.x, so.y);
      ctx.restore();
      return;
    }

    if (layer.kind === "solid-fill") {
      const f = layer as SolidFillLayer;
      const doc = this._currentDoc;
      if (!doc) return;
      ctx.save();
      ctx.globalAlpha = layer.opacity;
      const { r, g, b, a } = f.color;
      ctx.fillStyle = `rgba(${r},${g},${b},${a})`;
      ctx.fillRect(0, 0, doc.width, doc.height);
      ctx.restore();
      return;
    }

    if (layer.kind === "gradient-fill") {
      const doc = this._currentDoc;
      if (!doc) return;
      ctx.save();
      ctx.globalAlpha = layer.opacity;
      const gradient = this.createGradient(ctx, layer as GradientFillLayer, doc.width, doc.height);
      if (gradient) {
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, doc.width, doc.height);
      }
      ctx.restore();
      return;
    }

    if (layer.kind === "pattern-fill") {
      const f = layer as PatternFillLayer;
      const doc = this._currentDoc;
      if (!doc) return;
      ctx.save();
      ctx.globalAlpha = layer.opacity;
      if (f.pattern && f.pattern.width > 0 && f.pattern.height > 0) {
        this.drawPattern(ctx, f);
      }
      ctx.restore();
      return;
    }

    if (layer.kind === "background") {
      const bg = layer as BackgroundLayer;
      const bitmap = this.getLayerBitmap(bg as unknown as RasterLayer);
      if (!bitmap) return;
      ctx.save();
      ctx.globalAlpha = layer.opacity;
      ctx.imageSmoothingEnabled = false;
      ctx.drawImage(bitmap, bg.x, bg.y);
      ctx.restore();
      return;
    }

    const raster = layer as RasterLayer;
    const rawBitmap = this.getLayerBitmap(raster);
    if (!rawBitmap) return;

    let bitmap =
      raster.mask && raster.mask.enabled
        ? this.getMaskedBitmap(raster, rawBitmap)
        : rawBitmap;
    if (!bitmap) return;

    if (raster.fillOpacity != null && raster.fillOpacity < 1) {
      bitmap = this.getFillOpacityBitmap(raster, bitmap);
    }
    if (!bitmap) return;

    ctx.save();
    ctx.globalAlpha = layer.opacity;
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(bitmap, raster.x, raster.y);
    ctx.restore();
  }

  /**
   * Compute the bounding box of a layer in document space. Returns null for
   * empty/invisible layers.
   */
  private layerBBox(layer: Layer): { x: number; y: number; w: number; h: number } | null {
    if (!layer.visible || layer.opacity <= 0) return null;

    if (layer.kind === "group") {
      return this.groupBoundingBox(layer as GroupLayer);
    }

    if (layer.kind === "adjustment") {
      // Adjustment layers cover the full document
      const doc = this._currentDoc;
      if (!doc) return null;
      return { x: 0, y: 0, w: doc.width, h: doc.height };
    }

    if (layer.kind === "type") {
      const t = layer as TypeLayer;
      if (t.text.length === 0) return null;

      // Paragraph text: use textBounds directly
      if (t.textBounds) {
        return {
          x: t.x + t.textBounds.x,
          y: t.y + t.textBounds.y,
          w: t.textBounds.w,
          h: t.textBounds.h,
        };
      }

      // Use actual text metrics for accurate bounds
      const off = document.createElement("canvas");
      off.width = 1; off.height = 1;
      const ctx = off.getContext("2d");
      if (!ctx) return { x: t.x, y: t.y, w: 100, h: 50 };

      let weight = t.fontWeight;
      if (t.fauxBold) weight = Math.min(900, weight + 300);
      let style = t.fontStyle;
      if (t.fauxItalic && style === "normal") style = "italic";
      const fontSize = t.fontSize * (t.superscript ? 0.6 : t.subscript ? 0.6 : 1);
      ctx.font = `${style} ${weight} ${fontSize}px "${t.fontFamily}"`;

      let displayText = t.text;
      if (t.allCaps) displayText = displayText.toUpperCase();
      const lines = displayText.split("\n");
      const lineHeight = t.fontSize * t.leading;
      let maxWidth = 0;
      for (const line of lines) {
        const m = ctx.measureText(line);
        const w = m.width + Math.abs(t.tracking) * 0.01 * fontSize * line.length;
        if (w > maxWidth) maxWidth = w;
      }
      const totalHeight = lines.length * lineHeight;

      let x = t.x;
      if (t.textAlign === "center") x -= maxWidth / 2;
      else if (t.textAlign === "right") x -= maxWidth;

      let y = t.y;
      if (t.textBaseline === "middle") y -= totalHeight / 2;
      else if (t.textBaseline === "bottom") y -= totalHeight;

      return { x, y, w: maxWidth, h: totalHeight };
    }

    if (layer.kind === "smart-object") {
      const so = layer as SmartObjectLayer;
      const { scaleX, scaleY } = so.transform;
      const pw = so.embedded.width * scaleX;
      const ph = so.embedded.height * scaleY;
      if (pw <= 0 || ph <= 0) return null;
      return { x: so.x, y: so.y, w: pw, h: ph };
    }

    if (layer.kind === "shape") {
      const shape = layer as import("../document/Layer").ShapeLayer;
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      for (const subpath of shape.path.subpaths) {
        for (const pt of subpath.points) {
          if (pt.x < minX) minX = pt.x;
          if (pt.y < minY) minY = pt.y;
          if (pt.x > maxX) maxX = pt.x;
          if (pt.y > maxY) maxY = pt.y;
          if (pt.leftDirection) {
            if (pt.leftDirection.x < minX) minX = pt.leftDirection.x;
            if (pt.leftDirection.y < minY) minY = pt.leftDirection.y;
            if (pt.leftDirection.x > maxX) maxX = pt.leftDirection.x;
            if (pt.leftDirection.y > maxY) maxY = pt.leftDirection.y;
          }
          if (pt.rightDirection) {
            if (pt.rightDirection.x < minX) minX = pt.rightDirection.x;
            if (pt.rightDirection.y < minY) minY = pt.rightDirection.y;
            if (pt.rightDirection.x > maxX) maxX = pt.rightDirection.x;
            if (pt.rightDirection.y > maxY) maxY = pt.rightDirection.y;
          }
        }
      }
      if (!isFinite(minX)) return null;
      const strokePad = shape.strokeWidth / 2;
      return {
        x: minX - strokePad + shape.x,
        y: minY - strokePad + shape.y,
        w: (maxX - minX) + strokePad * 2,
        h: (maxY - minY) + strokePad * 2,
      };
    }

    if (layer.kind === "solid-fill" || layer.kind === "gradient-fill" || layer.kind === "pattern-fill") {
      const doc = this._currentDoc;
      if (!doc) return null;
      return { x: 0, y: 0, w: doc.width, h: doc.height };
    }

    if (layer.kind === "background") {
      const bg = layer as BackgroundLayer;
      const pw = bg.pixels?.width ?? 0;
      const ph = bg.pixels?.height ?? 0;
      if (pw <= 0 || ph <= 0) return null;
      return { x: bg.x, y: bg.y, w: pw, h: ph };
    }

    if (layer.kind === "artboard") {
      const ab = layer as ArtboardLayer;
      return { x: ab.artboardX, y: ab.artboardY, w: ab.artboardWidth, h: ab.artboardHeight };
    }

    const raster = layer as RasterLayer;
    const pw = raster.pixels?.width ?? 0;
    const ph = raster.pixels?.height ?? 0;
    if (pw <= 0 || ph <= 0) return null;
    return { x: raster.x, y: raster.y, w: pw, h: ph };
  }

  /**
   * Produce a canvas containing only the alpha channel of a layer (white =
   * opaque, black = transparent). Used as the clipping mask for clipped layers.
   */
  private getLayerAlphaBitmap(layer: Layer): HTMLCanvasElement | null {
    if (!layer.visible || layer.opacity <= 0) return null;

    if (layer.kind === "group") {
      // For groups, composite all children's alpha into a single canvas
      const bbox = this.groupBoundingBox(layer as GroupLayer);
      if (!bbox) return null;
      const w = bbox.w;
      const h = bbox.h;
      if (w <= 0 || h <= 0) return null;

      const off = document.createElement("canvas");
      off.width = w;
      off.height = h;
      const octx = off.getContext("2d");
      if (!octx) return null;
      octx.setTransform(1, 0, 0, 1, 0, 0);
      octx.clearRect(0, 0, w, h);
      octx.save();
      octx.translate(-bbox.x, -bbox.y);
      for (const child of (layer as GroupLayer).children) {
        this.drawLayerAlphaInternal(octx, child);
      }
      octx.restore();
      return off;
    }

    if (layer.kind === "adjustment") {
      // Adjustment layers are fully opaque over the document
      const doc = this._currentDoc;
      if (!doc) return null;
      const off = document.createElement("canvas");
      off.width = doc.width;
      off.height = doc.height;
      const octx = off.getContext("2d");
      if (!octx) return null;
      octx.fillStyle = "rgba(255,255,255,1)";
      octx.fillRect(0, 0, doc.width, doc.height);
      return off;
    }

    if (layer.kind === "type") {
      // Render type layer to get its alpha
      const t = layer as TypeLayer;
      if (t.text.length === 0) return null;
      const approxW = Math.ceil(t.text.length * t.fontSize * 0.6) + 10;
      const lines = t.text.split("\n").length;
      const approxH = Math.ceil(lines * t.fontSize * t.leading) + 10;
      const off = document.createElement("canvas");
      off.width = approxW;
      off.height = approxH;
      const octx = off.getContext("2d");
      if (!octx) return null;
      octx.setTransform(1, 0, 0, 1, 0, 0);
      octx.clearRect(0, 0, approxW, approxH);
      octx.globalAlpha = t.opacity;
      const color = t.color;
      octx.fillStyle = `rgba(${color.r},${color.g},${color.b},${color.a})`;
      octx.font = `${t.fontStyle} ${t.fontWeight} ${t.fontSize}px "${t.fontFamily}"`;
      octx.textAlign = t.textAlign === "justify" ? "left" : t.textAlign;
      octx.textBaseline = t.textBaseline;
      const textLines = t.text.split("\n");
      const lineHeight = t.fontSize * t.leading;
      let startY = t.y;
      if (t.textBaseline === "middle") startY -= (textLines.length * lineHeight) / 2;
      else if (t.textBaseline === "bottom") startY -= textLines.length * lineHeight;
      for (let i = 0; i < textLines.length; i++) {
        octx.fillText(textLines[i]!, t.x, startY + i * lineHeight);
      }
      return off;
    }

    if (layer.kind === "shape") {
      const shape = layer as import("../document/Layer").ShapeLayer;
      const bbox = this.layerBBox(layer);
      if (!bbox) return null;
      const w = Math.ceil(bbox.w);
      const h = Math.ceil(bbox.h);
      if (w <= 0 || h <= 0) return null;
      const off = document.createElement("canvas");
      off.width = w;
      off.height = h;
      const octx = off.getContext("2d");
      if (!octx) return null;
      octx.setTransform(1, 0, 0, 1, 0, 0);
      octx.clearRect(0, 0, w, h);
      octx.save();
      octx.translate(-bbox.x, -bbox.y);
      this.drawShapeLayer(octx, shape);
      octx.restore();
      return off;
    }

    if (layer.kind === "solid-fill" || layer.kind === "gradient-fill" || layer.kind === "pattern-fill") {
      const doc = this._currentDoc;
      if (!doc) return null;
      const off = document.createElement("canvas");
      off.width = doc.width;
      off.height = doc.height;
      const octx = off.getContext("2d");
      if (!octx) return null;
      octx.fillStyle = "rgba(255,255,255,1)";
      octx.fillRect(0, 0, doc.width, doc.height);
      return off;
    }

    if (layer.kind === "background") {
      const bg = layer as BackgroundLayer;
      const buf = bg.pixels;
      const w = buf.width;
      const h = buf.height;
      if (w <= 0 || h <= 0) return null;
      const off = document.createElement("canvas");
      off.width = w;
      off.height = h;
      const octx = off.getContext("2d");
      if (!octx) return null;
      const imageData = octx.createImageData(w, h);
      imageData.data.set(buf.data);
      octx.putImageData(imageData, 0, 0);
      return off;
    }

    // Raster layer
    const raster = layer as RasterLayer;
    const buf = raster.pixels;
    const w = buf.width;
    const h = buf.height;
    if (w <= 0 || h <= 0) return null;

    const cached = this.alphaCache.get(raster.id);
    if (cached && cached.version === buf.version) return cached.canvas;

    const off = document.createElement("canvas");
    off.width = w;
    off.height = h;
    const octx = off.getContext("2d");
    if (!octx) return null;

    // Draw the layer bitmap (with mask if present)
    const rawBitmap = this.getLayerBitmap(raster);
    if (!rawBitmap) return null;
    let bitmap =
      raster.mask && raster.mask.enabled
        ? this.getMaskedBitmap(raster, rawBitmap)
        : rawBitmap;
    if (!bitmap) return null;

    octx.setTransform(1, 0, 0, 1, 0, 0);
    octx.clearRect(0, 0, w, h);
    octx.drawImage(bitmap, 0, 0);

    this.alphaCache.set(raster.id, { canvas: off, version: buf.version });
    return off;
  }

  /**
   * Draw a layer's alpha contribution into a context. Used for building
   * group alpha masks.
   */
  private drawLayerAlphaInternal(ctx: CanvasRenderingContext2D, layer: Layer): void {
    if (!layer.visible || layer.opacity <= 0) return;

    if (layer.kind === "group") {
      ctx.save();
      ctx.globalAlpha *= layer.opacity;
      for (const child of (layer as GroupLayer).children) {
        this.drawLayerAlphaInternal(ctx, child);
      }
      ctx.restore();
      return;
    }

    if (layer.kind === "adjustment") return;
    if (layer.kind === "type") {
      // Simplified: draw type text as opaque white
      ctx.save();
      ctx.globalAlpha = layer.opacity;
      const t = layer as TypeLayer;
      if (t.text.length > 0) {
        ctx.fillStyle = "rgba(255,255,255,1)";
        ctx.font = `${t.fontStyle} ${t.fontWeight} ${t.fontSize}px "${t.fontFamily}"`;
        ctx.textAlign = t.textAlign === "justify" ? "left" : t.textAlign;
        ctx.textBaseline = t.textBaseline;
        const lines = t.text.split("\n");
        const lineHeight = t.fontSize * t.leading;
        let startY = t.y;
        if (t.textBaseline === "middle") startY -= (lines.length * lineHeight) / 2;
        else if (t.textBaseline === "bottom") startY -= lines.length * lineHeight;
        for (let i = 0; i < lines.length; i++) {
          ctx.fillText(lines[i]!, t.x, startY + i * lineHeight);
        }
      }
      ctx.restore();
      return;
    }

    if (layer.kind === "smart-object") {
      const so = layer as SmartObjectLayer;
      const bitmap = this.getSmartObjectBitmap(so);
      if (!bitmap) return;
      ctx.save();
      ctx.globalAlpha = layer.opacity;
      ctx.imageSmoothingEnabled = false;
      ctx.drawImage(bitmap, so.x, so.y);
      ctx.restore();
      return;
    }

    if (layer.kind === "shape") {
      const shape = layer as import("../document/Layer").ShapeLayer;
      const bbox = this.layerBBox(layer);
      if (!bbox) return;
      ctx.save();
      ctx.globalAlpha = layer.opacity;
      ctx.translate(-bbox.x, -bbox.y);
      this.drawShapeLayer(ctx, shape);
      ctx.restore();
      return;
    }

    if (layer.kind === "solid-fill") {
      const doc = this._currentDoc;
      if (!doc) return;
      ctx.save();
      ctx.globalAlpha = layer.opacity;
      ctx.fillStyle = "rgba(255,255,255,1)";
      ctx.fillRect(0, 0, doc.width, doc.height);
      ctx.restore();
      return;
    }

    if (layer.kind === "gradient-fill" || layer.kind === "pattern-fill") {
      const doc = this._currentDoc;
      if (!doc) return;
      ctx.save();
      ctx.globalAlpha = layer.opacity;
      ctx.fillStyle = "rgba(255,255,255,1)";
      ctx.fillRect(0, 0, doc.width, doc.height);
      ctx.restore();
      return;
    }

    if (layer.kind === "background") {
      const bg = layer as BackgroundLayer;
      const bitmap = this.getLayerBitmap(bg as unknown as RasterLayer);
      if (!bitmap) return;
      ctx.save();
      ctx.globalAlpha = layer.opacity;
      ctx.imageSmoothingEnabled = false;
      ctx.drawImage(bitmap, bg.x, bg.y);
      ctx.restore();
      return;
    }

    if (layer.kind === "artboard") {
      const ab = layer as ArtboardLayer;
      ctx.save();
      ctx.globalAlpha = layer.opacity;
      for (const child of ab.children) {
        this.drawLayerAlphaInternal(ctx, child);
      }
      ctx.restore();
      return;
    }

    const raster = layer as RasterLayer;
    const rawBitmap = this.getLayerBitmap(raster);
    if (!rawBitmap) return;
    ctx.save();
    ctx.globalAlpha = layer.opacity;
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(rawBitmap, raster.x, raster.y);
    ctx.restore();
  }

  // Cache for alpha bitmaps keyed by layer id
  private alphaCache = new Map<string, { canvas: HTMLCanvasElement; version: number }>();

  /**
   * Render a group layer with proper blend-mode isolation. In Photoshop, a
   * group composites its children into an isolated buffer first, then blends
   * that buffer with the destination using the group's own blendMode and
   * opacity. This matches the behaviour of non-"pass-through" group modes.
   *
   * For "normal" blend mode we fall back to the simpler pass-through render
   * (children blend directly against the parent context) because Photoshop's
   * true "pass through" isn't modelled in our BlendMode union yet.
   */
  private drawGroupLayer(ctx: CanvasRenderingContext2D, group: GroupLayer): void {
    // "normal" and "pass" → pass-through: children blend directly against parent context.
    if (group.blendMode === "normal" || group.blendMode === "pass") {
      ctx.save();
      ctx.globalAlpha *= group.opacity;
      for (const child of group.children) this.drawLayer(ctx, child);
      ctx.restore();
      return;
    }

    // Non-normal blend mode: isolate children into an offscreen buffer.
    const bbox = this.groupBoundingBox(group);
    if (!bbox) return;

    const w = bbox.w;
    const h = bbox.h;
    if (w <= 0 || h <= 0) return;

    let iso = this.groupIsoBuffer;
    if (!iso) {
      iso = document.createElement("canvas");
      this.groupIsoBuffer = iso;
    }
    iso.width = w;
    iso.height = h;
    const isoCtx = iso.getContext("2d");
    if (!isoCtx) return;

    isoCtx.setTransform(1, 0, 0, 1, 0, 0);
    isoCtx.clearRect(0, 0, w, h);

    // Render children into the isolated buffer, offset so (bbox.x, bbox.y)
    // maps to (0, 0) in the buffer.
    isoCtx.save();
    isoCtx.translate(-bbox.x, -bbox.y);
    for (const child of group.children) this.drawLayer(isoCtx, child);
    isoCtx.restore();

    // Composite the isolated buffer onto the main canvas.
    const native = nativeCompositeOp(group.blendMode);
    if (native !== null) {
      ctx.save();
      ctx.globalAlpha = group.opacity;
      ctx.globalCompositeOperation = native;
      ctx.imageSmoothingEnabled = false;
      ctx.drawImage(iso, bbox.x, bbox.y);
      ctx.restore();
    } else {
      this.compositeGroupOffscreen(ctx, group, iso, bbox);
    }
  }

  /**
   * Render an adjustment layer. Reads the current canvas pixels (all layers
   * below), applies the adjustment filter, then draws the result back with
   * the layer's opacity, blend mode, and mask respected.
   */
  private drawAdjustmentLayer(ctx: CanvasRenderingContext2D, layer: AdjustmentLayer): void {
    const doc = this._currentDoc;
    if (!doc) return;

    const w = doc.width;
    const h = doc.height;
    if (w <= 0 || h <= 0) return;

    // Read the current canvas pixels (everything rendered so far = all layers
    // below). CTM-correct read (bug i39): a plain getImageData(0,0,w,h) on the
    // pan/zoom/dpr-transformed main context reads the wrong device box, so the
    // adjustment sampled the wrong pixels whenever zoom≠1/pan≠0/dpr≠1. The
    // write-back below (drawImage under the CTM) was already correct, so only
    // the read needed fixing. readDocPixels is a no-op 1:1 read on identity
    // offscreen contexts, so adjustment layers inside isolated groups are
    // unaffected.
    const imageData = this.readDocPixels(ctx, w, h);
    if (!imageData) return;
    const pixels = imageData.data;

    // Save a copy of the original pixels for mask blending and opacity compositing.
    const original = new Uint8ClampedArray(pixels);

    // Apply the adjustment in-place on the working copy.
    applyAdjustment(pixels, w, h, {
      kind: layer.adjustment,
      params: layer.params,
    });

    // If a mask is attached and enabled, blend the adjusted pixels with the
    // original based on per-pixel mask alpha × density.  When feather > 0,
    // apply true Gaussian feather to the mask alpha first (see issue #84).
    if (layer.mask && layer.mask.enabled) {
      const mask = layer.mask;
      const maskBuf = mask.pixels;
      const mw = maskBuf.width;
      const mh = maskBuf.height;
      if (mw > 0 && mh > 0) {
        const density = mask.density;
        const ox = mask.x;
        const oy = mask.y;
        // Extract alpha + optionally feather (true Gaussian on alpha channel).
        let alphaChannel: Uint8ClampedArray;
        if (mask.feather > 0) {
          const a = new Uint8ClampedArray(mw * mh);
          for (let i = 0; i < a.length; i++) a[i] = maskBuf.data[i * 4 + 3] ?? 0;
          alphaChannel = gaussianBlurMaskAlpha(a, mw, mh, mask.feather);
        } else {
          alphaChannel = new Uint8ClampedArray(mw * mh);
          for (let i = 0; i < alphaChannel.length; i++) {
            alphaChannel[i] = maskBuf.data[i * 4 + 3] ?? 0;
          }
        }
        for (let y = 0; y < h; y++) {
          for (let x = 0; x < w; x++) {
            const di = (y * w + x) * 4;
            const mx = Math.max(0, Math.min(mw - 1, x - ox));
            const my = Math.max(0, Math.min(mh - 1, y - oy));
            const mi = my * mw + mx;
            const maskAlpha = ((alphaChannel[mi] ?? 0) / 255) * density;
            if (maskAlpha < 1) {
              const inv = 1 - maskAlpha;
              pixels[di] = Math.round(pixels[di]! * maskAlpha + original[di]! * inv);
              pixels[di + 1] = Math.round(pixels[di + 1]! * maskAlpha + original[di + 1]! * inv);
              pixels[di + 2] = Math.round(pixels[di + 2]! * maskAlpha + original[di + 2]! * inv);
              pixels[di + 3] = Math.max(pixels[di + 3]!, original[di + 3]!);
            }
          }
        }
      }
    }

    // Write the result back via an offscreen canvas so that globalAlpha and
    // globalCompositeOperation are honoured (putImageData bypasses both).
    let temp = this.offscreenTemp;
    if (!temp) { temp = document.createElement("canvas"); this.offscreenTemp = temp; }
    temp.width = w;
    temp.height = h;
    const tctx = temp.getContext("2d");
    if (!tctx) return;
    tctx.setTransform(1, 0, 0, 1, 0, 0);
    tctx.clearRect(0, 0, w, h);
    tctx.putImageData(new ImageData(new Uint8ClampedArray(pixels), w, h), 0, 0);

    ctx.save();
    ctx.globalAlpha = layer.opacity;
    if (layer.blendMode !== "normal") {
      const native = nativeCompositeOp(layer.blendMode);
      if (native !== null) {
        ctx.globalCompositeOperation = native;
      }
    }
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(temp, 0, 0);
    ctx.restore();
  }

  /**
   * Produce a canvas containing the mask's PixelBuffer for an adjustment layer.
   * Reuses the same logic as getMaskBitmapForLayer but for AdjustmentLayer.
   */
  private getMaskBitmapForAdjustmentLayer(layer: AdjustmentLayer): ImageData | null {
    const mask = layer.mask;
    if (!mask) return null;
    const buf = mask.pixels;
    const w = buf.width;
    const h = buf.height;
    if (w <= 0 || h <= 0) return null;

    const off = document.createElement("canvas");
    off.width = w;
    off.height = h;
    const offCtx = off.getContext("2d");
    if (!offCtx) return null;
    const imageData = offCtx.createImageData(w, h);
    imageData.data.set(buf.data);
    offCtx.putImageData(imageData, 0, 0);
    return imageData;
  }

  /**
   * Render a Smart Object layer. Composites the embedded document into a
   * canvas, applies the Smart Object's transform (scale, rotation, skew),
   * then draws it with the layer's blend mode and opacity.
   */
  private drawSmartObjectLayer(ctx: CanvasRenderingContext2D, layer: SmartObjectLayer): void {
    const embedded = layer.embedded;
    if (!embedded || embedded.layers.length === 0) return;

    // Composite the embedded document into a canvas
    const soBitmap = this.getSmartObjectBitmap(layer);
    if (!soBitmap) return;

    const { scaleX, scaleY, rotation, skewX, skewY } = layer.transform;
    const hasTransform = scaleX !== 1 || scaleY !== 1 || rotation !== 0 || skewX !== 0 || skewY !== 0;

    const native = nativeCompositeOp(layer.blendMode);
    if (native !== null) {
      ctx.save();
      ctx.globalAlpha = layer.opacity;
      ctx.globalCompositeOperation = native;
      ctx.imageSmoothingEnabled = false;

      if (hasTransform) {
        ctx.translate(layer.x, layer.y);
        ctx.rotate((rotation * Math.PI) / 180);
        ctx.scale(scaleX, scaleY);
        // Skew via transform matrix
        if (skewX !== 0 || skewY !== 0) {
          const currentTransform = ctx.getTransform();
          const skewMatrix = new DOMMatrix();
          skewMatrix.skewX(skewX);
          skewMatrix.skewY(skewY);
          ctx.setTransform(currentTransform.multiply(skewMatrix));
        }
        ctx.drawImage(soBitmap, 0, 0);
      } else {
        ctx.drawImage(soBitmap, layer.x, layer.y);
      }
      ctx.restore();
    } else {
      // Offscreen composite for non-native blend modes
      this.compositeSmartObjectOffscreen(ctx, layer, soBitmap);
    }

    // Render layer styles on top — apply the layer's blend mode (#193).
    if (layer.styles && hasAnyStyle(layer.styles)) {
      const styled = renderLayerStyles(soBitmap, layer.styles, soBitmap.width, soBitmap.height);
      const styledNative = nativeCompositeOp(layer.blendMode);
      ctx.save();
      ctx.globalAlpha = layer.opacity;
      if (styledNative !== null) ctx.globalCompositeOperation = styledNative;
      ctx.imageSmoothingEnabled = false;
      ctx.drawImage(styled, layer.x, layer.y);
      ctx.restore();
    }
  }

  /**
   * Paint the shape path fill + stroke of `layer` into `ctx`.
   * `layer.x` / `layer.y` are already translated before calling.
   */
  private paintShapeLayerContent(ctx: CanvasRenderingContext2D, layer: import("../document/Layer").ShapeLayer): void {
    for (const subpath of layer.path.subpaths) {
      if (subpath.points.length === 0) continue;

      ctx.beginPath();
      this.buildPathFromSubpath(ctx, subpath);

      if (layer.fillColor) {
        const { r, g, b, a } = layer.fillColor;
        ctx.fillStyle = `rgba(${r},${g},${b},${a})`;
        ctx.fill(layer.fillRule);
      }

      if (layer.strokeColor && layer.strokeWidth > 0) {
        const { r, g, b, a } = layer.strokeColor;
        ctx.strokeStyle = `rgba(${r},${g},${b},${a})`;
        ctx.lineWidth = layer.strokeWidth;
        ctx.lineCap = layer.lineCap;
        ctx.lineJoin = layer.lineJoin;
        ctx.miterLimit = layer.miterLimit;
        ctx.stroke();
      }
    }
  }

  private drawShapeLayer(ctx: CanvasRenderingContext2D, layer: import("../document/Layer").ShapeLayer): void {
    const hasStyles = !!(layer.styles && hasAnyStyle(layer.styles));

    // When layer styles are present, render the shape to an offscreen first
    // so that renderLayerStyles can extract alpha and apply effects (#192).
    if (hasStyles) {
      const doc = this._currentDoc;
      const w = doc?.width ?? 4096;
      const h = doc?.height ?? 4096;
      if (w <= 0 || h <= 0) return;

      const offscreen = document.createElement("canvas");
      offscreen.width = w;
      offscreen.height = h;
      const offCtx = offscreen.getContext("2d")!;
      offCtx.translate(layer.x, layer.y);
      this.paintShapeLayerContent(offCtx, layer);

      const styled = renderLayerStyles(offscreen, layer.styles!, w, h);

      const native = nativeCompositeOp(layer.blendMode);
      ctx.save();
      ctx.globalAlpha = layer.opacity;
      if (native !== null) ctx.globalCompositeOperation = native;
      ctx.imageSmoothingEnabled = false;
      ctx.drawImage(styled, 0, 0);
      ctx.restore();
      return;
    }

    // Fast path: no layer styles.
    ctx.save();
    ctx.globalAlpha = layer.opacity;
    ctx.translate(layer.x, layer.y);

    const native = nativeCompositeOp(layer.blendMode);
    if (native !== null) {
      ctx.globalCompositeOperation = native;
    }

    this.paintShapeLayerContent(ctx, layer);

    ctx.restore();
  }

  private buildPathFromSubpath(
    ctx: CanvasRenderingContext2D,
    subpath: import("../document/PathModel").SubPath,
  ): void {
    const pts = subpath.points;
    if (pts.length === 0) return;

    ctx.moveTo(pts[0]!.x, pts[0]!.y);

    for (let i = 1; i < pts.length; i++) {
      const prev = pts[i - 1]!;
      const curr = pts[i]!;

      if (prev.rightDirection && curr.leftDirection) {
        ctx.bezierCurveTo(
          prev.rightDirection.x, prev.rightDirection.y,
          curr.leftDirection.x, curr.leftDirection.y,
          curr.x, curr.y,
        );
      } else if (prev.rightDirection) {
        ctx.quadraticCurveTo(
          prev.rightDirection.x, prev.rightDirection.y,
          curr.x, curr.y,
        );
      } else if (curr.leftDirection) {
        ctx.quadraticCurveTo(
          curr.leftDirection.x, curr.leftDirection.y,
          curr.x, curr.y,
        );
      } else {
        ctx.lineTo(curr.x, curr.y);
      }
    }

    if (subpath.closed && pts.length > 2) {
      const last = pts[pts.length - 1]!;
      const first = pts[0]!;

      if (last.rightDirection && first.leftDirection) {
        ctx.bezierCurveTo(
          last.rightDirection.x, last.rightDirection.y,
          first.leftDirection.x, first.leftDirection.y,
          first.x, first.y,
        );
      } else if (last.rightDirection) {
        ctx.quadraticCurveTo(
          last.rightDirection.x, last.rightDirection.y,
          first.x, first.y,
        );
      } else if (first.leftDirection) {
        ctx.quadraticCurveTo(
          first.leftDirection.x, first.leftDirection.y,
          first.x, first.y,
        );
      } else {
        ctx.lineTo(first.x, first.y);
      }
      ctx.closePath();
    }
  }

  /** Render a Solid Fill Layer. */
  private drawSolidFillLayer(ctx: CanvasRenderingContext2D, layer: SolidFillLayer): void {
    const doc = this._currentDoc;
    if (!doc) return;

    // If a raster mask is present, render into an offscreen, then apply
    // the mask alpha via destination-in (matching getMaskedBitmap pattern).
    if (layer.mask && layer.mask.enabled) {
      const off = this.renderFillToCanvasWithMask(layer, doc.width, doc.height, (offCtx) => {
        const { r, g, b, a } = layer.color;
        offCtx.fillStyle = `rgba(${r},${g},${b},${a})`;
        offCtx.fillRect(0, 0, doc.width, doc.height);
      });
      if (off) {
        const native = nativeCompositeOp(layer.blendMode);
        ctx.save();
        ctx.globalAlpha = layer.opacity;
        if (native !== null) ctx.globalCompositeOperation = native;
        ctx.imageSmoothingEnabled = false;
        ctx.drawImage(off, 0, 0);
        ctx.restore();
        return;
      }
    }

    ctx.save();
    ctx.globalAlpha = layer.opacity;

    const native = nativeCompositeOp(layer.blendMode);
    if (native !== null) ctx.globalCompositeOperation = native;

    // Apply vector mask as clip path
    if (this.applyVectorMask(ctx, layer)) {
      const { r, g, b, a } = layer.color;
      ctx.fillStyle = `rgba(${r},${g},${b},${a})`;
      ctx.fillRect(0, 0, doc.width, doc.height);
      ctx.restore();
      return;
    }

    const { r, g, b, a } = layer.color;
    ctx.fillStyle = `rgba(${r},${g},${b},${a})`;
    ctx.fillRect(0, 0, doc.width, doc.height);
    ctx.restore();
  }

  /**
   * Render a fill (solid/gradient/pattern) into an offscreen canvas of the
   * given size, then apply the layer's raster mask (LayerMask) via the same
   * destination-in pattern used by getMaskedBitmap.  Returns the masked
   * canvas, or null if the layer has no mask.  Honours mask.enabled, density,
   * x/y offset, and true Gaussian feather.
   */
  private renderFillToCanvasWithMask(
    layer: { id: string; mask?: import("../document/Layer").LayerMask },
    width: number,
    height: number,
    paint: (octx: CanvasRenderingContext2D) => void,
  ): HTMLCanvasElement | null {
    const mask = layer.mask;
    if (!mask) return null;
    if (width <= 0 || height <= 0) return null;
    const off = document.createElement("canvas");
    off.width = width;
    off.height = height;
    const octx = off.getContext("2d");
    if (!octx) return null;
    octx.setTransform(1, 0, 0, 1, 0, 0);
    octx.clearRect(0, 0, width, height);
    paint(octx);

    if (mask.enabled) {
      const maskBitmap = this.getMaskBitmapForLayer(layer as unknown as Layer);
      if (maskBitmap) {
        octx.save();
        octx.globalCompositeOperation = "destination-in";
        octx.globalAlpha = mask.density;
        octx.drawImage(maskBitmap, mask.x, mask.y);
        octx.restore();
      }
    }
    return off;
  }

  /** Render a Gradient Fill Layer. */
  private drawGradientFillLayer(ctx: CanvasRenderingContext2D, layer: GradientFillLayer): void {
    const doc = this._currentDoc;
    if (!doc) return;

    // Raster mask path: render the gradient into an offscreen then mask.
    if (layer.mask && layer.mask.enabled) {
      const off = this.renderFillToCanvasWithMask(layer, doc.width, doc.height, (offCtx) => {
        const gradient = this.createGradient(offCtx, layer, doc.width, doc.height);
        if (gradient) {
          offCtx.fillStyle = gradient;
          offCtx.fillRect(0, 0, doc.width, doc.height);
        }
      });
      if (off) {
        const native = nativeCompositeOp(layer.blendMode);
        ctx.save();
        ctx.globalAlpha = layer.opacity;
        if (native !== null) ctx.globalCompositeOperation = native;
        ctx.imageSmoothingEnabled = false;
        ctx.drawImage(off, 0, 0);
        ctx.restore();
        return;
      }
    }

    ctx.save();
    ctx.globalAlpha = layer.opacity;

    const native = nativeCompositeOp(layer.blendMode);
    if (native !== null) ctx.globalCompositeOperation = native;

    if (this.applyVectorMask(ctx, layer)) {
      const gradient = this.createGradient(ctx, layer, doc.width, doc.height);
      if (gradient) {
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, doc.width, doc.height);
      }
      ctx.restore();
      return;
    }

    const gradient = this.createGradient(ctx, layer, doc.width, doc.height);
    if (gradient) {
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, doc.width, doc.height);
    }
    ctx.restore();
  }

  /**
   * Build the fillStyle for a Gradient Fill layer (#i144). Canvas2D's
   * native gradient constructors can only produce true linear and radial
   * ramps — "diamond", "angle" (conic sweep), and "reflected" (mirrored
   * linear) have no native equivalent, so those three are rendered by
   * sampling a per-pixel position metric into an offscreen ImageData
   * buffer and returning it as a `CanvasPattern` (a drop-in replacement
   * for `CanvasGradient` at every `ctx.fillStyle = gradient` call site in
   * this file — CanvasPattern is equally valid as a fillStyle).
   */
  private createGradient(
    ctx: CanvasRenderingContext2D,
    layer: GradientFillLayer,
    w: number,
    h: number,
  ): CanvasGradient | CanvasPattern | null {
    const angleRad = (layer.angle * Math.PI) / 180;
    const cx = w / 2;
    const cy = h / 2;
    const scale = layer.scale / 100;

    switch (layer.gradientType) {
      case "linear": {
        const x1 = cx - Math.cos(angleRad) * w * scale;
        const y1 = cy - Math.sin(angleRad) * h * scale;
        const x2 = cx + Math.cos(angleRad) * w * scale;
        const y2 = cy + Math.sin(angleRad) * h * scale;
        const gradient = ctx.createLinearGradient(x1, y1, x2, y2);
        this.addGradientStops(gradient, layer);
        return gradient;
      }
      case "radial": {
        const gradient = ctx.createRadialGradient(cx, cy, 0, cx, cy, Math.max(w, h) * scale);
        this.addGradientStops(gradient, layer);
        return gradient;
      }
      case "diamond": {
        // Diamond metric: |dx| + |dy| (Manhattan/L1 distance from center),
        // normalized so it reaches 1 at the edge of the diamond inscribed
        // at `scale` (matching the radial cases' `Math.max(w,h) * scale`
        // reference radius).
        const norm = Math.max(w, h) * scale || 1;
        return this.createPixelGradient(ctx, layer, w, h, (x, y) => {
          const dx = Math.abs(x - cx);
          const dy = Math.abs(y - cy);
          return (dx + dy) / norm;
        });
      }
      case "reflected": {
        // Mirror a linear gradient about the center, along the `angle`
        // axis: distance from the perpendicular-bisector plane through the
        // center, folded so both sides read the identical 0->1 ramp.
        const dirX = Math.cos(angleRad);
        const dirY = Math.sin(angleRad);
        const norm = (w * Math.abs(dirX) + h * Math.abs(dirY)) * scale || 1;
        return this.createPixelGradient(ctx, layer, w, h, (x, y) => {
          const proj = (x - cx) * dirX + (y - cy) * dirY;
          return Math.abs(proj) / norm;
        });
      }
      case "angle": {
        // Angle (conic) sweep: atan2 around the center, offset by the
        // layer's angle, wrapped into [0, 1).
        return this.createPixelGradient(ctx, layer, w, h, (x, y) => {
          const theta = Math.atan2(y - cy, x - cx) - angleRad;
          let t = theta / (2 * Math.PI);
          t -= Math.floor(t);
          return t;
        });
      }
      default: {
        const gradient = ctx.createLinearGradient(0, 0, w, 0);
        this.addGradientStops(gradient, layer);
        return gradient;
      }
    }
  }

  /** Add this layer's color stops (honoring `reverse`) to a native CanvasGradient. */
  private addGradientStops(gradient: CanvasGradient, layer: GradientFillLayer): void {
    const stops = layer.reverse ? [...layer.stops].reverse() : layer.stops;
    for (const stop of stops) {
      const { r, g, b, a } = stop.color;
      gradient.addColorStop(stop.position, `rgba(${r},${g},${b},${a})`);
    }
  }

  /**
   * Render a gradient type Canvas2D has no native constructor for by
   * sampling `posFn(x, y)` (a 0..1 position metric) at every pixel of a
   * w×h offscreen buffer, mapping it through the layer's stops, and
   * returning the result as a non-repeating CanvasPattern sized to exactly
   * cover a `fillRect(0, 0, w, h)` call (#i144).
   */
  private createPixelGradient(
    ctx: CanvasRenderingContext2D,
    layer: GradientFillLayer,
    w: number,
    h: number,
    posFn: (x: number, y: number) => number,
  ): CanvasPattern | null {
    if (w <= 0 || h <= 0) return null;
    const stops = layer.reverse ? [...layer.stops].reverse() : layer.stops;
    const sortedStops = [...stops].sort((a, b) => a.position - b.position);
    if (sortedStops.length === 0) return null;

    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const pctx = canvas.getContext("2d");
    if (!pctx) return null;

    const imageData = pctx.createImageData(w, h);
    const data = imageData.data;
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        // Sample at the pixel center, matching how the rest of this
        // renderer treats document-space pixel coordinates.
        const t = posFn(x + 0.5, y + 0.5);
        const color = this.sampleGradientStops(sortedStops, t);
        const i = (y * w + x) * 4;
        data[i] = Math.round(color.r);
        data[i + 1] = Math.round(color.g);
        data[i + 2] = Math.round(color.b);
        data[i + 3] = Math.round(color.a * 255);
      }
    }
    pctx.putImageData(imageData, 0, 0);
    return ctx.createPattern(canvas, "no-repeat");
  }

  /** Piecewise-linear color interpolation across sorted gradient stops at position `t`. */
  private sampleGradientStops(
    sortedStops: ReadonlyArray<{ position: number; color: { r: number; g: number; b: number; a: number } }>,
    t: number,
  ): { r: number; g: number; b: number; a: number } {
    const first = sortedStops[0]!;
    if (t <= first.position) return first.color;
    const last = sortedStops[sortedStops.length - 1]!;
    if (t >= last.position) return last.color;
    for (let i = 0; i < sortedStops.length - 1; i++) {
      const a = sortedStops[i]!;
      const b = sortedStops[i + 1]!;
      if (t >= a.position && t <= b.position) {
        const span = b.position - a.position;
        const f = span > 0 ? (t - a.position) / span : 0;
        return {
          r: a.color.r + (b.color.r - a.color.r) * f,
          g: a.color.g + (b.color.g - a.color.g) * f,
          b: a.color.b + (b.color.b - a.color.b) * f,
          a: a.color.a + (b.color.a - a.color.a) * f,
        };
      }
    }
    return last.color;
  }

  /** Render a Pattern Fill Layer. */
  private drawPatternFillLayer(ctx: CanvasRenderingContext2D, layer: PatternFillLayer): void {
    const doc = this._currentDoc;
    if (!doc) return;

    if (layer.mask && layer.mask.enabled) {
      const off = this.renderFillToCanvasWithMask(layer, doc.width, doc.height, (offCtx) => {
        // Re-issue drawPattern's body inline so we paint into offCtx.
        if (!layer.pattern || layer.pattern.width <= 0 || layer.pattern.height <= 0) return;
        const pat = layer.pattern;
        const scale = layer.scale / 100;
        const pw = pat.width * scale;
        const ph = pat.height * scale;
        const off2 = document.createElement("canvas");
        off2.width = Math.max(1, Math.round(pw));
        off2.height = Math.max(1, Math.round(ph));
        const o2 = off2.getContext("2d");
        if (!o2) return;
        o2.drawImage(
          this.pixelBufferToCanvas(pat),
          0, 0, pat.width, pat.height,
          0, 0, off2.width, off2.height,
        );
        const pattern = offCtx.createPattern(off2, "repeat");
        if (pattern) {
          offCtx.fillStyle = pattern;
          offCtx.fillRect(0, 0, doc.width, doc.height);
        }
      });
      if (off) {
        const native = nativeCompositeOp(layer.blendMode);
        ctx.save();
        ctx.globalAlpha = layer.opacity;
        if (native !== null) ctx.globalCompositeOperation = native;
        ctx.imageSmoothingEnabled = false;
        ctx.drawImage(off, 0, 0);
        ctx.restore();
        return;
      }
    }

    ctx.save();
    ctx.globalAlpha = layer.opacity;

    const native = nativeCompositeOp(layer.blendMode);
    if (native !== null) ctx.globalCompositeOperation = native;

    if (this.applyVectorMask(ctx, layer)) {
      if (layer.pattern && layer.pattern.width > 0 && layer.pattern.height > 0) {
        this.drawPattern(ctx, layer);
      }
      ctx.restore();
      return;
    }

    if (layer.pattern && layer.pattern.width > 0 && layer.pattern.height > 0) {
      this.drawPattern(ctx, layer);
    }
    ctx.restore();
  }

  private drawPattern(ctx: CanvasRenderingContext2D, layer: PatternFillLayer): void {
    const doc = this._currentDoc;
    if (!doc || !layer.pattern) return;

    const pat = layer.pattern;
    const scale = layer.scale / 100;
    const pw = pat.width * scale;
    const ph = pat.height * scale;

    // Create offscreen canvas for pattern tile
    const off = document.createElement("canvas");
    off.width = Math.max(1, Math.round(pw));
    off.height = Math.max(1, Math.round(ph));
    const octx = off.getContext("2d");
    if (!octx) return;

    // Draw the pattern scaled
    octx.drawImage(
      this.pixelBufferToCanvas(pat),
      0, 0, pat.width, pat.height,
      0, 0, off.width, off.height,
    );

    // Tile the pattern
    const pattern = ctx.createPattern(off, "repeat");
    if (pattern) {
      ctx.fillStyle = pattern;
      ctx.fillRect(0, 0, doc.width, doc.height);
    }
  }

  private pixelBufferToCanvas(buf: { pixels: Uint8ClampedArray; width: number; height: number }): HTMLCanvasElement {
    const off = document.createElement("canvas");
    off.width = buf.width;
    off.height = buf.height;
    const octx = off.getContext("2d");
    if (octx) {
      const imageData = octx.createImageData(buf.width, buf.height);
      imageData.data.set(buf.pixels);
      octx.putImageData(imageData, 0, 0);
    }
    return off;
  }

  /** Render a Background Layer. */
  private drawBackgroundLayer(ctx: CanvasRenderingContext2D, layer: BackgroundLayer): void {
    const rawBitmap = this.getLayerBitmap(layer as unknown as RasterLayer);
    if (!rawBitmap) return;

    ctx.save();
    ctx.globalAlpha = layer.opacity;
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(rawBitmap, layer.x, layer.y);
    ctx.restore();
  }

  /** Render an Artboard Layer. */
  private drawArtboardLayer(ctx: CanvasRenderingContext2D, layer: ArtboardLayer): void {
    ctx.save();

    // Draw artboard background
    const { r, g, b, a } = layer.backgroundColor;
    ctx.fillStyle = `rgba(${r},${g},${b},${a})`;
    ctx.fillRect(layer.artboardX, layer.artboardY, layer.artboardWidth, layer.artboardHeight);

    // Draw artboard border
    ctx.strokeStyle = "rgba(128, 128, 128, 0.5)";
    ctx.lineWidth = 1;
    ctx.strokeRect(layer.artboardX, layer.artboardY, layer.artboardWidth, layer.artboardHeight);

    // Clip children to artboard bounds
    ctx.beginPath();
    ctx.rect(layer.artboardX, layer.artboardY, layer.artboardWidth, layer.artboardHeight);
    ctx.clip();

    // Render children
    for (const child of layer.children) {
      this.drawLayer(ctx, child);
    }

    ctx.restore();
  }

  /** Render a Frame Layer. */
  private drawFrameLayer(ctx: CanvasRenderingContext2D, layer: import("../document/Layer").FrameLayer): void {
    ctx.save();

    const x = layer.x;
    const y = layer.y;
    const w = layer.frameWidth;
    const h = layer.frameHeight;

    // Draw frame background (placeholder gray)
    ctx.fillStyle = "rgba(200, 200, 200, 0.3)";
    ctx.strokeStyle = "rgba(100, 100, 100, 0.6)";
    ctx.lineWidth = 1;

    if (layer.shape === "ellipse") {
      ctx.beginPath();
      ctx.ellipse(x + w / 2, y + h / 2, w / 2, h / 2, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
    } else if (layer.shape === "rounded-rect") {
      const r = Math.min(layer.cornerRadius, w / 2, h / 2);
      ctx.beginPath();
      ctx.moveTo(x + r, y);
      ctx.lineTo(x + w - r, y);
      ctx.quadraticCurveTo(x + w, y, x + w, y + r);
      ctx.lineTo(x + w, y + h - r);
      ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
      ctx.lineTo(x + r, y + h);
      ctx.quadraticCurveTo(x, y + h, x, y + h - r);
      ctx.lineTo(x, y + r);
      ctx.quadraticCurveTo(x, y, x + r, y);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
    } else {
      ctx.fillRect(x, y, w, h);
      ctx.strokeRect(x, y, w, h);
    }

    // Draw content if present
    if (layer.content) {
      this.drawLayer(ctx, layer.content);
    }

    ctx.restore();
  }

  private drawLinkedSmartObjectLayer(ctx: CanvasRenderingContext2D, layer: import("../document/Layer").LinkedSmartObjectLayer): void {
    ctx.save();
    ctx.globalAlpha = layer.opacity;

    if (layer.isMissing) {
      ctx.fillStyle = "rgba(255, 0, 0, 0.2)";
      ctx.fillRect(layer.x, layer.y, 200, 100);
      ctx.fillStyle = "#ff0000";
      ctx.font = "12px sans-serif";
      ctx.fillText("Linked file missing", layer.x + 10, layer.y + 50);
      ctx.restore();
      return;
    }

    if (layer.composite) {
      const comp = layer.composite;
      const imageData = new ImageData(new Uint8ClampedArray(comp.data), comp.width, comp.height);
      const tmpCanvas = document.createElement("canvas");
      tmpCanvas.width = comp.width;
      tmpCanvas.height = comp.height;
      const tmpCtx = tmpCanvas.getContext("2d")!;
      tmpCtx.putImageData(imageData, 0, 0);

      const { scaleX, scaleY, rotation } = layer.transform;
      const cx = layer.x + comp.width / 2;
      const cy = layer.y + comp.height / 2;
      ctx.translate(cx, cy);
      ctx.rotate((rotation * Math.PI) / 180);
      ctx.scale(scaleX, scaleY);
      ctx.translate(-cx, -cy);
      ctx.imageSmoothingEnabled = false;
      ctx.drawImage(tmpCanvas, layer.x, layer.y);
    } else {
      ctx.fillStyle = "rgba(100, 150, 255, 0.2)";
      ctx.fillRect(layer.x, layer.y, 200, 100);
      ctx.fillStyle = "#6699ff";
      ctx.font = "12px sans-serif";
      ctx.fillText(`Linked: ${layer.filePath.split("/").pop() || layer.filePath}`, layer.x + 10, layer.y + 50);
    }

    ctx.restore();
  }

  /**
   * Apply a vector mask as a clip path if present on the layer.
   * Returns true if a mask was applied.
   * Handles all layer kinds — `vectorMask` is now declared on `LayerBase`
   * so any layer kind can carry one (fixes #194).
   */
  private applyVectorMask(ctx: CanvasRenderingContext2D, layer: Layer): boolean {
    // Access via LayerBase.vectorMask (covers raster, type, group, smart-object,
    // shape, fill layers).  The ShapeLayer-specific field is the same property.
    const mask = (layer as { vectorMask?: import("../document/PathModel").PathModel | null }).vectorMask;

    if (!mask || mask.subpaths.length === 0) return false;

    ctx.save();
    ctx.beginPath();
    for (const subpath of mask.subpaths) {
      if (subpath.points.length === 0) continue;
      this.buildPathFromSubpath(ctx, subpath);
    }
    ctx.clip();
    return true;
  }

  /** Draw fill layers for native rendering (offscreen composite). */
  private drawFillLayerNative(ctx: CanvasRenderingContext2D, layer: Layer): void {
    const doc = this._currentDoc;
    if (!doc) return;

    ctx.save();
    ctx.globalAlpha = 1;

    if (layer.kind === "solid-fill") {
      const f = layer as SolidFillLayer;
      const { r, g, b, a } = f.color;
      ctx.fillStyle = `rgba(${r},${g},${b},${a})`;
      ctx.fillRect(0, 0, doc.width, doc.height);
    } else if (layer.kind === "gradient-fill") {
      const gradient = this.createGradient(ctx, layer as GradientFillLayer, doc.width, doc.height);
      if (gradient) {
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, doc.width, doc.height);
      }
    } else if (layer.kind === "pattern-fill") {
      const f = layer as PatternFillLayer;
      if (f.pattern && f.pattern.width > 0 && f.pattern.height > 0) {
        this.drawPattern(ctx, f);
      }
    }

    ctx.restore();
  }

  /**
   * Composite a Smart Object's bitmap using a non-native blend mode.
   * Same strategy as compositeOffscreen for raster layers.
   */
  private compositeSmartObjectOffscreen(
    ctx: CanvasRenderingContext2D,
    layer: SmartObjectLayer,
    bitmap: HTMLCanvasElement,
  ): void {
    const w = bitmap.width;
    const h = bitmap.height;
    if (w <= 0 || h <= 0) return;

    let temp = this.offscreenTemp;
    if (!temp) {
      temp = document.createElement("canvas");
      this.offscreenTemp = temp;
    }
    temp.width = w;
    temp.height = h;
    const tctx = temp.getContext("2d");
    if (!tctx) return;

    tctx.setTransform(1, 0, 0, 1, 0, 0);
    tctx.clearRect(0, 0, w, h);

    // Render layers before this Smart Object
    const doc = this._currentDoc;
    if (doc) {
      tctx.save();
      tctx.translate(-layer.x, -layer.y);
      for (const l of doc.layers) {
        if (l.id === layer.id) break;
        this.drawLayerNative(tctx, l);
      }
      tctx.restore();
    }

    const dstImage = tctx.getImageData(0, 0, w, h);
    const sctx = bitmap.getContext("2d");
    if (!sctx) return;
    const srcImage = sctx.getImageData(0, 0, w, h);

    compositeLayer(dstImage.data, srcImage.data, layer.blendMode, layer.opacity);
    tctx.putImageData(dstImage, 0, 0);

    ctx.save();
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = "source-over";
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(temp, layer.x, layer.y, w, h);
    ctx.restore();
  }

  /**
   * Produce a canvas containing the composited content of a Smart Object's
   * embedded document. Cached per Smart Object id + embedded layer versions.
   */
  private getSmartObjectBitmap(layer: SmartObjectLayer): HTMLCanvasElement | null {
    const key = `${layer.id}:so`;
    // Compute a version signature from all embedded layer versions + smart filters
    let version = 0;
    const walk = (layers: readonly Layer[]) => {
      for (const l of layers) {
        if (l.kind === "raster") version += l.pixels.version;
        if (l.kind === "group") walk(l.children);
        if (l.kind === "smart-object") version += this.getSmartObjectVersion(l);
        if (l.kind === "shape") version += 1;
        if (l.kind === "type") version += 1;
        if (l.kind === "adjustment") version += 1;
      }
    };
    walk(layer.embedded.layers);

    // Include smart filter state in the version
    for (const sf of layer.smartFilters) {
      version += sf.enabled ? 1 : 0;
      // Hash params into version
      for (const [k, v] of Object.entries(sf.params)) {
        version += k.charCodeAt(0) + (typeof v === "number" ? Math.round(v * 1000) : String(v).length);
      }
    }

    const cached = this.soCache.get(key);
    if (cached && cached.version === version) return cached.canvas;

    // Render the embedded document to a canvas
    const w = layer.embedded.width;
    const h = layer.embedded.height;
    if (w <= 0 || h <= 0) return null;

    const off = document.createElement("canvas");
    off.width = w;
    off.height = h;
    const octx = off.getContext("2d");
    if (!octx) return null;

    octx.setTransform(1, 0, 0, 1, 0, 0);
    octx.clearRect(0, 0, w, h);

    // Render embedded layers
    for (const l of layer.embedded.layers) {
      this.drawEmbeddedLayer(octx, l);
    }

    // Apply smart filters sequentially
    if (layer.smartFilters.length > 0) {
      this.applySmartFilters(octx, layer.smartFilters, w, h);
    }

    this.soCache.set(key, { canvas: off, version });
    return off;
  }

  /**
   * Apply a stack of smart filters to canvas pixels. Each enabled filter
   * reads the current canvas pixels, applies the filter, then blends the
   * result back using the filter's opacity and blend mode.
   */
  private applySmartFilters(
    ctx: CanvasRenderingContext2D,
    filters: readonly import("../document/Layer").SmartFilterEntry[],
    w: number,
    h: number,
  ): void {
    for (const sf of filters) {
      if (!sf.enabled) continue;

      const filter = getFilterById(sf.filterId);
      if (!filter) continue;

      // Read current canvas pixels
      const imageData = ctx.getImageData(0, 0, w, h);
      const buf = new PixelBuffer(w, h);
      buf.data.set(imageData.data);

      // Apply filter
      const filtered = filter.apply(buf, sf.params as Record<string, unknown>, null);

      // Blend result back using filter's opacity and blend mode
      ctx.save();
      ctx.globalAlpha = sf.opacity;
      const native = nativeCompositeOp(sf.blendMode);
      if (native !== null) {
        ctx.globalCompositeOperation = native;
      }
      ctx.imageSmoothingEnabled = false;

      const filteredCanvas = document.createElement("canvas");
      filteredCanvas.width = w;
      filteredCanvas.height = h;
      const fctx = filteredCanvas.getContext("2d");
      if (fctx) {
        fctx.putImageData(new ImageData(new Uint8ClampedArray(filtered.data), w, h), 0, 0);
        ctx.drawImage(filteredCanvas, 0, 0);
      }
      ctx.restore();
    }
  }

  /**
   * Draw a layer inside a Smart Object's embedded document. Simplified
   * version that doesn't need blend-mode isolation (the embedded doc is
   * composited flat, then the result is blended as a single layer).
   */
  private drawEmbeddedLayer(ctx: CanvasRenderingContext2D, layer: Layer): void {
    if (!layer.visible || layer.opacity <= 0) return;

    if (layer.kind === "group") {
      ctx.save();
      ctx.globalAlpha *= layer.opacity;
      for (const child of layer.children) {
        this.drawEmbeddedLayer(ctx, child);
      }
      ctx.restore();
      return;
    }

    if (layer.kind === "adjustment") {
      // Adjustment layers in embedded docs: apply to canvas pixels
      this.drawAdjustmentLayer(ctx, layer);
      return;
    }

    if (layer.kind === "type") {
      this.drawTypeLayer(ctx, layer);
      return;
    }

    if (layer.kind === "smart-object") {
      // Nested Smart Objects: recursively composite
      const nestedBitmap = this.getSmartObjectBitmap(layer);
      if (nestedBitmap) {
        ctx.save();
        ctx.globalAlpha = layer.opacity;
        ctx.imageSmoothingEnabled = false;
        ctx.drawImage(nestedBitmap, layer.x, layer.y);
        ctx.restore();
      }
      return;
    }

    const raster = layer as RasterLayer;
    const rawBitmap = this.getLayerBitmap(raster);
    if (!rawBitmap) return;

    let bitmap =
      raster.mask && raster.mask.enabled
        ? this.getMaskedBitmap(raster, rawBitmap)
        : rawBitmap;
    if (!bitmap) return;

    if (raster.fillOpacity != null && raster.fillOpacity < 1) {
      bitmap = this.getFillOpacityBitmap(raster, bitmap);
    }
    if (!bitmap) return;

    ctx.save();
    ctx.globalAlpha = layer.opacity;
    ctx.globalCompositeOperation = nativeCompositeOp(layer.blendMode) ?? "source-over";
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(bitmap, raster.x, raster.y);
    ctx.restore();
  }

  /** Get a version number for a nested Smart Object (for cache invalidation). */
  private getSmartObjectVersion(layer: SmartObjectLayer): number {
    let v = 0;
    const walk = (layers: readonly Layer[]) => {
      for (const l of layers) {
        if (l.kind === "raster") v += l.pixels.version;
        if (l.kind === "group") walk(l.children);
        if (l.kind === "smart-object") v += this.getSmartObjectVersion(l);
      }
    };
    walk(layer.embedded.layers);
    return v;
  }

  /** Cache for Smart Object composites. */
  private soCache = new Map<string, { canvas: HTMLCanvasElement; version: number }>();

  /** Compute the union bounding box of all visible children in document-space. */
  private groupBoundingBox(group: GroupLayer): { x: number; y: number; w: number; h: number } | null {
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    let found = false;

    const visit = (l: Layer) => {
      if (!l.visible || l.opacity <= 0) return;
      if (l.kind === "group") {
        for (const child of l.children) visit(child);
        return;
      }
      if (l.kind === "smart-object") {
        const so = l as SmartObjectLayer;
        const { scaleX, scaleY } = so.transform;
        const pw = so.embedded.width * scaleX;
        const ph = so.embedded.height * scaleY;
        if (pw <= 0 || ph <= 0) return;
        if (l.x < minX) minX = l.x;
        if (l.y < minY) minY = l.y;
        if (l.x + pw > maxX) maxX = l.x + pw;
        if (l.y + ph > maxY) maxY = l.y + ph;
        found = true;
        return;
      }
      if (l.kind === "solid-fill" || l.kind === "gradient-fill" || l.kind === "pattern-fill") {
        const doc = this._currentDoc;
        if (!doc) return;
        if (0 < minX) minX = 0;
        if (0 < minY) minY = 0;
        if (doc.width > maxX) maxX = doc.width;
        if (doc.height > maxY) maxY = doc.height;
        found = true;
        return;
      }
      if (l.kind === "background") {
        const bg = l as BackgroundLayer;
        const pw = bg.pixels?.width ?? 0;
        const ph = bg.pixels?.height ?? 0;
        if (pw <= 0 || ph <= 0) return;
        if (bg.x < minX) minX = bg.x;
        if (bg.y < minY) minY = bg.y;
        if (bg.x + pw > maxX) maxX = bg.x + pw;
        if (bg.y + ph > maxY) maxY = bg.y + ph;
        found = true;
        return;
      }
      if (l.kind === "artboard") {
        const ab = l as ArtboardLayer;
        if (ab.artboardX < minX) minX = ab.artboardX;
        if (ab.artboardY < minY) minY = ab.artboardY;
        if (ab.artboardX + ab.artboardWidth > maxX) maxX = ab.artboardX + ab.artboardWidth;
        if (ab.artboardY + ab.artboardHeight > maxY) maxY = ab.artboardY + ab.artboardHeight;
        found = true;
        for (const child of ab.children) visit(child);
        return;
      }
      const layer = l as RasterLayer;
      const pw = layer.pixels?.width ?? 0;
      const ph = layer.pixels?.height ?? 0;
      if (pw <= 0 || ph <= 0) return;
      if (l.x < minX) minX = l.x;
      if (l.y < minY) minY = l.y;
      if (l.x + pw > maxX) maxX = l.x + pw;
      if (l.y + ph > maxY) maxY = l.y + ph;
      found = true;
    };

    for (const child of group.children) visit(child);
    if (!found) return null;
    return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
  }

  /** Composite a group's isolated buffer using a non-native blend mode. */
  private compositeGroupOffscreen(
    ctx: CanvasRenderingContext2D,
    group: GroupLayer,
    bitmap: HTMLCanvasElement,
    bbox: { x: number; y: number; w: number; h: number },
  ): void {
    const w = bbox.w;
    const h = bbox.h;
    if (w <= 0 || h <= 0) return;

    // Render the document's layers *before* the group into a temp canvas at
    // native resolution. This avoids interpolation artifacts from reading
    // device-space pixels from the zoomed/scaled main canvas.
    let temp = this.offscreenTemp;
    if (!temp) {
      temp = document.createElement("canvas");
      this.offscreenTemp = temp;
    }
    temp.width = w;
    temp.height = h;
    const tctx = temp.getContext("2d");
    if (!tctx) return;

    tctx.setTransform(1, 0, 0, 1, 0, 0);
    tctx.clearRect(0, 0, w, h);

    // Render layers before the group. We find the group in the document and
    // render everything before it.
    const doc = this._currentDoc;
    if (doc) {
      tctx.save();
      tctx.translate(-bbox.x, -bbox.y);
      const found = this.renderLayersBefore(doc.layers, group, tctx);
      if (!found) {
        // Group not found or no layers before it — render white background.
      }
      tctx.restore();
    }

    const dstImage = tctx.getImageData(0, 0, w, h);

    const sctx = bitmap.getContext("2d");
    if (!sctx) return;
    const srcImage = sctx.getImageData(0, 0, w, h);

    compositeLayer(dstImage.data, srcImage.data, group.blendMode, group.opacity);
    tctx.putImageData(dstImage, 0, 0);

    ctx.save();
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = "source-over";
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(temp, bbox.x, bbox.y, w, h);
    ctx.restore();
  }

  /** Render all layers before the given target layer. Returns true if target was found. */
  private renderLayersBefore(layers: readonly Layer[], target: Layer, ctx: CanvasRenderingContext2D): boolean {
    for (const l of layers) {
      if (l === target || l.id === target.id) return true;
      if (l.kind === "group") {
        const group = l as GroupLayer;
        if (this.layerIsInGroup(group, target)) {
          this.renderLayersBefore(group.children, target, ctx);
          return true;
        }
        this.drawLayerNative(ctx, l);
      } else if (l.kind === "adjustment") {
        this.drawAdjustmentLayer(ctx, l as AdjustmentLayer);
      } else if (l.kind === "smart-object") {
        const so = l as SmartObjectLayer;
        const bitmap = this.getSmartObjectBitmap(so);
        if (bitmap) {
          ctx.save();
          ctx.globalAlpha = 1;
          ctx.imageSmoothingEnabled = false;
          ctx.drawImage(bitmap, so.x, so.y);
          ctx.restore();
        }
      } else if (l.kind === "solid-fill") {
        const f = l as SolidFillLayer;
        const d = this._currentDoc;
        if (d) {
          ctx.save();
          const { r, g, b, a } = f.color;
          ctx.fillStyle = `rgba(${r},${g},${b},${a})`;
          ctx.fillRect(0, 0, d.width, d.height);
          ctx.restore();
        }
      } else if (l.kind === "gradient-fill") {
        const d = this._currentDoc;
        if (d) {
          ctx.save();
          const gradient = this.createGradient(ctx, l as GradientFillLayer, d.width, d.height);
          if (gradient) {
            ctx.fillStyle = gradient;
            ctx.fillRect(0, 0, d.width, d.height);
          }
          ctx.restore();
        }
      } else if (l.kind === "pattern-fill") {
        const f = l as PatternFillLayer;
        const d = this._currentDoc;
        if (d && f.pattern && f.pattern.width > 0 && f.pattern.height > 0) {
          ctx.save();
          this.drawPattern(ctx, f);
          ctx.restore();
        }
      } else if (l.kind === "background") {
        const bg = l as BackgroundLayer;
        const bitmap = this.getLayerBitmap(bg as unknown as RasterLayer);
        if (bitmap) {
          ctx.save();
          ctx.globalAlpha = 1;
          ctx.imageSmoothingEnabled = false;
          ctx.drawImage(bitmap, bg.x, bg.y);
          ctx.restore();
        }
      } else if (l.kind === "artboard") {
        const ab = l as ArtboardLayer;
        for (const child of ab.children) {
          this.drawLayerNative(ctx, child);
        }
      } else {
        this.drawLayerNative(ctx, l);
      }
    }
    return false;
  }

  /** Check if a layer is (recursively) inside a group. */
  private layerIsInGroup(group: GroupLayer, target: Layer): boolean {
    for (const child of group.children) {
      if (child === target || child.id === target.id) return true;
      if (child.kind === "group") {
        if (this.layerIsInGroup(child as GroupLayer, target)) return true;
      }
      if (child.kind === "smart-object") {
        // Check if target is inside the Smart Object's embedded doc
        if (this.layerIsInEmbeddedDoc((child as SmartObjectLayer).embedded, target)) return true;
      }
    }
    return false;
  }

  /** Check if a layer is inside a Smart Object's embedded document. */
  private layerIsInEmbeddedDoc(doc: Document, target: Layer): boolean {
    for (const l of doc.layers) {
      if (l === target || l.id === target.id) return true;
      if (l.kind === "group") {
        if (this.layerIsInGroup(l as GroupLayer, target)) return true;
      }
    }
    return false;
  }

  private groupIsoBuffer: HTMLCanvasElement | null = null;

  /**
   * Produce a canvas containing the layer's pixels with their alpha
   * multiplied by the mask's alpha. Cached per `${layer.id}:mask`, keyed by
   * the combined version of (layer.pixels, mask.pixels) plus enabled /
   * density / feather / offset so any of those triggering a change rebuilds
   * the cached canvas.
   *
   * Renderer integration notes:
   *
   *   - Density: scales every mask sample by `mask.density` (0..1) before
   *     applying. We do this on the mask canvas via globalAlpha on the
   *     destination-in pass, which composes uniformly without an extra
   *     pixel walk.
   *   - Feather: applies a Gaussian blur of radius `feather` px to the mask
   *     before applying. Uses `ctx.filter = "blur(Xpx)"`, supported in all
   *     modern browsers. In environments lacking `ctx.filter` (older
   *     headless contexts, some jsdom builds) the property assignment is a
   *     no-op and the mask renders sharp — visually wrong but not a crash.
   *     We don't currently fall back to a CPU blur because feather is not
   *     part of any test path; M6 (filters refactor) will revisit.
   *   - Offset: mask.x / mask.y position the mask relative to the layer.
   *     A mask smaller than the layer or offset out of frame leaves the
   *     uncovered parts of the layer with alpha = 0 (Photoshop's behavior:
   *     anywhere outside the mask is treated as fully masked).
   */
  private getMaskedBitmap(
    layer: RasterLayer,
    layerBitmap: HTMLCanvasElement,
  ): HTMLCanvasElement | null {
    const mask = layer.mask;
    if (!mask) return layerBitmap;

    const key = `${layer.id}:mask`;
    const layerVer = layer.pixels.version;
    const maskVer = mask.pixels.version;
    // Pack the parameters that affect output into a single version key.
    // Bit-shifting overflows int32 quickly; concatenated string is fine.
    const sig =
      layerVer * 1_000_003 +
      maskVer +
      // Bake the mask object's mutable-but-immutable-via-replace fields into
      // the signature too:
      (mask.enabled ? 1 : 0) * 9_999_991 +
      Math.round(mask.density * 1000) * 13_001 +
      Math.round(mask.feather * 100) * 17_009 +
      (mask.x | 0) * 23 +
      (mask.y | 0) * 29;

    const cached = this.maskCache.get(key);
    if (cached && cached.version === sig) return cached.canvas;

    const w = layer.pixels.width;
    const h = layer.pixels.height;
    if (w <= 0 || h <= 0) return null;

    const out = cached?.canvas ?? document.createElement("canvas");
    out.width = w;
    out.height = h;
    const octx = out.getContext("2d");
    if (!octx) return null;
    octx.setTransform(1, 0, 0, 1, 0, 0);
    octx.clearRect(0, 0, w, h);

    // 1. Draw the layer pixels as the base.
    octx.globalAlpha = 1;
    octx.globalCompositeOperation = "source-over";
    octx.imageSmoothingEnabled = false;
    octx.drawImage(layerBitmap, 0, 0);

    // 2. Build the mask canvas. We need a temp canvas at layer size so we
    //    can position the mask via mask.x / mask.y.
    //    If feather > 0 we apply a true Gaussian blur to the mask's alpha
    //    channel BEFORE drawing (replacing the previous stack-blur via
    //    ctx.filter that wouldn't match PS to ±1 — see issue #84).
    const maskBitmap = this.getMaskBitmapForLayer(layer);
    if (!maskBitmap) return out;

    // 3. Apply destination-in with the mask, using globalAlpha for density.
    octx.save();
    octx.globalCompositeOperation = "destination-in";
    octx.globalAlpha = mask.density;
    octx.drawImage(maskBitmap, mask.x, mask.y);
    octx.restore();

    this.maskCache.set(key, { canvas: out, version: sig });
    return out;
  }

  /**
   * Produce a canvas with the layer's pixel alpha multiplied by fillOpacity.
   * In Photoshop, fillOpacity scales the layer pixel content but NOT layer
   * styles (drop shadow, stroke, bevel, etc.). Cached per `${id}:fill`.
   */
  private getFillOpacityBitmap(
    layer: RasterLayer,
    source: HTMLCanvasElement,
  ): HTMLCanvasElement {
    const fill = layer.fillOpacity ?? 1;
    const buf = layer.pixels;
    const sig = buf.version | (fill * 0xffff);
    const key = `${layer.id}:fill`;
    const cached = this.maskCache.get(key);
    if (cached && cached.version === sig) return cached.canvas;

    const out = cached?.canvas ?? document.createElement("canvas");
    out.width = buf.width;
    out.height = buf.height;
    const octx = out.getContext("2d");
    if (!octx) return source;
    octx.setTransform(1, 0, 0, 1, 0, 0);
    octx.drawImage(source, 0, 0);

    // Multiply alpha by fillOpacity in-place
    const imageData = octx.getImageData(0, 0, buf.width, buf.height);
    const data = imageData.data;
    for (let i = 3; i < data.length; i += 4) {
      data[i] = Math.round((data[i] ?? 0) * fill);
    }
    octx.putImageData(imageData, 0, 0);

    this.maskCache.set(key, { canvas: out, version: sig });
    return out;
  }

  /**
   * Materialise the mask's PixelBuffer to a canvas, with its own cache
   * keyed by `${layer.id}:maskbuf` and a version that includes both the
   * mask buffer's version and the mask's feather radius.  The outer mask
   * cache (`getMaskedBitmap`) is keyed by a coarser signature including
   * layer pixel version, so this inner cache absorbs paints on the mask
   * without rebuilding the unrelated layer composite.
   *
   * Feather is applied here as a true Gaussian blur on the alpha channel
   * (see `MaskFeather.ts`) — not via `ctx.filter = blur(Npx)`, which in
   * Chromium is implemented as stack-blur and won't match Photoshop's
   * Gaussian feather to ±1 (see issue #84).
   */
  private getMaskBitmapForLayer(layer: { id: string; mask?: import("../document/Layer").LayerMask }): HTMLCanvasElement | null {
    const mask = layer.mask;
    if (!mask) return null;
    const buf = mask.pixels;
    const key = `${layer.id}:maskbuf`;
    // Pack mask-buffer version, feather radius, and density into the cache
    // key signature so a feather/density slider drag invalidates correctly.
    const sig =
      buf.version * 1_000_003 +
      Math.round(mask.feather * 100) * 17_009 +
      Math.round(mask.density * 1000);
    const cached = this.maskCache.get(key);
    if (cached && cached.version === sig) return cached.canvas;
    const off = cached?.canvas ?? document.createElement("canvas");
    off.width = buf.width;
    off.height = buf.height;
    const offCtx = off.getContext("2d");
    if (!offCtx) return null;
    const imageData = offCtx.createImageData(buf.width, buf.height);
    if (mask.feather > 0) {
      // Apply true Gaussian feather to the mask's alpha channel.  Reuses
      // the verified GaussianBlur math from src/core/filters/.
      const alpha = new Uint8ClampedArray(buf.width * buf.height);
      for (let i = 0; i < alpha.length; i++) {
        alpha[i] = buf.data[i * 4 + 3] ?? 0;
      }
      const blurred = gaussianBlurMaskAlpha(alpha, buf.width, buf.height, mask.feather);
      const out = imageData.data;
      for (let i = 0; i < alpha.length; i++) {
        out[i * 4] = 0;
        out[i * 4 + 1] = 0;
        out[i * 4 + 2] = 0;
        out[i * 4 + 3] = blurred[i] ?? 0;
      }
    } else {
      imageData.data.set(buf.data);
    }
    offCtx.putImageData(imageData, 0, 0);
    this.maskCache.set(key, { canvas: off, version: sig });
    return off;
  }

  private compositeOffscreen(
    ctx: CanvasRenderingContext2D,
    layer: RasterLayer,
    bitmap: HTMLCanvasElement,
  ): void {
    const w = bitmap.width;
    const h = bitmap.height;
    if (w <= 0 || h <= 0) return;

    let temp = this.offscreenTemp;
    if (!temp) {
      temp = document.createElement("canvas");
      this.offscreenTemp = temp;
    }
    temp.width = w;
    temp.height = h;
    const tctx = temp.getContext("2d");
    if (!tctx) return;

    tctx.setTransform(1, 0, 0, 1, 0, 0);
    tctx.clearRect(0, 0, w, h);

    const doc = this._currentDoc;
    if (doc) {
      tctx.save();
      tctx.translate(-layer.x, -layer.y);
      for (const l of doc.layers) {
        if (l.id === layer.id) break;
        this.drawLayerNative(tctx, l);
      }
      tctx.restore();
    }

    const dstImage = tctx.getImageData(0, 0, w, h);
    const sctx = bitmap.getContext("2d");
    if (!sctx) return;
    const srcImage = sctx.getImageData(0, 0, w, h);

    // Apply Blend If before compositing
    if (layer.blendIf) {
      this.applyBlendIf(srcImage.data, dstImage.data, layer.blendIf, w, h);
    }

    compositeLayer(dstImage.data, srcImage.data, layer.blendMode, layer.opacity);

    tctx.putImageData(dstImage, 0, 0);

    ctx.save();
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = "source-over";
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(temp, layer.x, layer.y, w, h);
    ctx.restore();
  }

  /**
   * Apply Blend If luminance/channel range filtering to source pixels.
   * Modifies src alpha based on whether source/dest pixel values fall
   * within the configured ranges.
   */
  private applyBlendIf(
    src: Uint8ClampedArray,
    dst: Uint8ClampedArray,
    blendIf: BlendIfConfig,
    w: number,
    h: number,
  ): void {
    const channels = blendIf.channels;
    if (!channels) return;

    const gray = channels["gray"];
    const red = channels["red"];
    const green = channels["green"];
    const blue = channels["blue"];

    for (let i = 0; i < src.length; i += 4) {
      const sr = src[i]!, sg = src[i + 1]!, sb = src[i + 2]!, sa = src[i + 3]!;
      if (sa === 0) continue;

      const dr = dst[i]!, dg = dst[i + 1]!, db = dst[i + 2]!;
      const sLum = (sr * 299 + sg * 587 + sb * 114) / 1000;
      const dLum = (dr * 299 + dg * 587 + db * 114) / 1000;

      let alphaFactor = 1;

      // This Layer — based on source luminance/channel values
      if (gray) {
        const [bMin, bMax] = gray.thisLayer;
        if (sLum < bMin || sLum > bMax) alphaFactor = 0;
      }
      if (red) {
        const [bMin, bMax] = red.thisLayer;
        if (sr < bMin || sr > bMax) alphaFactor = 0;
      }
      if (green) {
        const [bMin, bMax] = green.thisLayer;
        if (sg < bMin || sg > bMax) alphaFactor = 0;
      }
      if (blue) {
        const [bMin, bMax] = blue.thisLayer;
        if (sb < bMin || sb > bMax) alphaFactor = 0;
      }

      // Underlying Layer — based on dest luminance/channel values
      if (gray) {
        const [uMin, uMax] = gray.underlyingLayer;
        if (dLum < uMin || dLum > uMax) alphaFactor = 0;
      }
      if (red) {
        const [uMin, uMax] = red.underlyingLayer;
        if (dr < uMin || dr > uMax) alphaFactor = 0;
      }
      if (green) {
        const [uMin, uMax] = green.underlyingLayer;
        if (dg < uMin || dg > uMax) alphaFactor = 0;
      }
      if (blue) {
        const [uMin, uMax] = blue.underlyingLayer;
        if (db < uMin || db > uMax) alphaFactor = 0;
      }

      if (alphaFactor < 1) {
        src[i + 3] = Math.round(sa * alphaFactor);
      }
    }
  }

  /** Render a layer without blend-mode dispatch — for building the dest buffer for offscreen compositing. */
  private drawLayerNative(ctx: CanvasRenderingContext2D, layer: Layer): void {
    if (!layer.visible || layer.opacity <= 0) return;
    if (layer.kind === "group") {
      const g = layer as import("../document/Layer").GroupLayer;
      // Pass-through groups: render children directly
      if (g.blendMode === "normal" || g.blendMode === "pass") {
        ctx.save();
        ctx.globalAlpha *= layer.opacity;
        for (const child of g.children) {
          this.drawLayerNative(ctx, child);
        }
        ctx.restore();
        return;
      }
      // Non-normal blend mode: isolate children into offscreen buffer, then
      // composite with the group's blend mode. This ensures the destination
      // buffer built for offscreen compositing reflects correct group blending.
      const bbox = this.groupBoundingBox(g);
      if (!bbox || bbox.w <= 0 || bbox.h <= 0) return;
      const off = document.createElement("canvas");
      off.width = bbox.w;
      off.height = bbox.h;
      const octx = off.getContext("2d");
      if (!octx) return;
      octx.setTransform(1, 0, 0, 1, 0, 0);
      octx.save();
      octx.translate(-bbox.x, -bbox.y);
      for (const child of g.children) {
        this.drawLayerNative(octx, child);
      }
      octx.restore();
      // Composite the isolated buffer onto the destination context
      const native = nativeCompositeOp(g.blendMode);
      ctx.save();
      ctx.globalAlpha = layer.opacity;
      if (native !== null) {
        ctx.globalCompositeOperation = native;
      } else {
        // Offscreen pixel composite for non-native group blend modes
        const w = bbox.w;
        const h = bbox.h;
        const dstImage = ctx.getImageData(bbox.x, bbox.y, w, h);
        const srcImage = octx.getImageData(0, 0, w, h);
        compositeLayer(dstImage.data, srcImage.data, g.blendMode, 1);
        ctx.putImageData(dstImage, bbox.x, bbox.y);
        ctx.restore();
        return;
      }
      ctx.imageSmoothingEnabled = false;
      ctx.drawImage(off, bbox.x, bbox.y);
      ctx.restore();
      return;
    }
    if (layer.kind === "adjustment") {
      this.drawAdjustmentLayer(ctx, layer as AdjustmentLayer);
      return;
    }
    if (layer.kind === "type") {
      return;
    }
    if (layer.kind === "smart-object") {
      const so = layer as SmartObjectLayer;
      const bitmap = this.getSmartObjectBitmap(so);
      if (!bitmap) return;
      ctx.save();
      ctx.globalAlpha = layer.opacity;
      ctx.imageSmoothingEnabled = false;
      ctx.drawImage(bitmap, so.x, so.y);
      ctx.restore();
      return;
    }

    if (layer.kind === "shape") {
      this.drawShapeLayer(ctx, layer as import("../document/Layer").ShapeLayer);
      return;
    }
    if (layer.kind === "solid-fill" || layer.kind === "gradient-fill" || layer.kind === "pattern-fill") {
      // Fill layers: draw as opaque rect for native rendering
      this.drawFillLayerNative(ctx, layer);
      return;
    }
    if (layer.kind === "background") {
      const bg = layer as BackgroundLayer;
      const bitmap = this.getLayerBitmap(bg as unknown as RasterLayer);
      if (bitmap) {
        ctx.save();
        ctx.globalAlpha = 1;
        ctx.imageSmoothingEnabled = false;
        ctx.drawImage(bitmap, bg.x, bg.y);
        ctx.restore();
      }
      return;
    }
    if (layer.kind === "artboard") {
      // Artboard: render children
      const ab = layer as ArtboardLayer;
      for (const child of ab.children) {
        this.drawLayerNative(ctx, child);
      }
      return;
    }
    const raster = layer as RasterLayer;
    const rawBitmap = this.getLayerBitmap(raster);
    if (!rawBitmap) return;

    let bitmap =
      raster.mask && raster.mask.enabled
        ? this.getMaskedBitmap(raster, rawBitmap)
        : rawBitmap;
    if (!bitmap) return;

    // Apply fillOpacity.
    const fill = raster.fillOpacity ?? 1;
    if (fill < 1) {
      bitmap = this.getFillOpacityBitmap(raster, bitmap);
    }

    // Draw at 100% opacity — blending will be done by compositeLayer later.
    ctx.save();
    ctx.globalAlpha = 1;
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(bitmap, raster.x, raster.y);
    ctx.restore();
  }

  // Reference to the current document being rendered — set at render() entry
  // so compositeOffscreen can access it without changing the public API.
  private _currentDoc: Document | null = null;

  private offscreenTemp: HTMLCanvasElement | null = null;

  // Scratch canvas used by readDocPixels() to sample the on-screen document
  // region back down to document resolution. Kept separate from offscreenTemp
  // so a read (which precedes a write in drawAdjustmentLayer) never clobbers
  // the write buffer.
  private readbackTemp: HTMLCanvasElement | null = null;

  private getLayerBitmap(layer: RasterLayer): HTMLCanvasElement | null {
    const buf = layer.pixels;
    const cached = this.layerCache.get(layer.id);
    if (cached && cached.version === buf.version) return cached.canvas;
    const off = cached?.canvas ?? document.createElement("canvas");
    off.width = buf.width;
    off.height = buf.height;
    const offCtx = off.getContext("2d");
    if (!offCtx) return null;
    const imageData = offCtx.createImageData(buf.width, buf.height);
    imageData.data.set(buf.data);
    offCtx.putImageData(imageData, 0, 0);
    this.layerCache.set(layer.id, { canvas: off, version: buf.version });
    return off;
  }

  private evictUnused(doc: Document): void {
    const live = new Set<string>();
    const walk = (layers: readonly Layer[]) => {
      for (const l of layers) {
        live.add(l.id);
        if (l.kind === "group") walk(l.children);
      }
    };
    walk(doc.layers);
    for (const id of this.layerCache.keys()) {
      if (!live.has(id)) this.layerCache.delete(id);
    }
    for (const key of this.maskCache.keys()) {
      const colon = key.indexOf(":");
      const id = colon < 0 ? key : key.slice(0, colon);
      if (!live.has(id)) this.maskCache.delete(key);
    }
    for (const id of this.alphaCache.keys()) {
      if (!live.has(id)) this.alphaCache.delete(id);
    }
    for (const key of this.soCache.keys()) {
      const id = key.split(":")[0]!;
      if (!live.has(id)) this.soCache.delete(key);
    }
  }
}
